import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from "react-router-dom";
import './InventoryManagement.css'
import FilterDropdown from '../team-portal/TeamPortalUtil/FilterDropdown';
import Chart from 'react-apexcharts';
import { useAuth } from '../Auth';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import TeamHeader from '../team-portal/TeamHeader/TeamHeader';

// Flowchart imports
import { ReactFlow, MiniMap, Controls, Background, Node as FlowNode, Edge } from '@xyflow/react';

import '@xyflow/react/dist/style.css';

// Toast import 
import { toast, ToastContainer } from 'react-toastify';

// Icons
import { IoAddCircleOutline, IoBarChart, IoBagAddOutline, IoOptionsOutline } from "react-icons/io5";
import { MdShoppingCart, MdFileUpload, MdFileDownload } from "react-icons/md";
import { FaTrashAlt, FaTasks, FaLayerGroup } from "react-icons/fa";
import { FaBoltLightning, FaCircleInfo, FaPuzzlePiece, FaWarehouse } from "react-icons/fa6";
import { BiSolidBookAdd } from "react-icons/bi";
import { ImTree } from "react-icons/im";
import { BsBoxSeamFill } from "react-icons/bs";
import { GiTwoCoins } from "react-icons/gi";
import { TbReportMoney, TbPackages } from "react-icons/tb";
import { PiWarningDuotone } from "react-icons/pi";
import { HiBellAlert } from "react-icons/hi2";
import { AiFillPicture } from "react-icons/ai";
import { LuCopyPlus } from "react-icons/lu";


// Assets
import fnnBlueLogo from '../img/inventory/FNNIconBlue-07.png'
import fnnBlueLogo2 from '../img/inventory/inv-box.jpg'

// Interface for getting the available org content
interface InventoryItems {
    partID: number;
    partName: string;
    partPicture?: any;
    currentAmount: number;
    unitCost: number;
    category: string;
    partDocument?: any;
    partLink?: string;
    lastCounted?: string | null; // ISO date string
    location?: string | null;
    description?: string | null;
    mpn?: string | null;
    manufacturer?: string | null;
    location_picture?: any;
    stock_target?: number | null;
    sold_in_packets?: boolean;
    amount_per_packet?: number | null;
}

// Forecast
interface InventoryForecast {
    id: number;
    part: number;
    expectedAmount: number;
    cost: number;
    orderedDate: Date | null;
    arrivalDate: Date | null;
    status: string;
    sourceLinks: string;
    notes: string;
    requestor: string;
    requestor_email?: string;
    priority?: string;
    lead_time?: string | null;
    is_packet?: boolean;
    number_of_packets?: number | null;
    amount_per_packet?: number | null;
    pack_cost?: number;
    expectedArrivalDate?: Date | null;
    alert_generated?: boolean;
}

// Part requirement for SubAssembly
interface SubAssemblyPart {
    part: number; // not InventoryItems
    quantity_required: number;
}

// SubAssembly
interface SubAssembly {
    id: number;
    name: string;
    parts: SubAssemblyPart[];
    subPicture?: any;
    subSOP?: any;
}

// DetectorSubAssembly includes full SubAssembly object
interface DetectorSubAssembly {
    subassembly: number | SubAssembly; // Accepts either id or object
    quantity_required: number;
}

// Detector version
interface Detector {
    id: number;
    name: string;
    version: string;
    subassemblies: DetectorSubAssembly[];
}

type SubAssemblyOperation =
    | "BUILD_NEW"
    | "REGISTER_EXISTING"
    | "ADD_COMPONENTS";


type SubAssemblyRequest = {
    id: number;
    selectedSubAssemblyId: string;
    operation: SubAssemblyOperation;
    operationCount: number;
};

type DetectorRequest = {
    id: number;
    selectedDetectorId: string;
    operation: string;
    operationCount: number;
};

interface MonthlyProduction {
    content_type: string;
    object_id: number;
    object_name: string;
    object_version: string | null;
    month: Date | null;
    amount_produced: number;
}

interface BuiltSubAssemblies {
    id: string;
    built_at: string;
    built_by: string;
    status: string;
    subassembly: number;
    used_by: string;
}

interface BuiltDetectors {
    mac_address: string;
    code?: string;
    built_at: string;
    assembled_by: string | number; // can be PK
    status: string;
    detector: number | null;
    subassemblies: string[]; // IDs only
}

interface FNNUsers {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

// Function to ensure the time displayed in the order table matches the correct from backend (Avoid showing 1 day earlier)
function formatDateForDisplay(date: Date | string | null | undefined) {
    if (!date) return 'Unknown';

    // Convert string to Date object
    const d = typeof date === 'string' ? new Date(date + 'T00:00:00') : date;
    // Use UTC methods to avoid timezone shift
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const day = d.getUTCDate();

    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
}




const InventoryManagement = () => {

    const { first_name, last_name, email, username } = useAuth();
    const slipRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [invInfo, setInvInfo] = useState<InventoryItems[]>([]);
    const [subAssemblies, setSubAssemblies] = useState<SubAssembly[]>([]);
    const [detectors, setDetectors] = useState<Detector[]>([]);
    const [builtSubAssemblies, setBuiltSubAssemblies] = useState<BuiltSubAssemblies[]>([]);
    const [builtDetectors, setBuiltDetectors] = useState<BuiltDetectors[]>([]);
    const [fnnUsers, setFnnUsers] = useState<FNNUsers[]>([]);
    const [production, setProduction] = useState<MonthlyProduction[]>([]);
    const [showSectionPopup, setShowSectionPopup] = useState(false);
    const [showBOMPopup, setShowBOMPopup] = useState(false);
    const [showDetBOMPopup, setShowDetBOMPopup] = useState(false);
    const [activeSection, setActiveSection] = useState("");
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    // --- State ---
    const [sortMode, setSortMode] = useState<'alphabetical' | 'amount' | 'lastCounted'>('lastCounted');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Category filter state
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    // Sub-assembly filter state
    const [filterSelectedSubAssemblies, setFilterSelectedSubAssemblies] = useState<number[]>([]);


    // Sorting options
    const sortOptions = [
        { value: 'lastCounted', label: 'Last Counted' },
        { value: 'alphabetical', label: 'Alphabetical' },
        { value: 'amount', label: 'By Amount' },
    ];

    const location = useLocation();

    useEffect(() => {
        if (location.hash === "#openOrders") {
            setActiveSection("OrderParts");
            setShowSectionPopup(true);
            // Remove the hash from the URL so it looks clean
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, [location.hash]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const openBOM = params.get("openBOM");
        const subName = params.get("sub");
        const detId = params.get("det");
        const openAssembly = params.get("openAssembly");
        const mac = params.get("mac");
        const status = params.get("status");

        if (openBOM === "true") {
            if (subName) {
                // Handle Sub-Assembly BOM
                const foundSub = subAssemblies.find(sa => sa.name === subName);
                if (foundSub) {
                    setSelectedSubAssembly(foundSub);
                    setShowBOMPopup(true);
                }
            } else if (detId) {
                // Handle Detector BOM
                const foundDet = detectors.find(d => d.id === Number(detId));
                if (foundDet) {
                    setSelectedDetector2(foundDet);
                    setShowDetBOMPopup(true);
                }
            }

            window.history.replaceState(null, "", window.location.pathname);
        }

        // ✅ Handle "View Detector Assembly"
        if (openAssembly === "true" && mac) {
            setActiveSection("ViewBuiltUnits");
            setShowSectionPopup(true);
            setSelectedUnitStatus(status || "installed");
            setOpenSlips({ [mac]: true });

            // Clean URL so it doesn’t trigger again
            window.history.replaceState(null, "", window.location.pathname);

            // Wait for the DOM to render the slip, then scroll
            setTimeout(() => {
                const targetSlip = slipRefs.current[mac];
                if (targetSlip) {
                    targetSlip.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 400);
        }

    }, [location.search, subAssemblies, detectors]);




    // Function to create toast
    const warnUser = (message: string, fieldName?: string) => {
        toast.warn(message, {
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: 'white',
                color: 'black',
                fontSize: '16px',
            },
        });

        if (fieldName) {
            setInvalidFields((prev) => {
                const updated = new Set(prev);
                updated.add(fieldName);
                return Array.from(updated);
            });
        }
    };

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const fetchApiInfo = async () => {
        try {
            const response = await fetch('/api/inventory_view/', {
                credentials: "same-origin" // ensures cookies/session are sent for auth
            });

            const data = await response.json();
            // console.log(data)

            if (response.ok) {
                setInvInfo(data); // Assign the fetched gallery data to state
            } else {
                console.error("Failed to load api information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching api information:", error);
        }
    };

    useEffect(() => {
        fetchApiInfo();
    }, []);

    useEffect(() => {
        const fetchSubAssemblies = async () => {
            try {
                const response = await fetch('/api/inventory_subassemblies/', {
                    credentials: "same-origin"
                });
                const data = await response.json();
                if (response.ok) {
                    setSubAssemblies(data);
                } else {
                    console.error("Failed to load subassemblies:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching subassemblies:", error);
            }
        };

        const fetchDetectors = async () => {
            try {
                const response = await fetch('/api/inventory_detectors/', {
                    credentials: "same-origin"
                });
                const data = await response.json();
                if (response.ok) {
                    setDetectors(data);
                } else {
                    console.error("Failed to load detectors:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching detectors:", error);
            }
        };

        const fetchFNNUsers = async () => {
            try {
                const response = await fetch('/api/inventory/fnn-users/', {
                    credentials: "same-origin"
                });
                const data = await response.json();
                if (response.ok) {
                    setFnnUsers(data);
                } else {
                    console.error("Failed to load fnn users:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching fnn users:", error);
            }
        };

        fetchSubAssemblies();
        fetchDetectors();
        fetchFNNUsers();
    }, []);

    const fetchBuiltSubAssemblies = async () => {
        try {
            const response = await fetch('/api/inventory_built_subs/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                setBuiltSubAssemblies(data);
            } else {
                console.error("Failed to load subassemblies:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching subassemblies:", error);
        }
    };

    useEffect(() => {
        fetchBuiltSubAssemblies();
    }, []);

    const fetchbuiltDetectors = async () => {
        try {
            const response = await fetch('/api/inventory_built_dets/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                setBuiltDetectors(data);
            } else {
                console.error("Failed to load subassemblies:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching subassemblies:", error);
        }
    };

    useEffect(() => {
        fetchbuiltDetectors();
    }, []);

    const fetchMonthlyProdution = async () => {
        try {
            const response = await fetch('/api/inventory_montly_production/', {
                credentials: "same-origin" // ensures cookies/session are sent for auth
            });

            const data = await response.json();
            // console.log(data)

            if (response.ok) {
                setProduction(data); // Assign the fetched gallery data to state
            } else {
                console.error("Failed to load api information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching api information:", error);
        }
    };

    useEffect(() => {
        fetchMonthlyProdution();
    }, []);

    const detectorProduction = production.filter(item => item.content_type === 'detector');
    const subAssemblyProduction = production.filter(item => item.content_type === 'subassembly');


    // useEffect(() => {
    //     console.log("🔧 SubAssemblies:", subAssemblies);
    //     console.log("📦 Detectors:", detectors);
    //     console.log("📦 Production:", production);
    // }, [subAssemblies, detectors, production]);

    // Sorting inventory
    // To handle searching parts
    const [searchTerm, setSearchTerm] = useState("");
    const [orderSearchTerm, setOrderSearchTerm] = useState("");

    const filteredInventory = invInfo
        // Category filter
        .filter(item =>
            selectedCategories.length ? selectedCategories.includes(item.category) : true
        )
        // Sub-assembly filter
        .filter(item => {
            if (filterSelectedSubAssemblies.length === 0) return true;
            return subAssemblies.some(sa =>
                filterSelectedSubAssemblies.includes(sa.id) &&
                sa.parts.some(p => p.part === item.partID)
            );
        })
        // Search filter
        .filter(item => {
            if (!searchTerm.trim()) return true;
            const lower = searchTerm.toLowerCase();
            return (
                item.partName.toLowerCase().includes(lower) ||
                item.mpn?.toLowerCase().includes(lower) ||
                item.description?.toLowerCase().includes(lower)
            );
        });



    const sortedInventory = [...filteredInventory].sort((a, b) => {
        if (sortMode === 'alphabetical') {
            return a.partName.localeCompare(b.partName);
        } else if (sortMode === 'amount') {
            return b.currentAmount - a.currentAmount;
        } else if (sortMode === 'lastCounted') {
            const aDate = new Date(a.lastCounted || '1970-01-01');
            const bDate = new Date(b.lastCounted || '1970-01-01');
            return aDate.getTime() - bDate.getTime();
        }
        return 0;
    });



    function getColorTooltip(lastCounted: string | null | undefined): string {
        if (!lastCounted) return "Red - Item has never been counted.";

        const now = new Date();
        const countedDate = new Date(lastCounted);
        const diffInDays = Math.floor((now.getTime() - countedDate.getTime()) / (1000 * 60 * 60 * 24));

        if (diffInDays > 60) return `Red - Item has not been counted in over ${diffInDays} days.`;
        if (diffInDays > 30) return `Orange - Item has not been counted in over ${diffInDays} days.`;
        return "Normal - Item has been counted recently.";
    }

    function showInfoWithColor(lastCounted: string | null | undefined, extraClassName?: string) {
        const tooltipText = getColorTooltip(lastCounted);

        // Don't show icon if counted recently
        if (tooltipText.startsWith("Normal")) return null;

        // Decide icon color based on the tooltip prefix
        let iconColor = "black"; // default
        if (tooltipText.startsWith("Red")) iconColor = "red";
        else if (tooltipText.startsWith("Orange")) iconColor = "orange";

        return (
            <div className="tooltip-wrapper">
                <PiWarningDuotone style={{ color: iconColor }} />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {tooltipText}
                </div>
            </div>
        );
    }

    function showInfoTooltip(message: string, extraClassName?: string) {
        return (
            <div className="tooltip-wrapper">
                <FaCircleInfo />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {message}
                </div>
            </div>
        );
    }

    function getNeededTooltip(
        needed: number,
        available: number,
        forecasted: number,
        considerInventory: boolean,
        considerForecast: boolean,
        isCovered: boolean
    ): string {
        if (isCovered) {
            if (considerForecast) {
                return "Green - Possible to fulfill plan with items in inventory + forecasted";
            }
            if (considerInventory) {
                return "Green - Possible to fulfill plan with items in inventory";
            }
            return "Green - Possible to fulfill plan"; // fallback if neither flag
        } else {
            let missing = needed - (available + (considerForecast ? forecasted : 0));
            if (!considerForecast && considerInventory) {
                missing = needed - available;
            } else if (!considerInventory && !considerForecast) {
                missing = needed;
            }
            return `Red - Require ordering ${missing} more to fulfill plan`;
        }
    }

    // Util function to find the id of the logged in user
    const loggedFnnUserId = React.useMemo(() => {
        if (!fnnUsers?.length) return null;

        const matchedUser = fnnUsers.find(
            (user) =>
                user.username === username &&
                user.first_name === first_name &&
                user.last_name === last_name
        );

        return matchedUser ? matchedUser.id : null;
    }, [fnnUsers, username, first_name, last_name]);

    const getFnnUserName = (userId: string | number | null | undefined) => {
        if (!userId || !fnnUsers?.length) return "—";
        const user = fnnUsers.find((u) => String(u.id) === String(userId));
        if (!user) return "—";
        return `${user.first_name} ${user.last_name}`.trim() || user.username || "—";
    };



    // PUT, POST, and DELETE request components
    const [editingPartId, setEditingPartId] = useState<number | null>(null);
    const [editedValues, setEditedValues] = useState<Partial<InventoryItems>>({});
    const editingRowRef = useRef<HTMLTableRowElement | null>(null);
    const [showAddItemPopup, setShowAddItemPopup] = useState(false);
    const [showDeleteItemPopup, setShowDeleteItemPopup] = useState(false);
    const [showCountedPopup, setShowCountedPopup] = useState(false);
    const [itemToCount, setItemToCount] = useState<InventoryItems | null>(null);
    const [itemToDeleteId, setItemToDeleteId] = useState<number | null>(null);
    const [showArrivalItemPopup, setShowArrivalItemPopup] = useState(false);
    const [showConfirmProceedPopup, setShowConfirmProceedPopup] = useState(false);
    const [showLocationPopup, setShowLocationPopup] = useState(false);
    const [activeLocation, setActiveLocation] = useState<string | null>(null);
    const [activeLocationPicture, setActiveLocationPicture] = useState<string | undefined>(undefined);


    const CATEGORY_OPTIONS = [
        { value: 'misc', label: 'Accessories & Miscellaneous' },
        { value: 'cable', label: 'Wiring & Connectors' },
        { value: 'eletronics', label: 'Electronics' },
        { value: 'mounting', label: 'Fasteners & Mounting' },
        { value: 'branding', label: 'Branding & Labeling' },
        { value: 'power', label: 'Power Components' },
        { value: 'structural', label: 'Structural' },
        { value: 'protection', label: 'Protection' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'tools', label: 'Tools' },
        { value: 'outsourced', label: 'Outsourced Component' },
    ];

    const [newPartData, setNewPartData] = useState<{
        partName: string;
        currentAmount: number;
        unitCost: number;
        category: string;
        partPictureFile?: File | null;
        partDocumentFile?: File | null;
        partLink?: string;
        location?: string;
        description?: string;
        mpn?: string;
        manufacturer?: string;
        partLocationFile?: File | null;
        stock_target?: number | null;
        sold_in_packets?: boolean;
        amount_per_packet?: number | null;
    }>({
        partName: '',
        currentAmount: 0,
        unitCost: 0,
        category: CATEGORY_OPTIONS[0].value,
        partPictureFile: null,
        partDocumentFile: null,
        partLink: '',
        location: '',
        description: '',
        mpn: '',
        manufacturer: '',
        partLocationFile: null,
        stock_target: null,
        sold_in_packets: false,
        amount_per_packet: null,
    });


    const resetNewPartData = () => {
        setNewPartData({
            partName: '',
            currentAmount: 0,
            unitCost: 0,
            category: CATEGORY_OPTIONS[0].value,
            partPictureFile: null,
            partDocumentFile: null,
            partLink: '',
            location: '',
            description: '',
            mpn: '',
            manufacturer: '',
            partLocationFile: null,
            stock_target: null,
            sold_in_packets: false,
            amount_per_packet: null,
        });
    };



    const updateInventoryItem = async (id: number, updatedFields: Partial<InventoryItems>) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const original = invInfo.find(item => item.partID === id);
        if (!original) return;

        // Merge original + edited, but omit partPicture and partDocument for PUT update
        const { partPicture, partDocument, location_picture, ...restOriginal } = original;
        const payload = { ...restOriginal, ...updatedFields };

        try {
            const response = await fetch(`/api/inventory_view/${id}/`, {
                method: 'PUT',
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const updatedItem = await response.json();
                setInvInfo(prev => prev.map(item => item.partID === id ? updatedItem : item));

                toast.success("Item updated successfully!", {
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: {
                        backgroundColor: "white",
                        color: "black",
                        fontSize: "16px",
                    },
                });

            } else {
                console.error("Failed to update item:", response.statusText);
                const errorText = await response.text();

                toast.error(`Failed to update item: ${errorText}`, {
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: {
                        backgroundColor: "white",
                        color: "black",
                        fontSize: "16px",
                    },
                });

                console.error("Error details:", errorText);
            }
        } catch (err) {
            console.error("❌ PUT request failed:", err);
        }
    };

    const handleFileUpload = async (
        partID: number,
        file: File,
        fileType: 'image' | 'document' | 'location'
    ) => {

        // --- File size validation ---
        if (file.size > 1024 * 1024) {
            toast.warn("Files must be at most 1MB", {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '16px',
                },
            });
            return; // Stop upload
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const original = invInfo.find((item) => item.partID === partID);
        if (!original) return;

        const formData = new FormData();

        formData.append('partName', editedValues.partName ?? original.partName);
        formData.append('currentAmount', String(editedValues.currentAmount ?? original.currentAmount));
        formData.append('unitCost', String(editedValues.unitCost ?? original.unitCost));
        formData.append('category', editedValues.category ?? original.category);

        if (fileType === 'image') formData.append('partPicture', file);
        else if (fileType === 'document') formData.append('partDocument', file);
        else if (fileType === 'location') formData.append('location_picture', file);

        try {
            const response = await fetch(`/api/inventory_view/${partID}/`, {
                method: 'PUT',
                credentials: "same-origin",
                headers: { 'X-CSRFToken': csrfToken },
                body: formData,
            });

            if (response.ok) {
                const updatedItem = await response.json();
                setInvInfo((prev) =>
                    prev.map((item) => (item.partID === partID ? updatedItem : item))
                );
                setEditingPartId(null);
                setEditedValues({});
            } else {
                console.error('Failed to upload file:', response.statusText);
                console.error(await response.text());
            }
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };


    const addNewPart = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const formData = new FormData();

        formData.append('partName', newPartData.partName);
        formData.append('currentAmount', String(newPartData.currentAmount));
        formData.append('unitCost', String(newPartData.unitCost));
        formData.append('category', newPartData.category);

        if (newPartData.partPictureFile) formData.append('partPicture', newPartData.partPictureFile);
        if (newPartData.partDocumentFile) formData.append('partDocument', newPartData.partDocumentFile);
        if (newPartData.partLink) formData.append('partLink', newPartData.partLink);
        if (newPartData.location) formData.append('location', newPartData.location);
        if (newPartData.description) formData.append('description', newPartData.description);
        if (newPartData.mpn) formData.append('mpn', newPartData.mpn);
        if (newPartData.manufacturer) formData.append('manufacturer', newPartData.manufacturer);
        if (newPartData.partLocationFile) formData.append('location_picture', newPartData.partLocationFile);
        if (newPartData.stock_target != null)
            formData.append('stock_target', String(newPartData.stock_target));

        if (newPartData.sold_in_packets !== undefined) {
            formData.append(
                'sold_in_packets',
                String(newPartData.sold_in_packets) // ✅ boolean → string
            );
        }

        if (newPartData.amount_per_packet !== null && newPartData.amount_per_packet !== undefined) {
            formData.append(
                'amount_per_packet',
                String(newPartData.amount_per_packet) // ✅ number → string
            );
        }


        try {
            const response = await fetch('/api/inventory_view/', {
                method: 'POST',
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken,
                },
                body: formData,
            });

            if (response.ok) {
                const addedItem = await response.json();
                setInvInfo(prev => [...prev, addedItem]);
                // Reset form
                resetNewPartData();
                toast.success("Item created successfully.", toastStyle);
            } else {
                const errorText = await response.text();
                console.error('Failed to add new part:', response.statusText, errorText);
                toast.error(errorText, toastStyle);
            }
        } catch (error) {
            console.error('Error adding new part:', error);
        }
    };

    const deleteInventoryItem = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(`/api/inventory_view/${id}/`, {
                method: 'DELETE',
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok || response.status === 204) {
                setInvInfo(prev => prev.filter(item => item.partID !== id));
                setShowDeleteItemPopup(false);
                toast.success("Item was deleted successfully.", toastStyle);
                setItemToDeleteId(null);
            } else {
                const errorText = await response.text();
                console.error('Failed to delete item:', response.statusText, errorText);
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    // Function to submit request on ENTER press

    const handleSubmitEdit = () => {
        if (editingPartId === null) return;

        const original = invInfo.find(item => item.partID === editingPartId);
        const hasChanges = original &&
            Object.keys(editedValues).some(key => (original as any)[key] !== (editedValues as any)[key]);

        if (hasChanges) {
            updateInventoryItem(editingPartId, editedValues);
        }

        setEditingPartId(null);
        setEditedValues({});
    };


    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && editingPartId !== null) {
                handleSubmitEdit();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [editingPartId, editedValues, invInfo]);

    // ------------------------------------------- Dealing with Sub-Assembly Logic -------------------------------------------
    const [subAssemblyRequests, setSubAssemblyRequests] = useState<SubAssemblyRequest[]>([]);
    const [subAssemblyToDeleteId, setSubAssemblyToDeleteId] = useState<number | null>(null);
    const [showDeleteSubAssemblyPopup, setShowDeleteSubAssemblyPopup] = useState(false);
    const [showAddSubAssemblyPopup, setShowAddSubAssemblyPopup] = useState(false);

    // Adding/Editing new Sub-Assembly
    const [newSubAssemblyName, setNewSubAssemblyName] = useState('');
    const [newSubAssemblyPic, setNewSubAssemblyPic] = useState<File | null>(null);
    const [newSubAssemblySop, setNewSubAssemblySop] = useState<File | null>(null);
    const [newSubAssemblyParts, setNewSubAssemblyParts] = useState<
        { part: number; quantity_required: number }[]
    >([]);
    const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
    const [selectedPartAmount, setSelectedPartAmount] = useState<number>(1);
    const [editingSubAssemblyId, setEditingSubAssemblyId] = useState<number | null>(null);

    // Additonal popup handling to build a sub-assembly
    const [showBuildConfirmPopup, setShowBuildConfirmPopup] = useState(false);
    const [showBuildResultPopup, setShowBuildResultPopup] = useState(false);
    // Add this with your other useState declarations near the top
    const [buildSummary, setBuildSummary] = useState<{
        buildNew: { sub: string; qty: number }[];
        register: { sub: string; qty: number }[];
        addComponents: { sub: string; qty: number }[];
    }>({
        buildNew: [],
        register: [],
        addComponents: []
    });



    const [newlyBuiltSubs, setNewlyBuiltSubs] = useState<{ name: string; qty: number; ids: string[] }[]>([]);


    const handleRequestChange = <K extends keyof SubAssemblyRequest>(
        index: number,
        field: K,
        value: SubAssemblyRequest[K]
    ) => {
        const updatedRequests = [...subAssemblyRequests];
        updatedRequests[index][field] = value;
        setSubAssemblyRequests(updatedRequests);
    };

    const addNewRequest = () => {
        setSubAssemblyRequests(prev => [
            ...prev,
            {
                id: Date.now(),
                selectedSubAssemblyId: "",
                operation: "BUILD_NEW",
                operationCount: 1,
            },
        ]);
    };


    const removeRequest = (index: number) => {
        const updatedRequests = subAssemblyRequests.filter((_, i) => i !== index);
        setSubAssemblyRequests(updatedRequests);
    };

    const handleSubAssemblyBuildAction = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const newBuiltSubs: { name: string; qty: number; ids: string[] }[] = [];

        for (const req of subAssemblyRequests) {
            const { selectedSubAssemblyId, operation, operationCount } = req;
            const sub = subAssemblies.find(s => s.id.toString() === selectedSubAssemblyId);
            if (!sub) continue;

            const builtIds: string[] = [];

            /* =============================
               CREATE BUILT SUB-ASSEMBLIES
            ============================== */
            if (operation === "BUILD_NEW" || operation === "REGISTER_EXISTING") {
                for (let i = 0; i < operationCount; i++) {
                    const payload = {
                        subassembly: selectedSubAssemblyId,
                        status: "available",
                        built_by: loggedFnnUserId,
                    };

                    const response = await fetch(
                        "/api/inventory_built_subs/",
                        {
                            method: "POST",
                            credentials: "same-origin",
                            headers: {
                                "Content-Type": "application/json",
                                "X-CSRFToken": csrfToken,
                            },
                            body: JSON.stringify(payload),
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();
                        builtIds.push(data.id);
                    }
                }

                newBuiltSubs.push({
                    name: sub.name,
                    qty: operationCount,
                    ids: builtIds,
                });
            }

            /* =============================
               INVENTORY SIDE EFFECTS
            ============================== */
            if (operation === "BUILD_NEW") {
                // consume inventory
                await fetch(
                    `/api/inventory_subassemblies/${selectedSubAssemblyId}/consume/`,
                    {
                        method: "POST",
                        credentials: "same-origin",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        body: JSON.stringify({ times: operationCount }),
                    }
                );
            }

            if (operation === "ADD_COMPONENTS") {
                // add inventory
                await fetch(
                    `/api/inventory_subassemblies/${selectedSubAssemblyId}/add/`,
                    {
                        method: "POST",
                        credentials: "same-origin",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        body: JSON.stringify({ times: operationCount }),
                    }
                );
            }
        }

        setNewlyBuiltSubs(newBuiltSubs);

        await fetchApiInfo();
        await fetchMonthlyProdution();
        await fetchBuiltSubAssemblies();
        await fetchbuiltDetectors();
    };



    function closeConsumeAddPopup(activePopup: string, subOrDet: string) {
        setActiveSection(activePopup);
        setShowSectionPopup(true);

        if (subOrDet === 'sub')
            setSubAssemblyRequests([]);
        else
            setDetectorRequests([]);
    }

    const handleDeleteSubAssembly = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(`/api/inventory_subassemblies/${id}/`, {
                method: 'DELETE',
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok || response.status === 204) {
                setSubAssemblies(prev => prev.filter(sa => sa.id !== id));
                setShowDeleteSubAssemblyPopup(false);
                setSubAssemblyToDeleteId(null);
            } else {
                const err = await response.text();
                console.error('Failed to delete sub-assembly:', err);
            }
        } catch (error) {
            console.error('❌ Error deleting sub-assembly:', error);
        }
    };

    const editOrAddSubAssembly = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const method = editingSubAssemblyId ? 'PUT' : 'POST';
        const url = editingSubAssemblyId
            ? `/api/inventory_subassemblies/${editingSubAssemblyId}/`
            : '/api/inventory_subassemblies/';

        // Create FormData object
        const formData = new FormData();
        formData.append('name', newSubAssemblyName);
        formData.append('parts', JSON.stringify(newSubAssemblyParts)); // stringify parts

        if (newSubAssemblyPic) {
            formData.append('subPicture', newSubAssemblyPic);
        }
        if (newSubAssemblySop) {
            formData.append('subSOP', newSubAssemblySop);
        }

        try {
            const response = await fetch(url, {
                method,
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken,
                    // IMPORTANT: Do NOT set 'Content-Type' header here for FormData
                },
                body: formData,
            });

            if (response.ok) {
                const updatedSub = await response.json();

                setSubAssemblies(prev =>
                    editingSubAssemblyId
                        ? prev.map(s => (s.id === editingSubAssemblyId ? updatedSub : s))
                        : [...prev, updatedSub]
                );

                closeAddSubPopup(); // Reset state and close
            } else {
                const errorText = await response.text();
                console.error('Failed to save subassembly:', errorText);
            }
        } catch (err) {
            console.error('❌ Error saving subassembly:', err);
        }
    };

    const [existingPicName, setExistingPicName] = useState('');
    const [existingSopName, setExistingSopName] = useState('');

    useEffect(() => {
        if (editingSubAssemblyId) {
            const editingSub = subAssemblies.find(s => s.id === editingSubAssemblyId);
            if (editingSub) {
                setNewSubAssemblyName(editingSub.name);
                setNewSubAssemblyParts(editingSub.parts);

                setNewSubAssemblyPic(null);
                setNewSubAssemblySop(null);

                // extract filename from URL
                setExistingPicName(editingSub.subPicture?.split('/').pop() ?? '');
                setExistingSopName(editingSub.subSOP?.split('/').pop() ?? '');
            }
        }
    }, [editingSubAssemblyId]);



    const closeAddSubPopup = () => {
        setShowAddSubAssemblyPopup(false);
        setNewSubAssemblyName('');
        setNewSubAssemblyPic(null);
        setNewSubAssemblySop(null);
        setExistingSopName('');
        setExistingPicName('');
        setSelectedPartId(null);
        setSelectedPartAmount(1);
        setNewSubAssemblyParts([]);
        setEditingSubAssemblyId(null);
    };


    // ------------------------------------------- Dealing with Sub-Assembly Logic -------------------------------------------

    // ------------------------------------------- Dealing with Detector Logic -------------------------------------------
    const [detectorRequests, setDetectorRequests] = useState<DetectorRequest[]>([]);

    const handleDetectorRequestChange = <K extends keyof DetectorRequest>(
        index: number,
        field: K,
        value: DetectorRequest[K]
    ) => {
        const updatedRequests = [...detectorRequests];
        updatedRequests[index][field] = value;
        setDetectorRequests(updatedRequests);
    };

    const addNewDetectorRequest = () => {
        setDetectorRequests([
            ...detectorRequests,
            { id: Date.now(), selectedDetectorId: "", operation: "consume", operationCount: 1 }
        ]);
    };

    const removeDetectorRequest = (index: number) => {
        const updatedRequests = detectorRequests.filter((_, i) => i !== index);
        setDetectorRequests(updatedRequests);
    };

    const handleDetectorInventoryAction = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const insufficientAll: string[] = [];

        const cumulativeRequired: Record<number, number> = {};

        for (const req of detectorRequests) {
            const { selectedDetectorId, operation, operationCount } = req;

            if (!selectedDetectorId || operationCount < 1) {
                toast.warn("Each request must have a detector and a quantity >= 1.", toastStyle);
                return;
            }

            if (operation === "consume") {
                const detector = detectors.find(d => d.id.toString() === selectedDetectorId);
                if (!detector) {
                    toast.error(`Detector with ID ${selectedDetectorId} not found.`, toastStyle);
                    return;
                }

                for (const ds of detector.subassemblies) {
                    const sub = typeof ds.subassembly === 'number'
                        ? subAssemblies.find(s => s.id === ds.subassembly)
                        : ds.subassembly;

                    if (!sub) continue;

                    const subCount = ds.quantity_required * operationCount;
                    for (const part of sub.parts) {
                        const totalQty = part.quantity_required * subCount;
                        cumulativeRequired[part.part] = (cumulativeRequired[part.part] || 0) + totalQty;
                    }
                }
            }
        }

        for (const [partIDStr, requiredQty] of Object.entries(cumulativeRequired)) {
            const partID = Number(partIDStr);
            const part = invInfo.find(p => p.partID === partID);

            if (!part || part.currentAmount < requiredQty) {
                insufficientAll.push(
                    `${part?.partName || `Part ID ${partID}`} (needs ${requiredQty}, has ${part?.currentAmount ?? 0})`
                );
            }
        }

        if (insufficientAll.length > 0) {
            toast.error(
                `Operation failed:\n${insufficientAll.join('\n')}`,
                {
                    ...toastStyle,
                    style: {
                        ...toastStyle.style,
                        whiteSpace: 'pre-line'
                    }
                }
            );
            return;
        }



        for (const req of detectorRequests) {
            const { selectedDetectorId, operation, operationCount } = req;

            try {
                const response = await fetch(
                    `/api/inventory_detectors/${selectedDetectorId}/${operation}/`,
                    {
                        method: 'POST',
                        credentials: "same-origin",
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRFToken': csrfToken,
                        },
                        body: JSON.stringify({ times: operationCount }),
                    }
                );

                if (!response.ok) {
                    const err = await response.json();
                    toast.error(`Error for Detector ID ${selectedDetectorId}: ${err?.error || "Unknown error"}`, toastStyle);
                    return;
                }
            } catch (err) {
                console.error("❌ Detector operation failed:", err);
                toast.error("A network or server error occurred while performing detector operation.", toastStyle);
                return;
            }
        }

        toast.success("All detector operations completed successfully.", toastStyle);
        setDetectorRequests([{ id: Date.now(), selectedDetectorId: "", operation: "consume", operationCount: 1 }]);
        await fetchApiInfo();
        await fetchMonthlyProdution();
    };


    // Optional: Central toast style object
    const toastStyle = {
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
            backgroundColor: 'white',
            color: 'black',
            fontSize: '16px',
        },
    };



    // Adding and Deleting Detectors
    const [newDetectorName, setNewDetectorName] = useState('');
    const [newDetectorVersion, setNewDetectorVersion] = useState('');
    const [newDetectorSubAssemblies, setNewDetectorSubAssemblies] = useState<
        { subassembly: number; quantity_required: number }[]
    >([]);
    const [selectedSubAssemblyId, setSelectedSubAssemblyId] = useState<number | null>(null);
    const [selectedSubAssemblyAmount, setSelectedSubAssemblyAmount] = useState<number>(1);
    const [editingDetectorId, setEditingDetectorId] = useState<number | null>(null);
    const [detectorToDeleteId, setDetectorToDeleteId] = useState<number | null>(null);
    const [showAddDetectorPopup, setShowAddDetectorPopup] = useState(false);
    const [showDeleteDetectorPopup, setShowDeleteDetectorPopup] = useState(false);

    const editOrAddDetector = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const method = editingDetectorId ? 'PUT' : 'POST';
        const url = editingDetectorId
            ? `/api/inventory_detectors/${editingDetectorId}/`
            : '/api/inventory_detectors/';

        // Prepare the payload
        // const payload = {
        //     name: newDetectorName,
        //     version: newDetectorVersion,
        //     subassemblies: newDetectorSubAssemblies,
        // };

        // console.log('Sending payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await fetch(url, {
                method,
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    name: newDetectorName,
                    version: newDetectorVersion,
                    subassemblies: newDetectorSubAssemblies,
                }),
            });

            if (response.ok) {
                const updated = await response.json();
                setDetectors(prev =>
                    editingDetectorId
                        ? prev.map(d => (d.id === editingDetectorId ? updated : d))
                        : [...prev, updated]
                );
                closeAddDetectorPopup();
            } else {
                console.error(await response.text());
            }
        } catch (err) {
            console.error('❌ Error saving detector:', err);
        }
    };

    const handleDeleteDetector = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(`/api/inventory_detectors/${id}/`, {
                method: 'DELETE',
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok || response.status === 204) {
                setDetectors(prev => prev.filter(det => det.id !== id));
                setShowDeleteDetectorPopup(false);
                setDetectorToDeleteId(null);
            } else {
                console.error(await response.text());
            }
        } catch (error) {
            console.error('❌ Error deleting detector:', error);
        }
    };

    const closeAddDetectorPopup = () => {
        setShowAddDetectorPopup(false);
        setNewDetectorName('');
        setNewDetectorVersion('');
        setSelectedSubAssemblyId(null);
        setSelectedSubAssemblyAmount(1);
        setNewDetectorSubAssemblies([]);
        setEditingDetectorId(null);
    };

    // ------------------------------------------- Dealing with Detector Logic -------------------------------------------

    // ------------------------------------------- Placing and Managing Orders Logic -------------------------------------------

    const [showAddOrderPopup, setShowAddOrderPopup] = useState(false);
    const [forecastInfo, setForecastInfo] = useState<InventoryForecast[]>([]);
    const [forecastToDeleteId, setForecastToDeleteId] = useState<number | null>(null);
    const [showDeleteForecastPopup, setShowDeleteForecastPopup] = useState(false);


    // ------------------------------------------- Placing and Managing Orders Logic -------------------------------------------

    const fetchForecastInfo = async () => {
        try {
            const response = await fetch('/api/inventory_forecast/', {
                credentials: "same-origin" // ensures cookies/session are sent for auth
            });

            const data = await response.json();
            // console.log(data)

            if (response.ok) {
                setForecastInfo(data); // Assign the fetched gallery data to state
            } else {
                console.error("Failed to load api information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching api information:", error);
        }
    };

    useEffect(() => {
        fetchForecastInfo();
    }, []);

    const handleDeleteForecast = async (id: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(`/api/inventory_forecast/${id}/`, {
                method: 'DELETE',
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (response.ok) {
                setForecastInfo(prev => prev.filter(item => item.id !== id));
                setShowDeleteForecastPopup(false);
                setForecastToDeleteId(null);
            } else {
                console.error("Failed to delete forecast:", response.statusText);
            }
        } catch (error) {
            console.error("Error deleting forecast:", error);
        }
    };


    // Adding or Editing Orders
    const [forecastPartID, setForecastPartID] = useState<number | null>(null);
    const [forecastPartName, setForecastPartName] = useState('');
    const [forecastPartImage, setForecastPartImage] = useState<string | null>(null);
    const [forecastAmount, setForecastAmount] = useState<number>(0);
    const [forecastCost, setForecastCost] = useState<number>(0);
    const [forecastOrderedDate, setForecastOrderedDate] = useState('');
    const [forecastArrivalDate, setForecastArrivalDate] = useState('');
    const [forecastStatus, setForecastStatus] = useState<string>("Requested");
    const [forecastStoreLinks, setForecastStoreLinks] = useState('');
    const [forecastNotes, setForecastNotes] = useState('');
    const [forecastRequestor, setForecastRequestor] = useState('');
    const [forecastIsPacket, setForecastIsPacket] = useState(false);
    const [forecastNumberOfPackets, setForecastNumberOfPackets] = useState<number>(0);
    const [forecastAmountPerPacket, setForecastAmountPerPacket] = useState<number>(0);
    const [forecastPriority, setForecastPriority] = useState('Low');
    const [forecastLeadTime, setForecastLeadTime] = useState('');
    const [forecastPackCost, setForecastPackCost] = useState(0);
    const [forecastExpectedArrivalDate, setForecastExpectedArrivalDate] = useState('');

    const [editingForecastId, setEditingForecastId] = useState<number | null>(null);
    const [partSearchTerm, setPartSearchTerm] = useState('');
    const filteredPartOptions = invInfo.filter((part) => {
        const term = partSearchTerm.toLowerCase();
        return (
            part.partName?.toLowerCase().includes(term) ||
            part.mpn?.toLowerCase().includes(term)
        );
    });
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<keyof typeof statusPriority>("Requested");

    const [arrivalAmount, setArrivalAmount] = useState<number>(0);
    const [activeForecast, setActiveForecast] = useState<InventoryForecast | null>(null);

    const [subAssemblySearchTerm, setSubAssemblySearchTerm] = useState('');
    const [showSubAssemblyDropdown, setShowSubAssemblyDropdown] = useState(false);

    const isPacketOrder = activeForecast?.is_packet && activeForecast.amount_per_packet;
    const [arrivedPackets, setArrivedPackets] = useState<number>(0);

    // For handling the partial ordering
    const [showOrderItemPopup, setShowOrderItemPopup] = useState(false);
    const [orderedAmount, setOrderedAmount] = useState(0);
    const [orderedPackets, setOrderedPackets] = useState(0);

    // Setting the number of packets to the max possible when confirming arrival
    useEffect(() => {
        if (!showArrivalItemPopup || !activeForecast) return;

        if (isPacketOrder) {
            const maxPackets = activeForecast.number_of_packets ?? 0;

            setArrivedPackets(maxPackets);
            setArrivalAmount(
                maxPackets * (activeForecast.amount_per_packet ?? 1)
            );
        } else {
            setArrivalAmount(activeForecast.expectedAmount);
        }
    }, [showArrivalItemPopup, activeForecast, isPacketOrder]);


    // Filter sub-assemblies dynamically based on search term
    const filteredSubAssemblies = subAssemblies.filter(sa =>
        sa.name.toLowerCase().includes(subAssemblySearchTerm.toLowerCase())
    );

    const isArrivalAmountValid = () => {
        if (!activeForecast) return false;

        if (arrivalAmount <= 0) return false;
        if (arrivalAmount > activeForecast.expectedAmount) return false;

        return true;
    };


    const handleMarkArrived = async (): Promise<boolean> => {
        if (!activeForecast) return false;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return false;

        try {
            const response = await fetch(
                `/api/inventory_forecast/${activeForecast.id}/mark-arrived/`,
                {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({ arrived_amount: arrivalAmount }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                let message = errorText;

                try {
                    const json = JSON.parse(errorText);
                    message = json.error ?? errorText;
                } catch {  /*  */ }

                toast.warn(message, { autoClose: 5000 });
                return false; // ⛔ STOP FLOW
            }

            await fetchApiInfo();
            await fetchForecastInfo();

            toast.success(`Set ${arrivalAmount} items as Arrived`);
            return true; // ✅ SUCCESS

        } catch (err) {
            toast.error("Unexpected error marking arrival.");
            return false;
        }
    };

    const isOrderAmountValid = () => {
        if (!activeForecast) return false;
        if (orderedAmount <= 0) return false;
        if (orderedAmount > activeForecast.expectedAmount) return false;
        return true;
    };

    // Handler for marking items as ordered
    const handleMarkOrdered = async (): Promise<boolean> => {
        if (!activeForecast) return false;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return false;

        try {
            const response = await fetch(
                `/api/inventory_forecast/${activeForecast.id}/mark-ordered/`,
                {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify({ ordered_amount: orderedAmount }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                let message = errorText;

                try {
                    const json = JSON.parse(errorText);
                    message = json.error ?? errorText;
                } catch { /* ignore */ }

                toast.warn(message, { autoClose: 5000 });
                return false; // ⛔ STOP FLOW
            }

            await fetchApiInfo();
            await fetchForecastInfo();

            toast.success(`${orderedAmount} items marked as Ordered`);
            return true; // ✅ SUCCESS

        } catch (err) {
            toast.error("Unexpected error marking order.");
            return false;
        }
    };


    const editOrAddForecast = async () => {
        // Validation checks
        const isValidPart = filteredPartOptions.some(
            (item) => item.partName.toLowerCase() === partSearchTerm.trim().toLowerCase()
        );

        setInvalidFields([]); // clear previous errors

        if (!isValidPart) {
            warnUser("Please select a valid part from the suggestion list.", "part");
            return;
        }

        if (forecastAmount <= 0) {
            warnUser("Amount must be greater than 0.", "amount");
            return;
        }

        if (forecastCost <= 0) {
            warnUser("Cost must be greater than 0.", "cost");
            return;
        }

        if (!forecastStatus) {
            warnUser("Please select a status.", "status");
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const method = editingForecastId ? 'PUT' : 'POST';
        const url = editingForecastId
            ? `/api/inventory_forecast/${editingForecastId}/`
            : '/api/inventory_forecast/';

        const selectedPart = invInfo.find(item => item.partID === forecastPartID);
        const fallbackLink = selectedPart?.partLink || "";

        // Construct the payload
        const payload: any = {
            part: forecastPartID,
            expectedAmount: forecastAmount,
            cost: forecastCost,
            orderedDate: forecastOrderedDate || null,
            arrivalDate: forecastArrivalDate || null,
            status: forecastStatus,
            sourceLinks: forecastStoreLinks.trim() || fallbackLink,
            notes: forecastNotes,
            priority: forecastPriority,
            lead_time: forecastLeadTime,
            is_packet: forecastIsPacket,
            number_of_packets: forecastNumberOfPackets,
            amount_per_packet: forecastAmountPerPacket,
            pack_cost: forecastPackCost,
            expectedArrivalDate: forecastExpectedArrivalDate || null,
        };

        // Only add `requestor` if it's a POST request
        if (method === 'POST') {
            payload.requestor = `${first_name} ${last_name}`;
            payload.requestor_email = email;
        }

        try {
            const response = await fetch(url, {
                method,
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const updated = await response.json();
                setForecastInfo((prev: InventoryForecast[]) =>
                    editingForecastId
                        ? prev.map((f: InventoryForecast) =>
                            f.id === editingForecastId ? updated : f
                        )
                        : [...prev, updated]
                );

                if (forecastStatus === 'Arrived') {
                    fetchApiInfo(); // 👈 Refresh when arrived
                }

                setShowAddOrderPopup(false);

                // ✅ Show appropriate toast based on method
                if (method === 'POST') {
                    toast.success(`Order successfully ${forecastStatus.toLowerCase()}`, {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                } else {
                    toast.success("Order successfully updated", {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                }

                clearForecastForm(); // Reset the input fields
            } else {
                console.error(await response.text());
            }
        } catch (err) {
            console.error('❌ Error saving forecast:', err);
        }
    };


    // Add this new function for updating order status
    const updateOrderStatus = async (forecastId: number) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const today = new Date().toISOString().substring(0, 10); // format: YYYY-MM-DD

        // Find the current forecast data
        const currentForecast = forecastInfo.find(f => f.id === forecastId);
        if (!currentForecast) {
            console.error('Forecast not found');
            return;
        }

        try {
            const response = await fetch(`/api/inventory_forecast/${forecastId}/`, {
                method: 'PUT',
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    // Include all required fields from current forecast
                    part: currentForecast.part,
                    expectedAmount: currentForecast.expectedAmount,
                    cost: currentForecast.cost,
                    arrivalDate: currentForecast.arrivalDate,
                    sourceLinks: currentForecast.sourceLinks,
                    notes: currentForecast.notes,
                    // Update only these two fields
                    orderedDate: today,
                    status: 'Ordered'
                }),
            });

            if (response.ok) {
                const updated = await response.json();
                // Update the forecast info in the frontend
                setForecastInfo((prev: InventoryForecast[]) =>
                    prev.map((f: InventoryForecast) =>
                        f.id === forecastId ? updated : f
                    )
                );

                toast.success(`Item successfully marked as ordered`, {
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: {
                        backgroundColor: 'white',
                        color: 'black',
                        fontSize: '16px',
                    },
                });

            } else {
                console.error('Error updating order status:', await response.text());
            }
        } catch (err) {
            console.error('❌ Error updating order status:', err);
        }
    };

    const clearForecastForm = () => {
        setForecastPartID(null);
        setForecastPartName('');
        setPartSearchTerm('');
        setForecastAmount(0);
        setForecastCost(0);
        setForecastOrderedDate('');
        setForecastArrivalDate('');
        setForecastStatus("Requested");
        setForecastStoreLinks('');
        setForecastNotes('');
        setForecastRequestor('');
        setEditingForecastId(null);
        setForecastIsPacket(false);
        setForecastNumberOfPackets(0);
        setForecastAmountPerPacket(0);
        setForecastPriority("Low");
        setForecastLeadTime('')
        setForecastPartImage(null);
        setForecastPackCost(0)
        setForecastExpectedArrivalDate('');
    };

    const forecastAmountsByPart: Record<number, number> = {};

    forecastInfo.forEach(forecast => {
        if (!["Returned", "Cancelled", "Arrived"].includes(forecast.status)) {
            forecastAmountsByPart[forecast.part] = (forecastAmountsByPart[forecast.part] || 0) + forecast.expectedAmount;
        }
    });

    const statusPriority = {
        Requested: 1,
        Ordered: 2,
        Arrived: 3,
        Cancelled: 4,
        Returned: 5,
    };

    const today = new Date();

    const sortedForecasts = [...forecastInfo].sort((a, b) => {
        const priorityA = statusPriority[a.status as keyof typeof statusPriority] ?? 999;
        const priorityB = statusPriority[b.status as keyof typeof statusPriority] ?? 999;

        // 1️⃣ Sort by status priority
        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        // 2️⃣ SPECIAL CASE: Requested → sort by requestor (A–Z)
        if (a.status === "Requested" && b.status === "Requested") {
            return a.requestor.localeCompare(b.requestor, undefined, {
                sensitivity: "base",
            });
        }

        // 3️⃣ Default behavior for other statuses

        const arrivalA = a.arrivalDate ? new Date(a.arrivalDate).getTime() : Infinity;
        const arrivalB = b.arrivalDate ? new Date(b.arrivalDate).getTime() : Infinity;

        const diffA = Math.abs(arrivalA - today.getTime());
        const diffB = Math.abs(arrivalB - today.getTime());

        if (diffA !== diffB) {
            return diffA - diffB;
        }

        // 4️⃣ Tie-breaker: most recent order date first
        const orderA = a.orderedDate ? new Date(a.orderedDate).getTime() : 0;
        const orderB = b.orderedDate ? new Date(b.orderedDate).getTime() : 0;

        return orderB - orderA;
    });


    const filteredForecasts = sortedForecasts.filter((forecast) => {
        // status filter
        if (forecast.status !== selectedStatus) return false;

        // search filter
        if (!orderSearchTerm.trim()) return true;

        const matchingPart = invInfo.find(p => p.partID === forecast.part);

        const search = orderSearchTerm.toLowerCase();

        return (
            matchingPart?.partName?.toLowerCase().includes(search) ||
            matchingPart?.mpn?.toLowerCase().includes(search) ||
            forecast.requestor?.toLowerCase().includes(search)
        );
    });

    // Automatically resets search upon status change

    useEffect(() => {
        setOrderSearchTerm("");
    }, [selectedStatus]);




    // ------------------------------------------- Placing and Managing Orders Logic -------------------------------------------

    // ------------------------------------------- Functions for Analytics and Cards -------------------------------------------

    // Functions for Analytics and Cards
    const [maxBuildableDetectors, setMaxBuildableDetectors] = useState<number>(0);
    const [nextXDetectors, setNextXDetectors] = useState(1);
    const [partsNeededForNext, setPartsNeededForNext] = useState<Record<number, number>>({});
    const [showRequestItemPopup, setShowRequestItemPopup] = useState(false);
    const [activeRequestPartID, setActiveRequestPartID] = useState<number | null>(null);
    const [requestQuantity, setRequestQuantity] = useState<number>(0);


    // 1. Get latest version detector
    function getLatestDetectorVersion(detectors: Detector[]): Detector | null {
        if (!detectors || detectors.length === 0) return null;

        // Helper to parse version string into number array
        const parseVersion = (version: string) =>
            version.split('.').map(part => Number(part));

        return detectors.reduce((latest, current) => {
            const latestParts = parseVersion(latest.version);
            const currentParts = parseVersion(current.version);

            for (let i = 0; i < Math.max(latestParts.length, currentParts.length); i++) {
                const latestNum = latestParts[i] || 0;
                const currentNum = currentParts[i] || 0;

                if (currentNum > latestNum) return current;
                if (currentNum < latestNum) return latest;
                // if equal, continue to next part
            }

            return latest; // versions are equal
        });
    }


    // 2. Get parts required to build one detector
    function getRequiredPartsForDetector(detector: Detector, subAssemblies: SubAssembly[]) {
        const partRequirements: Record<number, number> = {};

        detector.subassemblies.forEach(({ subassembly, quantity_required }) => {
            const sub = subAssemblies.find(sa => sa.id === subassembly);
            if (!sub) return;

            sub.parts.forEach(({ part, quantity_required: partQty }) => {
                if (!partRequirements[part]) partRequirements[part] = 0;
                partRequirements[part] += partQty * quantity_required;
            });
        });

        return partRequirements;
    }

    // 3. Determine how many detectors can be built
    function getMaxBuildableDetectors(
        detector: Detector,
        subAssemblies: SubAssembly[],
        invInfo: InventoryItems[]
    ): number {
        const partRequirements = getRequiredPartsForDetector(detector, subAssemblies);

        let minBuildable = Infinity;

        for (const partID in partRequirements) {
            const requiredQty = partRequirements[Number(partID)];
            const partInInventory = invInfo.find(item => item.partID === Number(partID));
            const availableQty = partInInventory?.currentAmount ?? 0;

            const buildable = Math.floor(availableQty / requiredQty);
            minBuildable = Math.min(minBuildable, buildable);
        }

        return isFinite(minBuildable) ? minBuildable : 0;
    }

    function getPartsNeededForXDetectors(
        detector: Detector,
        subAssemblies: SubAssembly[],
        invInfo: InventoryItems[],
        targetCount: number
    ): Record<number, number> {
        const requiredParts = getRequiredPartsForDetector(detector, subAssemblies);
        const partsNeeded: Record<number, number> = {};

        // Step 1: Determine how many full detectors we can currently build
        let maxBuildable = Infinity;

        for (const partID in requiredParts) {
            const requiredQty = requiredParts[Number(partID)];
            const partInInventory = invInfo.find(item => item.partID === Number(partID));
            const availableQty = partInInventory?.currentAmount ?? 0;

            const buildableWithThisPart = Math.floor(availableQty / requiredQty);
            maxBuildable = Math.min(maxBuildable, buildableWithThisPart);
        }

        // Step 2: Calculate what’s missing to build `targetCount` more detectors beyond current
        const totalTarget = maxBuildable + targetCount;

        for (const partID in requiredParts) {
            const requiredQtyPerDetector = requiredParts[Number(partID)];
            const totalRequired = totalTarget * requiredQtyPerDetector;

            const partInInventory = invInfo.find(item => item.partID === Number(partID));
            const availableQty = partInInventory?.currentAmount ?? 0;

            if (availableQty < totalRequired) {
                partsNeeded[Number(partID)] = totalRequired - availableQty;
            }
        }

        return partsNeeded;
    }

    const handlePartForecastRequest = async (partID: number, quantity: number) => {
        const part = invInfo.find(item => item.partID === partID);
        if (!part) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        // Determine if part comes in packs
        let finalQuantity = quantity;
        const isPacket = part.sold_in_packets ?? false;
        const amountPerPacket = part.amount_per_packet ?? undefined;
        let numberOfPackets: number | undefined = undefined;
        let packCost: number | undefined = undefined;

        if (isPacket && amountPerPacket && amountPerPacket > 0) {
            numberOfPackets = Math.ceil(quantity / amountPerPacket);
            finalQuantity = numberOfPackets * amountPerPacket;
            packCost = part.unitCost * amountPerPacket;
        }

        // Build payload conditionally
        const payload: Partial<InventoryForecast> = {
            part: part.partID,
            expectedAmount: finalQuantity,
            cost: part.unitCost,
            orderedDate: null,
            arrivalDate: null,
            status: "Requested",
            sourceLinks: part.partLink || "",
            notes: "",
            requestor: `${first_name} ${last_name}`,
            requestor_email: email,
            is_packet: isPacket,
        };

        if (isPacket) {
            payload.number_of_packets = numberOfPackets;
            payload.amount_per_packet = amountPerPacket;
            payload.pack_cost = packCost;
        }

        try {
            const response = await fetch("/api/inventory_forecast/", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const newForecast = await response.json();
                setForecastInfo(prev => [...prev, newForecast]);

                toast.success("Item successfully requested!", {
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: {
                        backgroundColor: 'white',
                        color: 'black',
                        fontSize: '16px',
                    },
                });

            } else {
                console.error("❌ Request failed:", await response.text());
            }
        } catch (err) {
            console.error("❌ Error requesting forecast:", err);
        }
    };

    // ------------------------------------------- Functions for Analytics and Cards -------------------------------------------

    // Define state with correct types
    const [categorySeries, setCategorySeries] = useState<number[]>([]);
    const [categoryLabels, setCategoryLabels] = useState<string[]>([]);
    const [subAssemblyLabels, setSubAssemblyLabels] = useState<string[]>([]);
    const [subAssemblySeries, setSubAssemblySeries] = useState<number[]>([]);
    const [selectedDetectorId, setSelectedDetectorId] = useState<number | null>(null);

    useEffect(() => {
        if (!selectedDetectorId || detectors.length === 0 || subAssemblies.length === 0 || invInfo.length === 0) return;

        const selectedDetector = detectors.find(d => d.id === selectedDetectorId);
        if (!selectedDetector) return;

        const requiredParts = getRequiredPartsForDetector(selectedDetector, subAssemblies);
        const maxBuildable = getMaxBuildableDetectors(selectedDetector, subAssemblies, invInfo);
        setMaxBuildableDetectors(maxBuildable);

        const neededParts = getPartsNeededForXDetectors(selectedDetector, subAssemblies, invInfo, nextXDetectors);
        setPartsNeededForNext(neededParts);
    }, [selectedDetectorId, detectors, subAssemblies, invInfo, nextXDetectors]);

    // Assigning the lastest version to the correct detector ID for the donut graph
    useEffect(() => {
        if (detectors.length === 0) return;

        const latestDetector = getLatestDetectorVersion(detectors);
        if (latestDetector) {
            setSelectedDetectorId(latestDetector.id);
        }
    }, [detectors]);

    // Parts per category
    useEffect(() => {
        const categoryCountMap: { [key: string]: number } = {};

        invInfo.forEach(item => {
            if (categoryCountMap[item.category]) {
                categoryCountMap[item.category] += 1;
            } else {
                categoryCountMap[item.category] = 1;
            }
        });

        // invInfo.forEach(item => {
        //     if (categoryCountMap[item.category]) {
        //         categoryCountMap[item.category] += item.currentAmount;
        //     } else {
        //         categoryCountMap[item.category] = item.currentAmount;
        //     }
        // });


        const labels = Object.keys(categoryCountMap).map(key => {
            const option = CATEGORY_OPTIONS.find(opt => opt.value === key);
            return option ? option.label : key; // fallback to raw key if not found
        });

        setCategorySeries(Object.values(categoryCountMap));
        setCategoryLabels(labels);
    }, [invInfo]);

    // Parts per sub-assembly
    useEffect(() => {
        if (!selectedDetectorId) return;

        // Find selected detector object
        const detector = detectors.find(d => d.id === selectedDetectorId);
        if (!detector) return;

        // Map sub-assembly ID to SubAssembly object for quick lookup
        const subAssemblyMap = new Map(subAssemblies.map(sa => [sa.id, sa]));

        const subAssemblyPartCount: { [key: string]: number } = {};

        detector.subassemblies.forEach(({ subassembly, quantity_required }) => {
            // Get SubAssembly object (might be number or full object)
            const subAssemblyObj: SubAssembly | undefined =
                typeof subassembly === 'number' ? subAssemblyMap.get(subassembly) : subassembly;

            if (!subAssemblyObj) return;

            // Sum parts quantity in this subassembly multiplied by detector's quantity_required
            const totalParts = subAssemblyObj.parts.reduce((acc, part) => acc + part.quantity_required, 0) * quantity_required;

            subAssemblyPartCount[subAssemblyObj.name] = totalParts;
        });

        const labels = Object.keys(subAssemblyPartCount);
        const series = Object.values(subAssemblyPartCount);

        setSubAssemblyLabels(labels);
        setSubAssemblySeries(series);

    }, [selectedDetectorId, detectors, subAssemblies]);

    // Parts per detector version
    const [stackedBarOptions, setStackedBarOptions] = useState<any>({});
    const [stackedBarSeries, setStackedBarSeries] = useState<any[]>([]);
    const [flowOrTable, setFlowOrTable] = useState("flow");
    const [flowOrTableSub, setFlowOrTableSub] = useState<'flow' | 'table'>('flow');


    useEffect(() => {
        if (!detectors.length || !subAssemblies.length) return;

        const subAssemblyMap = new Map(subAssemblies.map(sa => [sa.id, sa]));

        // First pass: get all unique subassembly names
        const uniqueSubassemblyNames = new Set<string>();
        detectors.forEach(detector => {
            detector.subassemblies.forEach(({ subassembly }) => {
                const subassemblyObj: SubAssembly | undefined =
                    typeof subassembly === 'number' ? subAssemblyMap.get(subassembly) : subassembly;

                if (subassemblyObj) {
                    uniqueSubassemblyNames.add(subassemblyObj.name);
                }
            });
        });

        const subassemblyNames = Array.from(uniqueSubassemblyNames); // fix the order
        const detectorLabels: string[] = detectors.map(d => `v${d.version}`);

        // Initialize series map
        const subassemblySeriesMap: Record<string, number[]> = {};
        subassemblyNames.forEach(name => {
            subassemblySeriesMap[name] = new Array(detectors.length).fill(0);
        });

        detectors.forEach((detector, detectorIdx) => {
            detector.subassemblies.forEach(({ subassembly, quantity_required }) => {
                const subassemblyObj: SubAssembly | undefined =
                    typeof subassembly === 'number' ? subAssemblyMap.get(subassembly) : subassembly;

                if (!subassemblyObj) return;

                const partCount = subassemblyObj.parts.reduce(
                    (acc, part) => acc + part.quantity_required,
                    0
                ) * quantity_required;

                subassemblySeriesMap[subassemblyObj.name][detectorIdx] = partCount;
            });
        });

        const finalSeries = subassemblyNames.map(name => ({
            name,
            data: subassemblySeriesMap[name]
        }));

        const colors = subassemblyNames.map((_, i) => `hsl(${(i * 360) / subassemblyNames.length}, 70%, 50%)`);
        const chartHeight = 350; // or make it dynamic
        const legendMaxHeight = chartHeight / 5; // legend will take at most half


        const options = {
            chart: {
                type: 'bar',
                stacked: true,
                toolbar: { show: true }
            },
            colors,
            plotOptions: {
                bar: {
                    horizontal: false,
                    borderRadius: 4
                }
            },
            xaxis: {
                categories: detectorLabels
            },
            yaxis: {
                title: { text: 'Total Parts' }
            },
            legend: {
                position: 'bottom',
                height: legendMaxHeight,
            },
            tooltip: {
                y: {
                    formatter: (val: number) => `${val} part(s)`
                }
            }
        };

        setStackedBarSeries(finalSeries);
        setStackedBarOptions(options);
    }, [detectors, subAssemblies]);

    // Flowchart
    const [selectedDetector2, setSelectedDetector2] = useState<Detector | null>(null);
    const [selectedSubAssembly, setSelectedSubAssembly] = useState<SubAssembly | null>(null);

    // Util functions to display total costs
    function getSubAssemblyCost(sa: SubAssembly, invInfo: InventoryItems[]): number {
        return sa.parts.reduce((total, part) => {
            const inv = invInfo.find(i => i.partID === part.part);
            return inv ? total + inv.unitCost * part.quantity_required : total;
        }, 0);
    }

    function getDetectorCost(detector: Detector, subAssemblies: SubAssembly[], invInfo: InventoryItems[]): number {
        return detector.subassemblies.reduce((total, ds) => {
            const sa =
                typeof ds.subassembly === 'object'
                    ? ds.subassembly
                    : subAssemblies.find(s => s.id === ds.subassembly);

            return sa ? total + getSubAssemblyCost(sa, invInfo) : total;
        }, 0);
    }

    const totalSubAssemblyProductionCost = subAssemblyProduction.reduce((sum, item) => {
        const subassembly = subAssemblies.find(sa => sa.id === item.object_id);
        return subassembly ? sum + getSubAssemblyCost(subassembly, invInfo) * item.amount_produced : sum;
    }, 0);


    const totalDetectorProductionCost = detectorProduction.reduce((sum, item) => {
        const detector = detectors.find(d => d.id === item.object_id);
        return detector ? sum + getDetectorCost(detector, subAssemblies, invInfo) * item.amount_produced : sum;
    }, 0);

    // ------ Detector Flowchart ------
    // Set the latest detector version as the default
    useEffect(() => {
        if (detectors.length > 0) {
            const latest = getLatestDetectorVersion(detectors);
            setSelectedDetector2(latest);
        }
    }, [detectors]);

    // Generate flowchart nodes
    function generateFlowNodes(
        detector: Detector,
        subAssemblies: SubAssembly[],
        invInfo: InventoryItems[]
    ): FlowNode[] {
        const nodes: FlowNode[] = [];

        const totalDetectorCost = getDetectorCost(detector, subAssemblies, invInfo).toFixed(2);

        // Detector head node
        nodes.push({
            id: `detector-${detector.id}`,
            type: 'default',
            data: {
                label: (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 'bold' }} >{detector.name} (v{detector.version})</div>
                        <div>Total: ${totalDetectorCost}</div>
                    </div>
                )
            },
            position: { x: 300, y: 50 },
            style: {
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 6,
                padding: 10,
            },
        });

        // Sub-assembly nodes
        detector.subassemblies.forEach((ds, index) => {
            const sa =
                typeof ds.subassembly === 'object'
                    ? ds.subassembly
                    : subAssemblies.find((s) => s.id === ds.subassembly);

            if (!sa) return;

            // Build label JSX
            const saCost = getSubAssemblyCost(sa, invInfo).toFixed(2);
            const label = (
                <div style={{ fontSize: 12 }}>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: "1rem", justifyContent: 'center' }}>


                        <img
                            src={sa.subPicture ? sa.subPicture : fnnBlueLogo}
                            alt={sa.name}
                            className="part-image"
                            style={{
                                width: 35,
                                height: 35,
                                objectFit: 'cover',
                                marginRight: 8,
                                borderRadius: 4,
                            }}
                        // style={{ cursor: isEditing ? 'pointer' : 'default' }}
                        />

                        <strong>{sa.name} - ${saCost}</strong>

                    </div>


                    <ul style={{ listStyleType: 'none', padding: 0, marginTop: 8 }}>
                        {sa.parts.map((p) => {
                            const invPart = invInfo.find((i) => i.partID === p.part);
                            if (!invPart) return null;

                            const totalCost = (invPart.unitCost * p.quantity_required).toFixed(2);

                            return (
                                <li key={invPart.partID} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, textAlign: 'left' }}>
                                    <img
                                        src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                                        alt={invPart.partName}
                                        className="part-image"
                                        style={{
                                            width: 24,
                                            height: 24,
                                            objectFit: 'cover',
                                            marginRight: 8,
                                            borderRadius: 4,
                                        }}
                                    />
                                    <span>{invPart.partName} x{p.quantity_required} - ${totalCost}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );

            // Push the node
            nodes.push({
                id: `sub-${sa.id}`,
                type: 'default',
                data: { label },
                position: { x: 100 + index * 250, y: 250 },
                style: {
                    width: 220,
                    padding: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                },
            });
        });

        return nodes;
    }

    // Generate flowchart edges
    function generateFlowEdges(detector: Detector): Edge[] {
        return detector.subassemblies.map((ds) => {
            const saID = typeof ds.subassembly === 'object' ? ds.subassembly.id : ds.subassembly;
            return {
                id: `edge-detector-sub-${saID}`,
                source: `detector-${detector.id}`,
                target: `sub-${saID}`,
                type: 'smoothstep',
            };
        });
    }

    // ------ Sub-Assembly Flowchart ------
    function generateSubAssemblyNodes(
        subAssembly: SubAssembly,
        invInfo: InventoryItems[]
    ): FlowNode[] {
        const nodes: FlowNode[] = [];

        const totalCost = getSubAssemblyCost(subAssembly, invInfo).toFixed(2);

        // Head node for sub-assembly
        nodes.push({
            id: `sub-${subAssembly.id}`,
            type: 'default',
            data: {
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: "1rem", justifyContent: 'center' }}>

                        <img
                            src={subAssembly.subPicture ? subAssembly.subPicture : fnnBlueLogo}
                            alt={subAssembly.name}
                            className="part-image"
                            style={{
                                width: 35,
                                height: 35,
                                objectFit: 'cover',
                                marginRight: 8,
                                borderRadius: 4,
                            }}
                        // style={{ cursor: isEditing ? 'pointer' : 'default' }}
                        />

                        <div>
                            <div style={{ fontWeight: 'bold' }} >{subAssembly.name}</div>
                            <div>Total: ${totalCost}</div>
                        </div>
                    </div>
                )
            },
            position: { x: 300, y: 50 },
            style: {
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 6,
                padding: 10,
            },
        });


        // Part nodes
        subAssembly.parts.forEach((p, index) => {
            const invPart = invInfo.find(i => i.partID === p.part);
            if (!invPart) return;

            const label = (
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                    <img
                        src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                        alt={invPart.partName}
                        className='part-image'
                        style={{
                            width: 24,
                            height: 24,
                            objectFit: 'cover',
                            marginRight: 8,
                            borderRadius: 4,
                        }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <div style={{ textAlign: 'left' }}>{invPart.partName} x{p.quantity_required}</div>
                        <div> - ${(invPart.unitCost * p.quantity_required).toFixed(2)}</div>
                    </div>
                </div>
            );

            nodes.push({
                id: `part-${invPart.partID}`,
                type: 'default',
                data: { label },
                position: { x: 100 + index * 200, y: 200 },
                style: {
                    width: 180,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: 8,
                },
            });
        });

        return nodes;
    }

    function generateSubAssemblyEdges(subAssembly: SubAssembly): Edge[] {
        return subAssembly.parts.map(p => {
            return {
                id: `edge-sub-part-${p.part}`,
                source: `sub-${subAssembly.id}`,
                target: `part-${p.part}`,
                type: 'smoothstep',
            };
        });
    }

    // Analytics Functions
    function getLatestDetectorVersionNumber(detectors: Detector[]): number {
        return Math.max(...detectors.map(d => Number(d.version)));
    }

    function getNumberOfDetectorVersions(detectors: Detector[]): number {
        return detectors.length;
    }

    function getFullInventoryCost(invInfo: InventoryItems[]): number {
        return invInfo.reduce((total, item) => total + item.unitCost * item.currentAmount, 0);
    }

    function getFulfilledOrdersCostThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.reduce((sum, order) => {
            if (
                order.status === "Arrived" &&
                order.arrivalDate &&
                new Date(order.arrivalDate).getMonth() === now.getMonth() &&
                new Date(order.arrivalDate).getFullYear() === now.getFullYear()
            ) {
                return sum + (order.cost * order.expectedAmount);
            }
            return sum;
        }, 0);
    }

    function getMostExpensiveItem(invInfo: InventoryItems[]): string {
        const max = invInfo.reduce((prev, curr) => (curr.unitCost > prev.unitCost ? curr : prev), invInfo[0]);
        return max?.partName || '';
    }

    function isSameMonthAndYear(dateString: string | Date, now: Date = new Date()): boolean {
        const date = new Date(dateString);
        return (
            date.getUTCFullYear() === now.getUTCFullYear() &&
            date.getUTCMonth() === now.getUTCMonth()
        );
    }

    function getItemsOrderedThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.reduce((sum, order) => {
            if (
                order.orderedDate &&
                isSameMonthAndYear(order.orderedDate, now)
            ) {
                return sum + order.expectedAmount;
            }
            return sum;
        }, 0);
    }

    function getOrdersPlacedThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.filter(order =>
            (order.status === "Ordered" || order.status === "Arrived") &&
            order.orderedDate &&
            isSameMonthAndYear(order.orderedDate, now)
        ).length;
    }


    function getFulfilledOrdersCountThisMonth(forecastInfo: InventoryForecast[]): number {
        const now = new Date();
        return forecastInfo.filter(order =>
            order.status === "Arrived" &&
            order.arrivalDate &&
            isSameMonthAndYear(order.arrivalDate, now)
        ).length;
    }

    function getMostOrderedItemByAmount(forecastInfo: InventoryForecast[], invInfo: InventoryItems[]): string {
        const now = new Date();
        const partTotals: Record<number, number> = {};

        forecastInfo.forEach(order => {
            if (
                order.orderedDate &&
                isSameMonthAndYear(order.orderedDate, now)
            ) {
                partTotals[order.part] = (partTotals[order.part] || 0) + order.expectedAmount;
            }
        });

        const mostOrderedID = Object.entries(partTotals).sort((a, b) => b[1] - a[1])[0]?.[0];
        return invInfo.find(item => item.partID === Number(mostOrderedID))?.partName || '';
    }

    function getMostOrderedItemByOrders(forecastInfo: InventoryForecast[], invInfo: InventoryItems[]): string {
        const now = new Date();
        const orderCounts: Record<number, number> = {};

        forecastInfo.forEach(order => {
            if (
                order.orderedDate &&
                isSameMonthAndYear(order.orderedDate, now)
            ) {
                orderCounts[order.part] = (orderCounts[order.part] || 0) + 1;
            }
        });

        const mostOrderedID = Object.entries(orderCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
        return invInfo.find(item => item.partID === Number(mostOrderedID))?.partName || '';
    }

    // Getting the current dates for Analytics
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date: Date) =>
        `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;


    // Variable for generating pdf:
    const reportRef = useRef<HTMLDivElement>(null);
    const planningRef = useRef<HTMLDivElement>(null);

    // Reusable function
    const [isPrinting, setIsPrinting] = useState(false);

    const handlePrint = async (elementRef: React.RefObject<HTMLDivElement>, fileName: string) => {
        if (!elementRef.current) return;
        setIsPrinting(true);  // force desktop layout

        const element = elementRef.current;

        // Add print-mode class to force desktop layout
        element.classList.add("print-mode");

        // Save original styles
        const originalWidth = element.style.width;
        const originalHeight = element.style.height;
        const originalOverflow = element.style.overflow;

        try {
            element.style.width = "1700px";   // ensure desktop width
            element.style.height = "auto";
            element.style.overflow = "visible";

            await new Promise(resolve => setTimeout(resolve, 100));

            const canvas = await html2canvas(element, {
                scale: 2,
                scrollY: -window.scrollY,
                useCORS: true,
                windowWidth: 1024,   // important for consistent rendering
            });

            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "a4",
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const imgWidth = pageWidth - 40; // margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, "PNG", 20, 20, imgWidth, imgHeight);
            pdf.save(`${fileName}.pdf`);
        } catch (error) {
            console.error("❌ Failed to generate PDF:", error);
        } finally {
            // restore original styles
            element.style.width = originalWidth;
            element.style.height = originalHeight;
            element.style.overflow = originalOverflow;

            element.classList.remove("print-mode");
            setIsPrinting(false); // restore mobile layout
        }
    };




    const cards = [
        {
            title: 'Order',
            icon: <MdShoppingCart />,
            onClick: () => (setActiveSection('OrderParts'), setShowSectionPopup(true))
        },
        {
            title: 'Manage',
            icon: <FaLayerGroup />,
            options: [
                { label: 'Detectors', onClick: () => (setActiveSection('ManageDetector'), setShowSectionPopup(true)) },
                { label: 'Sub-Assemblies', onClick: () => (setActiveSection('ManageSubAssembly'), setShowSectionPopup(true)) },
            ]
        },
        {
            title: 'Build',
            icon: <BiSolidBookAdd />,
            onClick: () => closeConsumeAddPopup('AddConsumeSubAssembly', 'sub')
            // options: [
            //     { label: 'Detectors', onClick: () => closeConsumeAddPopup('AddConsumeDetector', 'det') },
            //     { label: 'Sub-Assemblies', onClick: () => closeConsumeAddPopup('AddConsumeSubAssembly', 'sub') }
            // ]
        },
        {
            title: 'Assemble',
            icon: <FaPuzzlePiece />,
            onClick: () => (setActiveSection('AssembleDetector'), setShowSectionPopup(true))
        },
        {
            title: 'Units',
            icon: <FaWarehouse />,
            onClick: () => (setActiveSection('ViewBuiltUnits'), setShowSectionPopup(true))
        }
    ];

    // Carousel logic for inv-card-stack
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isMobileCarousel, setIsMobileCarousel] = useState(window.innerWidth <= 1024);
    const [visibleCount, setVisibleCount] = useState(() => {
        const width = window.innerWidth;
        if (width <= 600) return 2;
        if (width <= 820) return 3;
        if (width <= 1024) return 4;
        // if (width <= 1250) return 5;
        return 5;
    });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobileCarousel(width <= 1024);
            if (width <= 600) setVisibleCount(2);
            else if (width <= 820) setVisibleCount(3);
            else if (width <= 1024) setVisibleCount(4);
            // else if (width <= 1250) setVisibleCount(5);
            else setVisibleCount(5);
        };

        window.addEventListener("resize", handleResize);
        handleResize(); // initialize on mount
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    const handleNext = () => {
        if (isMobileCarousel) {
            setCarouselIndex((prev) => (prev + 1) % cards.length);
        }
    };

    const handlePrev = () => {
        if (isMobileCarousel) {
            setCarouselIndex((prev) => (prev - 1 + cards.length) % cards.length);
        }
    };

    const getVisibleCards = () => {
        if (isMobileCarousel) {
            const visible = [];
            for (let i = 0; i < visibleCount; i++) {
                visible.push(cards[(carouselIndex + i) % cards.length]);
            }
            return visible;
        }
        return cards;
    };

    const [isMobile, setIsMobile] = useState(window.innerWidth < 840);
    const [isMobile2, setIsMobile2] = useState(window.innerWidth < 768);
    const [isMobile3, setIsMobile3] = useState(window.innerWidth < 585);
    const [isMobile4, setIsMobile4] = useState(window.innerWidth < 1050)
    const [expandedPartId, setExpandedPartId] = useState<number | null>(null);
    const [expandedForecastId, setExpandedForecastId] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 840);
            setIsMobile2(window.innerWidth < 768);
            setIsMobile3(window.innerWidth < 585);
            setIsMobile4(window.innerWidth < 1050);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const truncateText = (text: string, maxLength: number) =>
        text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

    // Renders part name, with hover tooltip when text is truncated
    function renderPartName(item: any) {
        const tooltipText = `
        ${item.partName}
        ${item.description ? "\n\n" + item.description : ""}
    `.trim();

        const nameContent = (
            <span className="tooltip-wrapper">
                {item.partLink ? (
                    <a
                        href={item.partLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="clickable-inv-option"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                    </a>
                ) : (
                    <span className="unclickable-inv-option">
                        {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                    </span>
                )}
                <div className="custom-tooltip part-name-tooltip">
                    <strong>{item.partName}</strong>
                    {item.description && (
                        <>
                            <br />
                            <span>{item.description}</span>
                        </>
                    )}
                </div>
            </span>
        );

        return (
            <div className="text-with-icon">
                {nameContent}
                {showInfoWithColor(item.lastCounted)}
            </div>
        );
    }

    function renderUnitAmountWithPackInfo(forecast: InventoryForecast) {
        const numberOfPackets = forecast.number_of_packets ?? 0;
        const amountPerPacket = forecast.amount_per_packet ?? 0;
        const hasPack = !!forecast.is_packet && numberOfPackets > 0 && amountPerPacket > 0;

        return (
            <div>
                <span className="tooltip-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    {forecast.expectedAmount}
                    {hasPack && (
                        <>
                            <TbPackages className="pack-icon" />
                            <div className="custom-tooltip pack-tooltip">
                                <strong>Item comes in a pack:</strong>
                                <br />
                                - Number of packs: {numberOfPackets}
                                <br />
                                - Items per pack: {amountPerPacket}
                                <br />
                                - Total amount: {forecast.expectedAmount}
                            </div>
                        </>
                    )}
                </span>
            </div>
        );
    }

    function renderPartNameWithPriority(forecast: InventoryForecast) {
        const matchingPart = invInfo.find(p => p.partID === forecast.part);

        if (!matchingPart) return <span>Unknown Part</span>;

        // Use first link from comma-separated sourceLinks
        const firstLink = forecast.sourceLinks?.split(",")[0]?.trim() || matchingPart.partLink || "";

        // Determine priority color
        const priority = forecast.priority || "Low";
        let priorityColor = "";
        if (priority === "Medium") priorityColor = "orange";
        else if (priority === "High") priorityColor = "red";

        // Part name with tooltip
        const nameContent = (
            <span className="tooltip-wrapper">
                {firstLink ? (
                    <a
                        href={firstLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="clickable-inv-option"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {truncateText(((matchingPart.mpn ? (matchingPart.mpn + " - ") : "") + matchingPart.partName), 40)}
                    </a>
                ) : (
                    <span className="unclickable-inv-option">
                        {truncateText(((matchingPart.mpn ? (matchingPart.mpn + " - ") : "") + matchingPart.partName), 40)}
                    </span>
                )}
                <div className="custom-tooltip part-name-tooltip">
                    <strong>{matchingPart.partName}</strong>
                    {matchingPart.description && (
                        <>
                            <br />
                            <span>{matchingPart.description}</span>
                        </>
                    )}
                </div>
            </span>
        );

        // Priority icon with tooltip
        const priorityIcon = priority !== "Low" && (
            <span className="tooltip-wrapper" style={{ marginLeft: "0.3rem" }}>
                <HiBellAlert color={priorityColor} />
                <div className="custom-tooltip pack-tooltip2">
                    <strong>Priority:</strong> {priority}
                    {forecast.lead_time && (
                        <>
                            <br />
                            <strong>Lead time:</strong> {forecast.lead_time}
                        </>
                    )}
                </div>
            </span>
        );

        return (
            <div className="text-with-icon">
                {nameContent}
                {priorityIcon}
            </div>
        );
    }






    // Functions to handle the sub-assembly planning
    const [subAssemblyAmounts, setSubAssemblyAmounts] = useState<Record<number, number>>({}); // { [subAssemblyId]: number of sub-assemblies to build }
    const [partsNeeded, setPartsNeeded] = useState<Record<number, number>>({}); // { [partID]: total amount needed considering all sub-assemblies }

    const handleSubAssemblyAmountChange = (subId: number, newAmount: number) => {
        setSubAssemblyAmounts(prev => {
            const updated = { ...prev, [subId]: newAmount };

            if (selectedDetectorId) {
                const detector = detectors.find(d => d.id === selectedDetectorId);
                if (detector) {
                    // find how many detectors we can make from the updated plan
                    const possibleDetectors = Math.min(
                        ...detector.subassemblies.map(ds => {
                            const subId = typeof ds.subassembly === "number" ? ds.subassembly : ds.subassembly.id;
                            const subAmount = updated[subId] || 0;
                            const perDetector = ds.quantity_required || 1;
                            return Math.floor(subAmount / perDetector);
                        })
                    );
                    setDetectorsToAdd(possibleDetectors);
                }
            }

            return updated;
        });
    };

    useEffect(() => {
        // Recalculate total parts needed whenever subAssemblyAmounts changes
        const newPartsNeeded: Record<number, number> = {};

        subAssemblies.forEach(sub => {
            const amount = subAssemblyAmounts[sub.id] || 0;
            sub.parts.forEach(p => {
                newPartsNeeded[p.part] = (newPartsNeeded[p.part] || 0) + p.quantity_required * amount;
            });
        });

        setPartsNeeded(newPartsNeeded);
    }, [subAssemblyAmounts, subAssemblies]);

    const [considerInventory, setConsiderInventory] = useState(true);
    const [considerForecast, setConsiderForecast] = useState(false);
    const [includeMaxBuildable, setIncludeMaxBuildable] = useState(false);
    const [detectorsToAdd, setDetectorsToAdd] = useState<number>(0);

    // On detector change, prefill amounts
    useEffect(() => {
        if (!selectedDetectorId || !detectors.length || !subAssemblies.length) return;

        const detector = detectors.find(d => d.id === selectedDetectorId);
        if (!detector) return;

        const maxBuildable = getMaxBuildableDetectors(detector, subAssemblies, invInfo);
        setMaxBuildableDetectors(maxBuildable);

        // Prefill subAssemblyAmounts for maxBuildable detectors
        // const defaults: Record<number, number> = {};
        // detector.subassemblies.forEach(ds => {
        //     const sub = subAssemblies.find(sa => sa.id === ds.subassembly);
        //     if (sub) {
        //         const amountPerDetector = ds.quantity_required || 1;
        //         defaults[sub.id] = amountPerDetector * maxBuildable;
        //     }
        // });
        // setSubAssemblyAmounts(defaults);
    }, [selectedDetectorId, detectors, subAssemblies, invInfo]);

    const missingRequests = Object.entries(partsNeeded)
        .map(([partId, needed]) => {
            const inventoryItem = invInfo.find(i => i.partID === Number(partId));
            const available = considerInventory ? (inventoryItem?.currentAmount || 0) : 0;
            const forecasted = considerForecast ? (forecastAmountsByPart[Number(partId)] || 0) : 0;

            let isCovered = false;
            if (considerForecast) {
                isCovered = (available + forecasted) >= needed;
            } else if (considerInventory) {
                isCovered = available >= needed;
            }

            if (isCovered) return null;

            let requestQty = 0;
            if (considerForecast) {
                requestQty = needed - (available + forecasted);
            } else if (considerInventory) {
                requestQty = needed - available;
            } else {
                requestQty = needed;
            }

            return {
                partId: Number(partId),
                partName: inventoryItem?.partName || `Unknown Part #${partId}`,
                requestQty,
            };
        })
        .filter((r): r is { partId: number; partName: string; requestQty: number } => r !== null);


    // ------------------------------------ New functions to determine color and tooltip of the amount based on the set detector number ------------------------------------

    const [desiredPartsNeeded, setDesiredPartsNeeded] = useState<Record<number, number>>({});
    const [desiredDetectors, setDesiredDetectors] = useState<number | null>(null);
    const [stockAmountId, setStockAmountId] = useState<number | null>(null);

    useEffect(() => {
        const fetchStockNumber = async () => {
            try {
                const response = await fetch("/api/inventory_stock_amount/", {
                    credentials: "same-origin"
                });
                const data = await response.json();
                if (response.ok && data.length > 0) {
                    setDesiredDetectors(data[0].stockAmount ?? null);
                    setStockAmountId(data[0].id); // store the id for later PUT
                } else {
                    console.error("Failed to load stock number:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching stock number:", error);
            }
        };

        fetchStockNumber();
    }, []);

    useEffect(() => {
        if (!detectors.length || !subAssemblies.length) return;

        const latestDetector = getLatestDetectorVersion(detectors);
        if (!latestDetector) return;

        const partsMap: Record<number, number> = {};

        latestDetector.subassemblies.forEach(ds => {
            const subassembly = typeof ds.subassembly === "number"
                ? subAssemblies.find(s => s.id === ds.subassembly)
                : ds.subassembly;

            if (!subassembly) return;

            subassembly.parts.forEach(p => {
                const total = (p.quantity_required || 0) * ds.quantity_required * (desiredDetectors ?? 0);
                partsMap[p.part] = (partsMap[p.part] || 0) + total;
            });
        });

        setDesiredPartsNeeded(partsMap);
    }, [detectors, subAssemblies, desiredDetectors]);

    // New function for stock status
    function getStockStatus(
        item: InventoryItems,
        desiredPartsNeeded: Record<number, number>
    ): { color: string; status: string; tooltip: string } {
        const requiredFromDetector = desiredPartsNeeded[item.partID];
        const stockTarget = item.stock_target ?? 0; // ✅ New variable
        const current = item.currentAmount ?? 0;
        const unitCost = item.unitCost ?? 0;

        let required: number | null = null;
        let source: "stock" | "detector" | "none" = "none";

        // 1️⃣ Priority: use stock_target if available and > 0
        if (stockTarget > 0) {
            required = stockTarget;
            source = "stock";
        }
        // 2️⃣ Otherwise, use detector requirement if exists
        else if (requiredFromDetector) {
            required = requiredFromDetector;
            source = "detector";
        }
        // 3️⃣ Otherwise, no known requirement
        else {
            return {
                color: "black",
                status: "N/A",
                tooltip: "Item is neither from a detector build nor has its stock target determined."
            };
        }

        // ✅ Now calculate ratios
        const difference = current - required;
        const ratio = current / required;
        const percent = Math.round(ratio * 100);

        // 🔴 Critical (≤50%)
        if (ratio < 0.5) {
            return {
                color: "red",
                status: `Critical - ${percent}%`,
                tooltip:
                    `Critical (${percent}% of Stock) — Needs ${Math.abs(difference)} more to meet the ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}.`
            };
        }

        // 🟠 Low (>50% but <100%)
        if (ratio < 1) {
            return {
                color: "orange",
                status: `Low - ${percent}%`,
                tooltip:
                    `Low (${percent}% of Stock) — Needs ${Math.abs(difference)} more to meet the ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}.`
            };
        }

        // 🟢 Adequate (≤120%)
        if (ratio <= 1.2) {
            return {
                color: "green",
                status: `Adequate - ${percent}%`,
                tooltip:
                    `Adequate (${percent}% of Stock) — ${current} available out of ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}.`
            };
        }

        // 🟣 Excess (>120%)
        const extraCost = difference * unitCost;
        const formattedCost = extraCost.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        });

        return {
            color: "purple",
            status: `Excess - ${percent}%`,
            tooltip:
                `Excess (${percent}% of Stock) — ${difference} more than the ${required} required by the set ${source === "stock" ? "stock target" : "detector stock"}. Estimated ${formattedCost} in idle inventory.`
        };
    }



    // Modifying the stock number
    const [isEditingStock, setIsEditingStock] = useState(false);
    const [newStockAmount, setNewStockAmount] = useState(desiredDetectors ?? 0);

    const handleStockNumber = async () => {
        if (!stockAmountId) return; // no record to update

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch(`/api/inventory_stock_amount/${stockAmountId}/`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "same-origin",
                body: JSON.stringify({ stockAmount: newStockAmount }),
            });

            if (response.ok) {
                setDesiredDetectors(newStockAmount);
                setIsEditingStock(false);
            } else {
                console.error("❌ Failed to update stock amount:", await response.text());
            }
        } catch (error) {
            console.error("❌ Error updating stock amount:", error);
        }
    };

    // ------------------------------------ New functions to determine color and tooltip of the amount based on the set detector number ------------------------------------

    // Functions for assemble popup
    const [assembleDetectorId, setAssembleDetectorId] = useState<number | null>(null);
    const [assembleSelectedSubAssemblies, setAssembleSelectedSubAssemblies] = useState<Record<number, string>>({});
    const [assembleMacAddress, setAssembleMacAddress] = useState<string>("");
    const [assembleDetCode, setAssembleDetCode] = useState<string>("");
    const [editCodes, setEditCodes] = useState<Record<string, string>>({});

    const assembleDetector = async (
        detectorId: number | null,
        selectedSubAssemblies: Record<number, string>,
        macAddress: string,
        code?: string,
    ) => {
        if (!detectorId || !macAddress.trim()) {
            toast.warn("Please fill in all required fields.", {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: 'white',
                    color: 'black',
                    fontSize: '16px',
                },
            });
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            mac_address: macAddress,
            subassemblies: Object.values(selectedSubAssemblies),
            assembled_by: loggedFnnUserId,
            detector: detectorId,
            code: (code ? code : null)
        };

        try {
            const response = await fetch("/api/inventory_built_dets/", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                // Refresh data
                fetchBuiltSubAssemblies();
                fetchbuiltDetectors();
                setShowSectionPopup(false);
                setAssembleDetectorId(null);
                setAssembleSelectedSubAssemblies({});
                setAssembleMacAddress("");
                setAssembleDetCode("");

                toast.success("Detector assembled successfully!", {
                    autoClose: 3000,
                    style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
                });
            } else {
                const errorData = await response.json();
                console.error(errorData);

                // Check for specific MAC address conflict
                if (errorData.mac_address && Array.isArray(errorData.mac_address)) {
                    toast.error(errorData.mac_address[0], {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                } else {
                    toast.error("Failed to assemble detector. Please try again.", {
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        style: {
                            backgroundColor: 'white',
                            color: 'black',
                            fontSize: '16px',
                        },
                    });
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred.", {
                autoClose: 4000,
                style: { backgroundColor: 'white', color: 'black', fontSize: '16px' },
            });
        }
    };

    const handleCodeUpdate = async (det: any) => {
        const newCode = editCodes[det.mac_address]?.trim() || null;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            mac_address: det.mac_address,
            subassemblies: det.subassemblies || [],
            assembled_by: det.assembled_by,
            detector: det.detector,
            code: newCode,
            status: det.status,
        };

        try {
            const response = await fetch(`/api/inventory_built_dets/${det.mac_address}/`, {
                method: "PUT",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success("Detector code updated successfully!", {
                    autoClose: 3000,
                    style: { backgroundColor: "white", color: "black", fontSize: "16px" },
                });
                fetchbuiltDetectors(); // refresh list
            } else {
                const errorData = await response.json();
                console.error(errorData);
                toast.error("Failed to update detector code.", {
                    autoClose: 4000,
                    style: { backgroundColor: "white", color: "black", fontSize: "16px" },
                });
            }
        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred.", {
                autoClose: 4000,
                style: { backgroundColor: "white", color: "black", fontSize: "16px" },
            });
        }
    };


    // Functions for managing built units
    const [selectedUnitStatus, setSelectedUnitStatus] = useState<string | null>("available");
    const [showSelectDetectorPopup, setShowSelectDetectorPopup] = useState(false);
    const [detectorForSubAssembly, setDetectorForSubAssembly] = useState<string | null>(null);
    const [currentSubAssemblyId, setCurrentSubAssemblyId] = useState<string | null>(null);

    // Confirmation popup variables
    const [showStatusConfirmPopup, setShowStatusConfirmPopup] = useState(false);
    const [statusToConfirm, setStatusToConfirm] = useState<string | null>(null);
    const [subAssemblyToConfirmId, setSubAssemblyToConfirmId] = useState<string | null>(null);

    const handleUnitStatusChange = async (
        id: string,
        newStatus: string,
        usedByMac?: string
    ) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            // If marking as "installed" and no detector provided, open the popup
            if (newStatus === "installed" && !usedByMac) {
                setCurrentSubAssemblyId(id);
                setShowSelectDetectorPopup(true);
                return;
            }

            const targetSub = builtSubAssemblies.find(bsa => bsa.id === id);
            if (!targetSub) return toast.error("Sub-assembly not found", { autoClose: 4000 });

            // If "installed", update detector
            if (newStatus === "installed" && usedByMac) {
                const targetDetector = builtDetectors.find(det => det.mac_address === usedByMac);
                if (!targetDetector) return toast.error("Invalid detector selected", { autoClose: 4000 });

                // Remove any previous subassembly of the same type
                const updatedSubIds = targetDetector.subassemblies.filter(subId => {
                    const subObj = builtSubAssemblies.find(bsa => bsa.id === subId);
                    return subObj?.subassembly !== targetSub!.subassembly;
                });
                updatedSubIds.push(targetSub.id);

                const detectorPayload = {
                    ...targetDetector,
                    subassemblies: updatedSubIds,
                };

                const detResp = await fetch(
                    `/api/inventory_built_dets/${usedByMac}/`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        credentials: "same-origin",
                        body: JSON.stringify(detectorPayload),
                    }
                );

                if (!detResp.ok) return toast.error("❌ Failed to link sub-assembly to detector", { autoClose: 4000 });
            }

            // PUT subassembly update
            const subResp = await fetch(
                `/api/inventory_built_subs/${id}/`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    credentials: "same-origin",
                    body: JSON.stringify({
                        ...targetSub,
                        status: newStatus,
                        used_by: usedByMac || null,
                    }),
                }
            );

            if (!subResp.ok) return toast.error("❌ Failed to update sub-assembly", { autoClose: 4000 });

            await Promise.all([fetchBuiltSubAssemblies(), fetchbuiltDetectors()]);
            toast.success("Status updated successfully", { autoClose: 3000 });
        } catch (error) {
            console.error("❌ Error updating status:", error);
            toast.error("❌ Error updating status", { autoClose: 4000 });
        }
    };

    // Add this at the top of your component
    const [openSlips, setOpenSlips] = useState<{ [key: string]: boolean }>({});

    // Toggle slip open/close
    const toggleSlip = (id: string | number) => {
        const key = String(id); // ✅ ensure it's a string
        setOpenSlips(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Function to change status of assembled detector
    const handleDetectorStatusChange = async (macAddress: string, newStatus: string) => {
        const det = builtDetectors.find(d => d.mac_address === macAddress);
        if (!det) {
            console.error("Detector not found for MAC:", macAddress);
            return;
        }

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const bodyData = {
                mac_address: det.mac_address,
                built_at: det.built_at,
                assembled_by: det.assembled_by,
                detector: det.detector,
                subassemblies: det.subassemblies,
                status: newStatus,
            };

            const response = await fetch(`/api/inventory_built_dets/${macAddress}/`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "same-origin", // ✅ ensure session cookie is sent
                body: JSON.stringify(bodyData),
            });

            if (!response.ok) {
                const errText = await response.text();
                console.error("PUT failed:", errText);
                throw new Error("Failed to update detector status");
            }

            // ✅ Update local state
            setBuiltDetectors(prev =>
                prev.map(det =>
                    det.mac_address === macAddress ? { ...det, status: newStatus } : det
                )
            );

        } catch (error) {
            console.error("Error updating detector status:", error);
            alert("Failed to update detector status.");
        }
    };

    const [expandedSlips, setExpandedSlips] = useState<Record<number, boolean>>({});
    const [editedValuesMap, setEditedValuesMap] = useState<Record<number, Partial<InventoryItems>>>({});

    const stopClick = (e: React.MouseEvent) => e.stopPropagation();


    return (
        <div className='inventory-wrapper'>

            <TeamHeader title="Inventory" />
            <ToastContainer />

            <div className="carousel-container">
                {isMobileCarousel && (
                    <button className="carousel-arrow left" onClick={handlePrev}>&lt;</button>
                )}

                <div className="inventory-card-stack">
                    {getVisibleCards().map((card, idx) => (
                        <div
                            key={idx}
                            className={`inventory-card ${card.options ? 'subassembly-card' : 'standard-inv-card'}`}
                            onClick={card.onClick}
                        >
                            <div className={`card-default ${card.options ? 'subassembly-default' : ''}`}>
                                <h1>{card.icon} {card.title}</h1>
                            </div>
                            {card.options && (
                                <div className="card-hoverable subassembly-options">
                                    {card.options.map((opt, index) => (
                                        <div key={index} className="hover-item" onClick={opt.onClick}>
                                            {opt.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {isMobileCarousel && (
                    <button className="carousel-arrow right" onClick={handleNext}>&gt;</button>
                )}
            </div>

            <div className='table-and-stats'>

                <div className='inv-filter-and-table'>

                    <div className='inv-stat-filters-and-opener'>

                        <div className="inv-sort-dropdown-wrapper">

                            <div className='inv-filter-sort'>
                                <FilterDropdown
                                    dropdownClassName="inv-filter-dropdown"
                                    sideClass="inv-side-drop "
                                    initialSelected={{
                                        subAssemblies: [],
                                        categories: [],
                                    }}
                                    resetValue={{
                                        subAssemblies: [],
                                        categories: [],
                                    }}
                                    sections={[
                                        {
                                            key: "subAssemblies",
                                            title: "Sub-Assembly",
                                            options: subAssemblies.map(sa => sa.name),
                                            type: "checkbox",
                                        },
                                        {
                                            key: "categories",
                                            title: "Category",
                                            options: CATEGORY_OPTIONS.map(opt => opt.value),
                                            type: "checkbox",
                                            formatLabel: (option) =>
                                                CATEGORY_OPTIONS.find(opt => opt.value === option)?.label ?? option,
                                        },
                                    ]}
                                    onChange={(filters) => {
                                        const selectedSubNames = filters.subAssemblies || [];
                                        const matchedSubIds = subAssemblies
                                            .filter(sa => selectedSubNames.includes(sa.name))
                                            .map(sa => sa.id);

                                        setFilterSelectedSubAssemblies(matchedSubIds);
                                        setSelectedCategories(filters.categories || []);
                                    }}
                                />

                                <div
                                    className={`inv-sort-dropdown ${isDropdownOpen ? 'open' : ''}`}
                                    onClick={() => setIsDropdownOpen(prev => !prev)}
                                >
                                    <span className="inv-sort-selected">
                                        {sortOptions.find(o => o.value === sortMode)?.label}
                                    </span>
                                    <span className={`inv-sort-arrow ${isDropdownOpen ? 'rotated' : ''}`}>▼</span>
                                    {isDropdownOpen && (
                                        <div className="inv-sort-options">
                                            {sortOptions.map(option => (
                                                <div
                                                    key={option.value}
                                                    className={`inv-sort-option ${sortMode === option.value ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSortMode(option.value as any);
                                                        setIsDropdownOpen(false);
                                                    }}
                                                >
                                                    {option.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="inv-search-wrapper">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="inv-search-input"
                                />
                            </div>

                        </div>

                        <div className='inv-add-item-and-cards'>
                            <button className='inv-stats-opener inv-stats-extender double-extender' onClick={() => setShowAddItemPopup(true)}>
                                Add Item
                                <IoAddCircleOutline className='add-part-icon' />
                            </button>

                            <button className='inv-stats-opener inv-stats-extender' onClick={() => (setActiveSection('StatsCards'), setShowSectionPopup(true))}>
                                Stats Cards
                            </button>
                        </div>

                    </div>

                    <div className="inventory-slip-wrapper">
                        {sortedInventory.map((item) => {
                            const isOpen = !!expandedSlips[item.partID];
                            const editedValues = editedValuesMap[item.partID] || {};

                            const handleToggle = () =>
                                setExpandedSlips(prev => ({ ...prev, [item.partID]: !prev[item.partID] }));

                            const handleToggleDesktop = () =>
                                setExpandedSlips(prev => ({ ...prev, [item.partID]: true }));

                            const handleSave = (e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (Object.keys(editedValues).length > 0) {
                                    updateInventoryItem(item.partID, editedValues);
                                    setEditedValuesMap(prev => ({ ...prev, [item.partID]: {} }));
                                }
                                setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                            };

                            const handleKeyDown = (e: React.KeyboardEvent) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSave(e as any); // trigger same logic as clicking save
                                }
                            };

                            if (isMobile) {
                                return (
                                    <div
                                        key={item.partID}
                                        className="inventory-item-container"
                                        style={{ display: "flex", flexDirection: "column" }}
                                    >
                                        {/* Closed Slip (Mobile) */}
                                        {!isOpen && (
                                            <div
                                                className="inventory-item-slip mobile-slip"
                                                onClick={handleToggle}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <img
                                                    src={item.partPicture || fnnBlueLogo}
                                                    className="part-image"
                                                    alt={item.partName}
                                                />
                                                <div className="inventory-item-section">
                                                    <p>{renderPartName(item)}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Open Slip (Mobile) */}
                                        {isOpen && (
                                            <div
                                                className="inventory-item-slip mobile-slip open"
                                                onClick={handleToggle}
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    width: "100%",
                                                }}
                                            >

                                                {/* --- Image Upload Field --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Image</strong></p>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                                                        <img
                                                            src={item.partPicture || fnnBlueLogo}
                                                            alt={item.partName}
                                                            className="part-image"
                                                            style={{ cursor: 'pointer', maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const fileInput = document.getElementById(`img-input-${item.partID}`);
                                                                if (fileInput) fileInput.click();
                                                            }}
                                                        />
                                                    </div>
                                                    <input
                                                        type="file"
                                                        id={`img-input-${item.partID}`}
                                                        style={{ display: 'none' }}
                                                        accept="image/*"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            handleFileUpload(item.partID, file, "image"); // Reuse your upload handler
                                                        }}
                                                    />
                                                </div>

                                                {/* --- Part Name --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Part Name</strong></p>
                                                    <input
                                                        type="text"
                                                        defaultValue={item.partName}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], partName: e.target.value },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />

                                                </div>

                                                {/* --- Unit Amount --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Unit Amount</strong></p>
                                                    {isOpen ? (
                                                        <input
                                                            type="number"
                                                            defaultValue={item.currentAmount}
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], currentAmount: Number(e.target.value) }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    ) : (
                                                        (() => {

                                                            return (
                                                                <p>{item.currentAmount}</p>
                                                            );
                                                        })()
                                                    )}
                                                </div>

                                                <div className="inventory-item-section">
                                                    <div className='inventory-mobile-checkbox'>
                                                        <p style={{ margin: 0 }}>
                                                            <strong>
                                                                {(
                                                                    editedValuesMap[item.partID]?.sold_in_packets ??
                                                                    item.sold_in_packets
                                                                )
                                                                    ? "Amount in Pack"
                                                                    : "Sold in Packs?"
                                                                }
                                                            </strong>
                                                        </p>

                                                        <div style={{ display: 'flex', alignItems: "flex-end", gap: "0.5rem", flexDirection: 'column' }}>
                                                            {/* Checkbox */}
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    editedValuesMap[item.partID]?.sold_in_packets ??
                                                                    !!item.sold_in_packets
                                                                }
                                                                onClick={stopClick}
                                                                onChange={(e) =>
                                                                    setEditedValuesMap(prev => ({
                                                                        ...prev,
                                                                        [item.partID]: {
                                                                            ...prev[item.partID],
                                                                            sold_in_packets: e.target.checked,
                                                                            amount_per_packet: e.target.checked
                                                                                ? prev[item.partID]?.amount_per_packet ?? item.amount_per_packet ?? null
                                                                                : null, // clear when unchecked
                                                                        },
                                                                    }))
                                                                }
                                                            />

                                                            {/* Amount per packet (only if checked) */}
                                                            {(editedValuesMap[item.partID]?.sold_in_packets ??
                                                                item.sold_in_packets) && (
                                                                    <input
                                                                        type="number"
                                                                        min={1}
                                                                        placeholder="How many?"
                                                                        defaultValue={
                                                                            editedValuesMap[item.partID]?.amount_per_packet ??
                                                                            item.amount_per_packet ??
                                                                            ""
                                                                        }
                                                                        onClick={stopClick}
                                                                        onChange={(e) =>
                                                                            setEditedValuesMap(prev => ({
                                                                                ...prev,
                                                                                [item.partID]: {
                                                                                    ...prev[item.partID],
                                                                                    amount_per_packet:
                                                                                        e.target.value === ""
                                                                                            ? null
                                                                                            : Number(e.target.value),
                                                                                },
                                                                            }))
                                                                        }
                                                                        onKeyDown={handleKeyDown}
                                                                    />
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* --- Stock Target --- */}
                                                <div className="inventory-item-section">

                                                    <div style={{ display: "flex", gap: "0.3rem" }}>
                                                        <p><strong>Stock Target</strong></p>
                                                        {showInfoTooltip("Desired amount to be kept in stock.")}
                                                    </div>
                                                    <input
                                                        type="number"
                                                        defaultValue={item.stock_target ?? ''}  // empty string if null
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: {
                                                                    ...prev[item.partID],
                                                                    stock_target: e.target.value === '' ? null : Number(e.target.value), // handle clear as null
                                                                },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />

                                                </div>

                                                {/* --- Stock Status --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Stock Status</strong></p>
                                                    <span
                                                        style={{
                                                            color: getStockStatus(item, desiredPartsNeeded).color,
                                                            fontWeight: 600,
                                                            cursor: "help",
                                                            paddingRight: '1rem',
                                                            paddingTop: '0.25rem',
                                                            paddingBottom: '0.25rem'
                                                        }}
                                                        className="tooltip-wrapper"
                                                    >
                                                        {getStockStatus(item, desiredPartsNeeded).status}
                                                        <div className="custom-tooltip">
                                                            {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                        </div>
                                                    </span>
                                                </div>

                                                {/* --- Unit Cost --- */}
                                                <div className="inventory-item-section">
                                                    <div style={{ display: "flex", gap: "0.3rem" }}>
                                                        <p><strong>Unit Cost</strong></p>
                                                        {showInfoTooltip("Price of a single unit of this item. NOT the cost of the pack")}
                                                    </div>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        defaultValue={item.unitCost}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], unitCost: Number(e.target.value) },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </div>

                                                {/* --- Category --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Category</strong></p>
                                                    <select
                                                        defaultValue={item.category}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], category: e.target.value },
                                                            }))
                                                        }
                                                    >
                                                        {CATEGORY_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* --- Manufacturer --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Manufacturer</strong></p>
                                                    <input
                                                        type="text"
                                                        defaultValue={item.manufacturer || ""}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], manufacturer: e.target.value },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </div>

                                                {/* --- MPN --- */}
                                                <div className="inventory-item-section">
                                                    <div style={{ display: "flex", gap: "0.3rem" }}>
                                                        <p><strong>MPN</strong></p>
                                                        {showInfoTooltip("Manufacturer Part Number - normally found in the part link.")}
                                                    </div>
                                                    <input
                                                        type="text"
                                                        defaultValue={item.mpn || ""}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], mpn: e.target.value },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </div>

                                                {/* --- Link --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Link</strong></p>
                                                    <input
                                                        type="url"
                                                        defaultValue={item.partLink || ""}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], partLink: e.target.value },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </div>

                                                {/* --- Document Upload --- */}
                                                <div className="inventory-item-section">
                                                    <div style={{ display: "flex", gap: "0.3rem" }}>
                                                        <p><strong>Document</strong></p>
                                                        {showInfoTooltip("Normally used for custom parts indicating its SOP.")}
                                                    </div>

                                                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                                                        {item.partDocument ? (
                                                            <a
                                                                href={item.partDocument}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="clickable-inv-option"
                                                                onClick={stopClick}
                                                            >
                                                                Download
                                                            </a>
                                                        ) : (
                                                            <span>None</span>
                                                        )}
                                                        <button
                                                            className="counted-button mobile-counted-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const fileInput = document.getElementById(`doc-input-${item.partID}`);
                                                                if (fileInput) fileInput.click();
                                                            }}
                                                        >
                                                            Upload
                                                        </button>
                                                    </div>

                                                    <input
                                                        type="file"
                                                        id={`doc-input-${item.partID}`}
                                                        style={{ display: "none" }}
                                                        accept=".pdf,.doc,.docx,.txt"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            handleFileUpload(item.partID, file, "document");
                                                        }}
                                                    />
                                                </div>

                                                {/* --- Description --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Description</strong></p>
                                                    <textarea
                                                        defaultValue={item.description || ""}
                                                        onClick={stopClick}
                                                        rows={4}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], description: e.target.value },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </div>

                                                {/* --- Location --- */}
                                                <div className="inventory-item-section">
                                                    <p><strong>Location</strong></p>
                                                    <textarea
                                                        defaultValue={item.location || ""}
                                                        onClick={stopClick}
                                                        rows={4}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], location: e.target.value },
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </div>

                                                <div className="inventory-item-section">
                                                    <p><strong>Picture of Location</strong></p>

                                                    <div
                                                        className="location-picture-container"
                                                        style={{ backgroundColor: item.location_picture ? "transparent" : "#e2dedeff" }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const fileInput = document.getElementById(`location-input-${item.partID}`);
                                                            if (fileInput) fileInput.click();
                                                        }}
                                                    >
                                                        {item.location_picture ? (
                                                            <>
                                                                <img
                                                                    src={item.location_picture}
                                                                    alt="Location"
                                                                    className="location-picture"
                                                                />
                                                            </>
                                                        ) : (
                                                            <AiFillPicture className="location-placeholder-icon" />
                                                        )}

                                                        <div className="location-picture-overlay">
                                                            <span>Click to Upload</span>
                                                        </div>
                                                    </div>

                                                    <input
                                                        type="file"
                                                        id={`location-input-${item.partID}`}
                                                        style={{ display: "none" }}
                                                        accept="image/*"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            handleFileUpload(item.partID, file, "location");
                                                        }}
                                                    />
                                                </div>

                                                {/* --- Counted Button --- */}
                                                <div className="inventory-item-section" style={{ alignItems: "center" }}>
                                                    <p><strong>Mark as Counted</strong></p>
                                                    <button
                                                        className="delete-part-button mobile-confirm-part-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setItemToCount(item);
                                                            setShowCountedPopup(true);
                                                        }}
                                                    >
                                                        ✓
                                                    </button>
                                                </div>

                                                {/* --- Save / Delete Buttons --- */}
                                                <div
                                                    className="inventory-item-open-slip-buttons"
                                                    style={{ display: "flex", gap: "0.5rem" }}
                                                    onClick={handleToggle}
                                                >
                                                    <button onClick={handleSave}>Save</button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setItemToDeleteId(item.partID);
                                                            setShowDeleteItemPopup(true);
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }


                            return (
                                <div key={item.partID} className="inventory-item-container">
                                    {/* --- Collapsed / Header Row --- */}
                                    <div className={`inventory-item-slip ${isOpen ? "open" : ""}`} onClick={handleToggleDesktop}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                            {/* --- Image Upload Field --- */}
                                            <div
                                                style={{
                                                    position: "relative",
                                                    display: "inline-block",
                                                    cursor: isOpen ? "pointer" : "default"
                                                }}
                                                onClick={(e) => {
                                                    if (!isOpen) return;
                                                    e.stopPropagation();
                                                    const fileInput = document.getElementById(`img-input-${item.partID}`);
                                                    if (fileInput) fileInput.click();
                                                }}
                                            >
                                                <img
                                                    src={item.partPicture || fnnBlueLogo}
                                                    className="part-image desktop-upload-image"
                                                    alt={item.partName}
                                                />

                                                {item.partPicture && (
                                                    <div className="part-image-tooltip">
                                                        <img src={item.partPicture} alt="Preview" />
                                                    </div>
                                                )}

                                                {isOpen && (
                                                    <>
                                                        <div className="image-upload-overlay">
                                                            <span>Click to Upload</span>
                                                        </div>

                                                    </>
                                                )}
                                            </div>

                                            <input
                                                type="file"
                                                id={`img-input-${item.partID}`}
                                                style={{ display: "none" }}
                                                accept="image/*"
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    handleFileUpload(item.partID, file, "image"); // Reuse your handler
                                                }}
                                            />

                                            {/* --- Part Name --- */}
                                            <div className="inventory-item-section">
                                                <p><strong>Part Name</strong></p>
                                                {isOpen ? (
                                                    <input
                                                        type="text"
                                                        defaultValue={item.partName}
                                                        onClick={stopClick}
                                                        onChange={(e) =>
                                                            setEditedValuesMap(prev => ({
                                                                ...prev,
                                                                [item.partID]: { ...prev[item.partID], partName: e.target.value }
                                                            }))
                                                        }
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                ) : (
                                                    <p>{renderPartName(item)}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="inventory-item-section">
                                            <p><strong>Unit Amount</strong></p>
                                            {isOpen ? (
                                                <input
                                                    type="number"
                                                    defaultValue={item.currentAmount}
                                                    onClick={stopClick}
                                                    onChange={(e) =>
                                                        setEditedValuesMap(prev => ({
                                                            ...prev,
                                                            [item.partID]: { ...prev[item.partID], currentAmount: Number(e.target.value) }
                                                        }))
                                                    }
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                (() => {

                                                    return (
                                                        <p>{item.currentAmount}</p>
                                                    );

                                                })()
                                            )}
                                        </div>


                                        <div className="inventory-item-section">
                                            <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                <p style={{ whiteSpace: 'nowrap' }}><strong>Unit Cost</strong></p>
                                                {showInfoTooltip("Price of a single unit of this item. NOT the cost of the pack.")}
                                            </div>

                                            {isOpen ? (
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    defaultValue={item.unitCost}
                                                    onClick={stopClick}
                                                    onChange={(e) =>
                                                        setEditedValuesMap(prev => ({
                                                            ...prev,
                                                            [item.partID]: { ...prev[item.partID], unitCost: Number(e.target.value) }
                                                        }))
                                                    }
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <p>${item.unitCost.toFixed(2)}</p>
                                            )}
                                        </div>

                                        <div className="inventory-item-section">
                                            <p><strong>Total Cost</strong></p>

                                            {isOpen ? (
                                                <p style={{ marginTop: '0.3rem' }}>${(item.unitCost * item.currentAmount).toFixed(2)}</p>
                                            ) : (
                                                <p>${(item.unitCost * item.currentAmount).toFixed(2)}</p>
                                            )}
                                        </div>

                                        <div className="inventory-item-section">
                                            <p><strong>Category</strong></p>
                                            {isOpen ? (
                                                <select
                                                    defaultValue={item.category}
                                                    onClick={stopClick}
                                                    onChange={(e) =>
                                                        setEditedValuesMap(prev => ({
                                                            ...prev,
                                                            [item.partID]: { ...prev[item.partID], category: e.target.value }
                                                        }))
                                                    }
                                                    onKeyDown={handleKeyDown}
                                                >
                                                    {CATEGORY_OPTIONS.map(opt => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <p>{CATEGORY_OPTIONS.find(opt => opt.value === item.category)?.label || item.category}</p>
                                            )}
                                        </div>


                                        <div className="inventory-item-section">
                                            <p><strong>Stock Status</strong></p>
                                            {isOpen ? (
                                                <span
                                                    style={{
                                                        color: getStockStatus(item, desiredPartsNeeded).color,
                                                        fontWeight: 600,
                                                        cursor: "help",
                                                        marginTop: '0.3rem'
                                                    }}
                                                    className="tooltip-wrapper"
                                                >
                                                    {getStockStatus(item, desiredPartsNeeded).status}
                                                    <div className="custom-tooltip">
                                                        {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                    </div>
                                                </span>
                                            ) : (
                                                <span
                                                    style={{
                                                        color: getStockStatus(item, desiredPartsNeeded).color,
                                                        fontWeight: 600,
                                                        cursor: "help"
                                                    }}
                                                    className="tooltip-wrapper"
                                                >
                                                    {getStockStatus(item, desiredPartsNeeded).status}
                                                    <div className="custom-tooltip">
                                                        {getStockStatus(item, desiredPartsNeeded).tooltip}
                                                    </div>
                                                </span>
                                            )}
                                        </div>





                                    </div>

                                    {/* --- Expanded Details --- */}
                                    {isOpen && (
                                        <>
                                            <div className="inventory-item-slip invetory-item-selected-slip" onClick={handleToggleDesktop}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                    <div className="inventory-item-section">
                                                        <p><strong>Link</strong></p>
                                                        <input
                                                            type="url"
                                                            defaultValue={item.partLink || undefined}
                                                            placeholder='E.g "www.store.com"'
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], partLink: e.target.value }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>
                                                    <div className="inventory-item-section">

                                                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                            <p><strong>Document</strong></p>
                                                            {showInfoTooltip("Normally used for custom parts indicating its SOP.")}
                                                        </div>

                                                        {item.partDocument ? (
                                                            <a
                                                                href={item.partDocument}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="clickable-inv-option"
                                                                onClick={stopClick}
                                                            >
                                                                {item.partDocument.split("/").pop()} {/* Shows the file name */}
                                                            </a>
                                                        ) : (
                                                            <span>None</span>
                                                        )}

                                                        <button
                                                            className="counted-button mobile-counted-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const fileInput = document.getElementById(`doc-input-${item.partID}`);
                                                                if (fileInput) fileInput.click();
                                                            }}
                                                        >
                                                            Upload
                                                        </button>

                                                        <input
                                                            type="file"
                                                            id={`doc-input-${item.partID}`}
                                                            style={{ display: "none" }}
                                                            accept=".pdf,.doc,.docx,.txt"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;
                                                                handleFileUpload(item.partID, file, "document");
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="inventory-item-section" style={{ marginTop: '2rem', width: 'fit-content' }}>
                                                        <p><strong>Mark as Counted</strong></p>
                                                        <button
                                                            className="counted-button mobile-counted-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setItemToCount(item);
                                                                setShowCountedPopup(true);
                                                            }}
                                                        >
                                                            Confirm
                                                        </button>
                                                    </div>

                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                                                    <div className="inventory-item-section">

                                                        <div style={{ display: "flex", gap: "0.3rem" }}>
                                                            <p><strong>Stock Target</strong></p>
                                                            {showInfoTooltip("Desired amount to be kept in stock.")}
                                                        </div>
                                                        <input
                                                            type="number"
                                                            defaultValue={item.stock_target ?? ''}  // empty string if null
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: {
                                                                        ...prev[item.partID],
                                                                        stock_target: e.target.value === '' ? null : Number(e.target.value), // handle clear as null
                                                                    },
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />

                                                    </div>


                                                    <div className="inventory-item-section">
                                                        <div>

                                                            <p style={{ margin: 0 }}>
                                                                <strong>
                                                                    {(
                                                                        editedValuesMap[item.partID]?.sold_in_packets ??
                                                                        item.sold_in_packets
                                                                    )
                                                                        ? "Amount in Pack"
                                                                        : "Sold in Packs?"
                                                                    }
                                                                </strong>
                                                            </p>

                                                            <div style={{ display: 'flex', alignItems: "center", gap: "0.5rem" }}>
                                                                {/* Checkbox */}
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        editedValuesMap[item.partID]?.sold_in_packets ??
                                                                        !!item.sold_in_packets
                                                                    }
                                                                    onClick={stopClick}
                                                                    onChange={(e) =>
                                                                        setEditedValuesMap(prev => ({
                                                                            ...prev,
                                                                            [item.partID]: {
                                                                                ...prev[item.partID],
                                                                                sold_in_packets: e.target.checked,
                                                                                amount_per_packet: e.target.checked
                                                                                    ? prev[item.partID]?.amount_per_packet ?? item.amount_per_packet ?? null
                                                                                    : null, // clear when unchecked
                                                                            },
                                                                        }))
                                                                    }
                                                                />

                                                                {/* Amount per packet (only if checked) */}
                                                                {(editedValuesMap[item.partID]?.sold_in_packets ??
                                                                    item.sold_in_packets) && (
                                                                        <input
                                                                            type="number"
                                                                            min={1}
                                                                            placeholder="How many?"
                                                                            defaultValue={
                                                                                editedValuesMap[item.partID]?.amount_per_packet ??
                                                                                item.amount_per_packet ??
                                                                                ""
                                                                            }
                                                                            onClick={stopClick}
                                                                            onChange={(e) =>
                                                                                setEditedValuesMap(prev => ({
                                                                                    ...prev,
                                                                                    [item.partID]: {
                                                                                        ...prev[item.partID],
                                                                                        amount_per_packet:
                                                                                            e.target.value === ""
                                                                                                ? null
                                                                                                : Number(e.target.value),
                                                                                    },
                                                                                }))
                                                                            }
                                                                            onKeyDown={handleKeyDown}
                                                                        />
                                                                    )}
                                                            </div>

                                                        </div>
                                                    </div>


                                                    <div className="inventory-item-section">
                                                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                            <p><strong>MPN</strong></p>
                                                            {showInfoTooltip("Manufacturer Part Number - hardware or building materials normally have this number in its store link.")}
                                                        </div>

                                                        <input
                                                            type="text"
                                                            defaultValue={item.mpn || ""}
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], mpn: e.target.value }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>

                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                                                    <div className="inventory-item-section">
                                                        <p><strong>Description</strong></p>
                                                        <textarea
                                                            defaultValue={item.description || undefined}
                                                            placeholder='E.g "Screw used to mount Dome to Pole"'
                                                            onClick={stopClick}
                                                            rows={4}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], description: e.target.value }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>

                                                    <div className="inventory-item-section">

                                                        <p><strong>Manufacturer</strong></p>
                                                        <input
                                                            type="text"
                                                            defaultValue={item.manufacturer || ""}
                                                            onClick={stopClick}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], manufacturer: e.target.value }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>
                                                </div>



                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                    <div className="inventory-item-section">
                                                        <p><strong>Location</strong></p>
                                                        <textarea
                                                            defaultValue={item.location || undefined}
                                                            placeholder='E.g "Stored in room X, first drawer"'
                                                            onClick={stopClick}
                                                            rows={4}
                                                            onChange={(e) =>
                                                                setEditedValuesMap(prev => ({
                                                                    ...prev,
                                                                    [item.partID]: { ...prev[item.partID], location: e.target.value }
                                                                }))
                                                            }
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </div>

                                                    <div className="inventory-item-section">
                                                        <p><strong>Picture of Location</strong></p>

                                                        <div
                                                            className="location-picture-container"
                                                            style={{ backgroundColor: item.location_picture ? "transparent" : "#e2dedeff" }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const fileInput = document.getElementById(`location-input-${item.partID}`);
                                                                if (fileInput) fileInput.click();
                                                            }}
                                                        >
                                                            {item.location_picture ? (
                                                                <>
                                                                    <img
                                                                        src={item.location_picture}
                                                                        alt="Location"
                                                                        className="location-picture"
                                                                    />
                                                                    {/* Large preview tooltip */}
                                                                    <div className="location-picture-tooltip">
                                                                        <img src={item.location_picture} alt="Preview" />
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <AiFillPicture className="location-placeholder-icon" />
                                                            )}

                                                            <div className="location-picture-overlay">
                                                                <span>Click to Upload</span>
                                                            </div>
                                                        </div>

                                                        <input
                                                            type="file"
                                                            id={`location-input-${item.partID}`}
                                                            style={{ display: "none" }}
                                                            accept="image/*"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;
                                                                handleFileUpload(item.partID, file, "location");
                                                            }}
                                                        />
                                                    </div>


                                                </div>

                                            </div>

                                            <div className="inventory-item-open-slip-buttons">
                                                <button onClick={handleSave}>Save</button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                                                        setItemToDeleteId(item.partID);
                                                        setShowDeleteItemPopup(true);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedSlips(prev => ({ ...prev, [item.partID]: false }));
                                                        setEditingPartId(null);
                                                        setEditedValues({});
                                                    }}
                                                >
                                                    Close
                                                </button>

                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                </div>

                <div className='inv-stats in-pop'>

                    <div className="stat-inv-card">
                        <h2>Buildable Detectors</h2>

                        {/* Detector Version Selector */}
                        <select
                            value={selectedDetectorId ?? ''}
                            onChange={e => setSelectedDetectorId(Number(e.target.value))}
                            style={{ marginBottom: '1rem', padding: '0.5rem', fontSize: '1rem' }}
                            className='selecting-detec'
                        >
                            <option value="" disabled>Select Detector Version</option>
                            {detectors.map(detector => (
                                <option key={detector.id} value={detector.id}>
                                    {detector.name} v{detector.version}
                                </option>
                            ))}
                        </select>

                        <h1>{maxBuildableDetectors}</h1>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button
                                onClick={() => {
                                    setActiveSection('PlanningSection');
                                    setShowSectionPopup(true);
                                }}
                                className='place-request-button btn-for-prod'
                            >
                                <FaTasks /> Plan a Production Batch
                            </button>

                            <button
                                onClick={() => {
                                    setActiveSection('PartsForXMore');
                                    setShowSectionPopup(true);
                                }}
                                className='place-request-button btn-for-prod'
                            >
                                <BsBoxSeamFill /> What is Missing for More?
                            </button>

                            {/* <button
                                onClick={() => {
                                    setActiveSection('PartsForXMore');
                                    setShowSectionPopup(true);
                                }}
                                className='place-request-button btn-for-prod'
                            >
                                <IoFileTrayStackedSharp /> Desired Detector Stock Count
                            </button> */}

                        </div>
                    </div>

                    <div className="stat-inv-card"
                        style={{ cursor: !isEditingStock ? "pointer" : "default" }}
                        onClick={() => {
                            if (!isEditingStock) {
                                setNewStockAmount(desiredDetectors ?? 0); // set default value
                                setIsEditingStock(true);
                            }
                        }}
                    >

                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                            <h2>Detectors to Keep in Stock</h2>
                            {showInfoTooltip("This number sets the unit color scheme to show when detector stock falls below your desired level.", "stock-tooltip")}
                        </div>

                        {!isEditingStock ? (
                            <h1 style={{ marginTop: "1rem" }}>{desiredDetectors ?? 0}</h1>
                        ) : (
                            <div
                                style={{
                                    marginTop: "1rem",
                                    display: "flex",
                                    gap: "0.5rem",
                                    alignItems: "center",
                                }}
                            >
                                <input
                                    type="number"
                                    value={newStockAmount}
                                    onChange={(e) => setNewStockAmount(Number(e.target.value))}
                                    style={{ padding: "0.25rem", width: "5rem" }}
                                />
                                <button onClick={handleStockNumber}>Save</button>
                                <button onClick={() => setIsEditingStock(false)}>Cancel</button>
                            </div>
                        )}
                    </div>

                    <div
                        className={`inventory-card standard-inv-card`}
                        style={{ width: '100%' }}
                        onClick={() => {
                            setActiveSection('Analytics');
                            setShowSectionPopup(true);
                        }}
                    >
                        <div className={`card-default `}>
                            <h1><IoBarChart /> Analytics</h1>
                        </div>

                    </div>

                    <div className="stat-inv-card graph-placeholder">
                        <h2>
                            Items per Sub-Assembly
                            {selectedDetectorId !== null && (() => {
                                const selectedDetector = detectors.find(d => d.id === selectedDetectorId);
                                return selectedDetector
                                    ? ` for ${selectedDetector.name} v${selectedDetector.version}`
                                    : '';
                            })()}
                        </h2>

                        <Chart
                            options={{
                                labels: subAssemblyLabels,
                                colors: [
                                    '#1e6296ff', // dark blue-gray
                                    '#16517B', // navy blue
                                    '#28AEE1', // sky blue
                                    '#0e2943ff', // deep blue
                                    '#3A7CA5', // medium blue
                                    '#6096C5', // soft blue
                                    '#9EC9E2'  // light blue
                                ],
                                legend: {
                                    position: 'bottom',
                                },
                                tooltip: {
                                    y: {
                                        formatter: val => `${val} item(s)`
                                    }
                                },
                                plotOptions: {
                                    pie: {
                                        donut: {
                                            size: '50%'
                                        }
                                    }
                                }
                            }}
                            series={subAssemblySeries}
                            type="donut"
                            width="100%"
                            height={300}
                        />
                    </div>

                    <div className="stat-inv-card graph-placeholder">
                        <h2>Parts per Detector Version</h2>
                        <Chart
                            options={stackedBarOptions}
                            series={stackedBarSeries}
                            type="bar"
                            height={350}
                        />

                    </div>

                    <div className="stat-inv-card graph-placeholder">
                        <h2>Items per Category</h2>
                        <Chart
                            options={{
                                labels: categoryLabels,
                                colors: [
                                    '#1E6296', // Accessories & Miscellaneous - dark blue-gray
                                    '#16517B', // Wiring & Connectors - navy blue
                                    '#28AEE1', // Electronics - sky blue
                                    '#0E2943', // Fasteners & Mounting - deep blue
                                    '#3A7CA5', // Branding & Labeling - medium blue
                                    '#6096C5', // Power Components - soft blue
                                    '#9EC9E2', // Structural - light blue
                                    '#5CC1A6', // Protection - teal blue-green
                                    '#A5D8FF', // Marketing - pale cyan
                                    '#1B4965', // Tools - dark steel blue
                                    '#41729F', // Outsourced Component - cool medium slate blue
                                ],
                                legend: {
                                    position: 'bottom',
                                },
                                tooltip: {
                                    y: {
                                        formatter: val => `${val} item(s)`
                                    }
                                },
                                plotOptions: {
                                    pie: {
                                        donut: {
                                            size: '50%'
                                        }
                                    }
                                }
                            }}
                            series={categorySeries}
                            type="donut"
                            width="100%"
                            height={350}
                        />
                    </div>
                </div>
            </div>

            {/* Stats card in mobile view */}
            {activeSection === "StatsCards" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>Inventory Statistics</h2>

                        <div className='inv-stats'>

                            <div
                                className={`inventory-card standard-inv-card inv-card-for-mobile`}
                                style={{ width: '100%' }}
                                onClick={() => {
                                    setActiveSection('Analytics');
                                    setShowSectionPopup(true);
                                }}
                            >
                                <div className={`card-default `}>
                                    <h1><IoBarChart /> Analytics</h1>
                                </div>

                            </div>

                            <div className="stat-inv-card">
                                <h2>Buildable Detectors</h2>

                                {/* Detector Version Selector */}
                                <select
                                    value={selectedDetectorId ?? ''}
                                    onChange={e => setSelectedDetectorId(Number(e.target.value))}
                                    style={{ marginBottom: '1rem', padding: '0.5rem', fontSize: '1rem' }}
                                    className='selecting-detec'
                                >
                                    <option value="" disabled>Select Detector Version</option>
                                    {detectors.map(detector => (
                                        <option key={detector.id} value={detector.id}>
                                            {detector.name} v{detector.version}
                                        </option>
                                    ))}
                                </select>

                                <h1>{maxBuildableDetectors}</h1>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <button
                                        onClick={() => {
                                            setActiveSection('PlanningSection');
                                            setShowSectionPopup(true);
                                        }}
                                        className='place-request-button btn-for-prod'
                                    >
                                        <FaTasks /> Plan a Production Batch
                                    </button>

                                    <button
                                        onClick={() => {
                                            setActiveSection('PartsForXMore');
                                            setShowSectionPopup(true);
                                        }}
                                        className='place-request-button btn-for-prod'
                                    >
                                        <BsBoxSeamFill /> What is Missing for More?
                                    </button>
                                </div>
                            </div>

                            <div className="stat-inv-card"
                                style={{ cursor: !isEditingStock ? "pointer" : "default" }}
                                onClick={() => {
                                    if (!isEditingStock) {
                                        setNewStockAmount(desiredDetectors ?? 0); // set default value
                                        setIsEditingStock(true);
                                    }
                                }}
                            >
                                <h2>Detectors to Keep in Stock</h2>

                                {!isEditingStock ? (
                                    <h1 style={{ marginTop: "1rem" }}>{desiredDetectors ?? 0}</h1>
                                ) : (
                                    <div
                                        style={{
                                            marginTop: "1rem",
                                            display: "flex",
                                            gap: "0.5rem",
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            type="number"
                                            value={newStockAmount}
                                            onChange={(e) => setNewStockAmount(Number(e.target.value))}
                                            style={{ padding: "0.25rem", width: "5rem" }}
                                        />
                                        <button onClick={handleStockNumber}>Save</button>
                                        <button onClick={() => setIsEditingStock(false)}>Cancel</button>
                                    </div>
                                )}
                            </div>

                            <div className="stat-inv-card graph-placeholder">
                                <h2>
                                    Items per Sub-Assembly
                                    {selectedDetectorId !== null && (() => {
                                        const selectedDetector = detectors.find(d => d.id === selectedDetectorId);
                                        return selectedDetector
                                            ? ` for v${selectedDetector.version}`
                                            : '';
                                    })()}
                                </h2>

                                <Chart
                                    options={{
                                        labels: subAssemblyLabels,
                                        colors: [
                                            '#1e6296ff', // dark blue-gray
                                            '#16517B', // navy blue
                                            '#28AEE1', // sky blue
                                            '#0e2943ff', // deep blue
                                            '#3A7CA5', // medium blue
                                            '#6096C5', // soft blue
                                            '#9EC9E2'  // light blue
                                        ],
                                        legend: {
                                            position: 'bottom',
                                        },
                                        tooltip: {
                                            y: {
                                                formatter: val => `${val} item(s)`
                                            }
                                        },
                                        plotOptions: {
                                            pie: {
                                                donut: {
                                                    size: '50%'
                                                }
                                            }
                                        }
                                    }}
                                    series={subAssemblySeries}
                                    type="donut"
                                    width="100%"
                                    height={300}
                                />
                            </div>

                            <div className="stat-inv-card graph-placeholder">
                                <h2>Parts per Detector Version</h2>
                                <Chart
                                    options={stackedBarOptions}
                                    series={stackedBarSeries}
                                    type="bar"
                                    height={350}
                                />

                            </div>

                            <div className="stat-inv-card graph-placeholder">
                                <h2>Items per Category</h2>
                                <Chart
                                    options={{
                                        labels: categoryLabels,
                                        colors: [
                                            '#1e6296ff', // dark blue-gray
                                            '#16517B', // navy blue
                                            '#28AEE1', // sky blue
                                            '#0e2943ff', // deep blue
                                            '#3A7CA5', // medium blue
                                            '#6096C5', // soft blue
                                            '#9EC9E2'  // light blue
                                        ],
                                        legend: {
                                            position: 'bottom',
                                        },
                                        tooltip: {
                                            y: {
                                                formatter: val => `${val} item(s)`
                                            }
                                        },
                                        plotOptions: {
                                            pie: {
                                                donut: {
                                                    size: '50%'
                                                }
                                            }
                                        }
                                    }}
                                    series={categorySeries}
                                    type="donut"
                                    width="100%"
                                    height={350}
                                />
                            </div>
                        </div>


                    </div>
                </div>
            )}

            {/* Sub-Assembly */}
            {activeSection === "AddConsumeSubAssembly" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>Build Sub-Assembly</h2>

                        <div className='subassembly-manage-stack'>
                            {subAssemblyRequests.map((req, index) => (
                                <div className="subassembly-manage-table" key={req.id}>
                                    <label>
                                        Sub-Assembly:
                                        <select
                                            value={req.selectedSubAssemblyId}
                                            onChange={(e) => handleRequestChange(index, 'selectedSubAssemblyId', e.target.value)}

                                        >
                                            <option value="">-- Select Sub-Assembly --</option>
                                            {subAssemblies.map(sa => (
                                                <option key={sa.id} value={sa.id}>{sa.name}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label>
                                        Operation:
                                        <select
                                            value={req.operation}
                                            onChange={(e) =>
                                                handleRequestChange(index, "operation", e.target.value as SubAssemblyOperation)
                                            }
                                        >
                                            <option value="BUILD_NEW">Build New Sub-Assembly</option>
                                            <option value="REGISTER_EXISTING">Register Existing Sub-Assembly</option>
                                            <option value="ADD_COMPONENTS">Add Components to Inventory</option>
                                        </select>
                                    </label>


                                    <label>
                                        Quantity:
                                        <input
                                            type="number"
                                            min="1"
                                            value={req.operationCount}
                                            onChange={(e) => handleRequestChange(index, 'operationCount', Number(e.target.value))}
                                        />
                                    </label>

                                    {subAssemblyRequests.length > 1 && (
                                        <button onClick={() => removeRequest(index)}>Remove</button>
                                    )}
                                </div>
                            ))}



                            <button className='add-request-card' onClick={addNewRequest}>+ New Request</button>
                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <button
                                className="confirm-requests-button"
                                onClick={() => {
                                    // Check if any request has no selected sub-assembly
                                    const invalidRequest = subAssemblyRequests.some(r => !r.selectedSubAssemblyId);
                                    if (invalidRequest) {
                                        toast.error("Please select a valid sub-assembly for all requests.", toastStyle);
                                        return;
                                    }

                                    // Split requests by operation
                                    const buildNewSubs = subAssemblyRequests.filter(r => r.operation === "BUILD_NEW");
                                    const registerSubs = subAssemblyRequests.filter(r => r.operation === "REGISTER_EXISTING");
                                    const addSubs = subAssemblyRequests.filter(r => r.operation === "ADD_COMPONENTS");

                                    if (buildNewSubs.length === 0 && registerSubs.length === 0 && addSubs.length === 0) {
                                        toast.error("Please select at least one valid sub-assembly request.", toastStyle);
                                        return;
                                    }

                                    setBuildSummary({
                                        buildNew: buildNewSubs.map(r => ({
                                            sub: subAssemblies.find(s => s.id.toString() === r.selectedSubAssemblyId)?.name || `Sub ${r.selectedSubAssemblyId}`,
                                            qty: r.operationCount
                                        })),
                                        register: registerSubs.map(r => ({
                                            sub: subAssemblies.find(s => s.id.toString() === r.selectedSubAssemblyId)?.name || `Sub ${r.selectedSubAssemblyId}`,
                                            qty: r.operationCount
                                        })),
                                        addComponents: addSubs.map(r => ({
                                            sub: subAssemblies.find(s => s.id.toString() === r.selectedSubAssemblyId)?.name || `Sub ${r.selectedSubAssemblyId}`,
                                            qty: r.operationCount
                                        }))
                                    });
                                    setShowBuildConfirmPopup(true);
                                }}
                            >
                                Confirm
                            </button>

                        </div>

                    </div>
                </div>
            )}

            {activeSection === "ManageSubAssembly" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">

                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>Manage Sub-Assembly</h2>

                        <div className="inventory-table-wrapper" style={{ paddingLeft: "2rem" }}>

                            <div className='inventory-table-scroll-container'>
                                <table className="inventory-table">
                                    <thead className=''>
                                        <tr>
                                            <th>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Sub-Assembly
                                                    <IoAddCircleOutline className='add-part-icon' onClick={() => setShowAddSubAssemblyPopup(true)} />
                                                </div>
                                            </th>

                                            <th><div className='column-row-div'>Bill of Materials</div></th>
                                        </tr>
                                    </thead>


                                    <tbody>
                                        {subAssemblies.map((sub, index) => (
                                            <tr key={sub.id}>
                                                <td
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setEditingSubAssemblyId(sub.id);
                                                        setNewSubAssemblyName(sub.name);
                                                        setNewSubAssemblyPic(sub.subPicture);
                                                        setNewSubAssemblySop(sub.subSOP);
                                                        setNewSubAssemblyParts(
                                                            sub.parts.map((p) => ({
                                                                part: p.part,
                                                                quantity_required: p.quantity_required,
                                                            }))
                                                        );

                                                        setShowAddSubAssemblyPopup(true);
                                                    }}
                                                >

                                                    <div className="part-info-cell">
                                                        <img
                                                            src={sub.subPicture ? sub.subPicture : fnnBlueLogo}
                                                            alt={sub.name}
                                                            className="part-image"
                                                        // style={{ cursor: isEditing ? 'pointer' : 'default' }}
                                                        />

                                                        {sub.subSOP ? (
                                                            <a
                                                                href={sub.subSOP}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="clickable-inv-option"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {sub.name}
                                                            </a>
                                                        ) : (
                                                            <span className="unclickable-inv-option">{truncateText(sub.name, 40)}</span>
                                                        )}
                                                    </div>


                                                </td>

                                                <td
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setEditingSubAssemblyId(sub.id);
                                                        setNewSubAssemblyName(sub.name);
                                                        setNewSubAssemblyPic(sub.subPicture);
                                                        setNewSubAssemblySop(sub.subSOP);
                                                        setNewSubAssemblyParts(
                                                            sub.parts.map((p) => ({
                                                                part: p.part,
                                                                quantity_required: p.quantity_required,
                                                            }))
                                                        );

                                                        setShowAddSubAssemblyPopup(true);
                                                    }}
                                                >
                                                    <div className='column-row-div'>
                                                        <div
                                                            className='place-request-button'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setShowBOMPopup(true);
                                                                setSelectedSubAssembly(sub);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            View
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>


                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detector */}
            {activeSection === "AddConsumeDetector" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>Add | Consume Detector</h2>

                        <div className='subassembly-manage-stack'>
                            {detectorRequests.map((req, index) => (
                                <div className="subassembly-manage-table" key={req.id}>
                                    <label>
                                        Detector:
                                        <select
                                            value={req.selectedDetectorId}
                                            onChange={(e) => handleDetectorRequestChange(index, 'selectedDetectorId', e.target.value)}
                                        >
                                            <option value="">-- Select Detector --</option>
                                            {detectors.map(det => (
                                                <option key={det.id} value={det.id}>{det.name} v{det.version}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label>
                                        Operation:
                                        <select
                                            value={req.operation}
                                            onChange={(e) => handleDetectorRequestChange(index, 'operation', e.target.value)}
                                        >
                                            <option value="consume">Consume</option>
                                            <option value="add">Add</option>
                                        </select>
                                    </label>

                                    <label>
                                        Quantity:
                                        <input
                                            type="number"
                                            min="1"
                                            value={req.operationCount}
                                            onChange={(e) => handleDetectorRequestChange(index, 'operationCount', Number(e.target.value))}
                                        />
                                    </label>

                                    {detectorRequests.length > 1 && (
                                        <button onClick={() => removeDetectorRequest(index)}>Remove</button>
                                    )}
                                </div>
                            ))}

                            <button className='add-request-card' onClick={addNewDetectorRequest}>+ New Request</button>
                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <button className='confirm-requests-button' onClick={handleDetectorInventoryAction}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeSection === "ManageDetector" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">

                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>Manage Detectors</h2>

                        <div className="inventory-table-wrapper" style={{ paddingLeft: "2rem" }}>

                            <div className='inventory-table-scroll-container'>
                                <table className="inventory-table">
                                    <thead className=''>
                                        <tr>
                                            <th>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Detector
                                                    <IoAddCircleOutline className='add-part-icon' onClick={() => setShowAddDetectorPopup(true)} />
                                                </div>
                                            </th>
                                            <th><div className='column-row-div'>Bill of Materials</div></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detectors.map((det) => (
                                            <tr key={det.id}>
                                                <td
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setEditingDetectorId(det.id);
                                                        setNewDetectorName(det.name);
                                                        setNewDetectorVersion(det.version);
                                                        setNewDetectorSubAssemblies(
                                                            det.subassemblies.map((s: any) => ({
                                                                subassembly: typeof s.subassembly === 'number' ? s.subassembly : s.subassembly.id,
                                                                quantity_required: s.quantity_required,
                                                            }))
                                                        );

                                                        setShowAddDetectorPopup(true);
                                                    }}
                                                >
                                                    {det.name} - v{det.version}
                                                </td>

                                                <td
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setEditingDetectorId(det.id);
                                                        setNewDetectorName(det.name);
                                                        setNewDetectorVersion(det.version);
                                                        setNewDetectorSubAssemblies(
                                                            det.subassemblies.map((s: any) => ({
                                                                subassembly: typeof s.subassembly === 'number' ? s.subassembly : s.subassembly.id,
                                                                quantity_required: s.quantity_required,
                                                            }))
                                                        );

                                                        setShowAddDetectorPopup(true);
                                                    }}>
                                                    <div className='column-row-div'>
                                                        <div
                                                            className='place-request-button'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setShowDetBOMPopup(true);
                                                                setSelectedDetector2(det);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            View
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assemble Detector */}
            {activeSection === "AssembleDetector" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content" style={{ overflowY: 'unset' }}>
                        <button
                            className="close-popup-button"
                            onClick={() => (setShowSectionPopup(false), setAssembleDetectorId(null), setAssembleSelectedSubAssemblies({}), setAssembleMacAddress(""), setAssembleDetCode(""))}
                        >
                            x
                        </button>

                        <h2>Assemble Detectors</h2>

                        <div className="inventory-assemble-dets">

                            {/* MAC Address Field */}
                            <label>
                                <span>MAC Address</span>
                                <input
                                    type="text"
                                    placeholder="e.g. 0xfb3ee462"
                                    value={assembleMacAddress}
                                    onChange={(e) => setAssembleMacAddress(e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Detector Code</span>
                                <input
                                    type="text"
                                    placeholder="e.g. GOE (Leave blank if still unknown)"
                                    value={assembleDetCode}
                                    onChange={(e) => setAssembleDetCode(e.target.value)}
                                />
                            </label>

                            {/* Detector Version Selector */}
                            <label>
                                <span>Select Detector Version</span>
                                <select
                                    value={assembleDetectorId || ""}
                                    onChange={(e) => {
                                        const detId = Number(e.target.value);
                                        setAssembleDetectorId(detId);
                                        setAssembleSelectedSubAssemblies({});
                                    }}
                                >
                                    <option value="" disabled>-- Select Detector --</option>
                                    {detectors.map(det => (
                                        <option key={det.id} value={det.id}>
                                            {det.name} v{det.version}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {/* Dynamic Sub-Assembly Selectors */}

                            <div className='subassembly-selector-wrapper'>
                                {assembleDetectorId &&
                                    detectors.find(d => d.id === assembleDetectorId)?.subassemblies.map(ds => {
                                        const subObj =
                                            typeof ds.subassembly === "number"
                                                ? subAssemblies.find(s => s.id === ds.subassembly)
                                                : ds.subassembly;

                                        if (!subObj) return null;

                                        const availableOptions = builtSubAssemblies.filter(
                                            bsa => bsa.subassembly === subObj.id && bsa.status === "available"
                                        );

                                        const selectedId = assembleSelectedSubAssemblies[subObj.id];

                                        return (
                                            <div key={subObj.id} className="subassembly-selector">
                                                <strong>{subObj.name}</strong>

                                                <div className="subassembly-options-box">
                                                    {availableOptions.length === 0 ? (
                                                        <p className="no-options">No available {subObj.name}s</p>
                                                    ) : (
                                                        availableOptions.map(opt => (
                                                            <div
                                                                key={opt.id}
                                                                className={`subassembly-option ${selectedId === String(opt.id) ? "selected" : ""
                                                                    }`}
                                                                onClick={() =>
                                                                    setAssembleSelectedSubAssemblies(prev => ({
                                                                        ...prev,
                                                                        [subObj.id]: String(opt.id)
                                                                    }))
                                                                }
                                                            >
                                                                <span>{opt.id}</span>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>

                            {/* Assemble Button */}
                            <div className="assemble-footer">
                                <button
                                    disabled={
                                        !assembleDetectorId ||
                                        !assembleMacAddress.trim() ||
                                        detectors.find(d => d.id === assembleDetectorId)?.subassemblies.some(
                                            ds => !assembleSelectedSubAssemblies[
                                                typeof ds.subassembly === "number"
                                                    ? ds.subassembly
                                                    : ds.subassembly.id
                                            ]
                                        )
                                    }
                                    onClick={() =>
                                        assembleDetector(assembleDetectorId, assembleSelectedSubAssemblies, assembleMacAddress, assembleDetCode)
                                    }
                                >
                                    Assemble Detector
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Built Sub-Assemblies & Detectors */}
            {activeSection === "ViewBuiltUnits" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowSectionPopup(false);
                                setOpenSlips({}); // close all slips
                            }}
                        >
                            x
                        </button>

                        <h2>Built Units</h2>

                        <div className="inventory-table-wrapper wrapper-for-orders">
                            {/* --- Status Filters --- */}
                            <div className="inv-stat-filters filter-for-order">
                                {["available", "installed", "faulty", "discarded"].map((status) => (
                                    <button
                                        key={status}
                                        className={`inv-stats-opener ${selectedUnitStatus === status ? "active" : ""}`}
                                        onClick={() => setSelectedUnitStatus(status)}
                                    >
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </button>
                                ))}
                            </div>

                            <div className="slips-container wrapper-for-built">
                                {/* AVAILABLE */}
                                {selectedUnitStatus === "available" && (() => {
                                    const availableSubs = builtSubAssemblies.filter(bsa => bsa.status === "available");
                                    if (!availableSubs.length)
                                        return <p className="no-items-msg">No available sub-assemblies found.</p>;

                                    return subAssemblies
                                        .filter(sa => availableSubs.some(bsa => bsa.subassembly === sa.id))
                                        .map(sa => {
                                            const subRows = availableSubs.filter(bsa => bsa.subassembly === sa.id);
                                            return (
                                                <div key={sa.id} className={`slip ${openSlips[String(sa.id)] ? 'open' : ''}`}>
                                                    <div className="slip-header" onClick={() => toggleSlip(sa.id)}>
                                                        {sa.name} ({subRows.length})
                                                    </div>
                                                    {openSlips[String(sa.id)] && (
                                                        <div className="slip-content">

                                                            {!isMobile4 && (
                                                                <table className="inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>ID</th>
                                                                            <th>Built On</th>
                                                                            <th>Built By</th>
                                                                            <th>Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {subRows.map(bsa => (
                                                                            <tr key={bsa.id}>
                                                                                <td>{bsa.id}</td>
                                                                                <td>{new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</td>
                                                                                <td>{getFnnUserName(bsa.built_by)}</td>
                                                                                <td>
                                                                                    <select
                                                                                        className="form-select select-built"
                                                                                        value={bsa.status}
                                                                                        onChange={(e) => {
                                                                                            const newStatus = e.target.value;
                                                                                            if (newStatus === "installed") {
                                                                                                handleUnitStatusChange(bsa.id, newStatus);
                                                                                            } else {
                                                                                                setSubAssemblyToConfirmId(bsa.id);
                                                                                                setStatusToConfirm(newStatus);
                                                                                                setShowStatusConfirmPopup(true);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {["available", "installed", "faulty", "discarded"].map(statusOption => (
                                                                                            <option key={statusOption} value={statusOption}>
                                                                                                {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}

                                                            {isMobile4 && (
                                                                <div className="warehouse-mobile-list">
                                                                    {subRows.map((bsa) => (
                                                                        <div key={bsa.id} className="warehouse-card">
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">ID:</span>
                                                                                <span className="warehouse-value">{bsa.id}</span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Built On:</span>
                                                                                <span className="warehouse-value">
                                                                                    {new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                                                </span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Built By:</span>
                                                                                <span className="warehouse-value">{getFnnUserName(bsa.built_by)}</span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Status:</span>
                                                                                <select
                                                                                    className="form-select select-built warehouse-select"
                                                                                    value={bsa.status}
                                                                                    onChange={(e) => {
                                                                                        const newStatus = e.target.value;
                                                                                        if (newStatus === "installed") {
                                                                                            handleUnitStatusChange(bsa.id, newStatus);
                                                                                        } else {
                                                                                            setSubAssemblyToConfirmId(bsa.id);
                                                                                            setStatusToConfirm(newStatus);
                                                                                            setShowStatusConfirmPopup(true);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {["available", "installed", "faulty", "discarded"].map((statusOption) => (
                                                                                        <option key={statusOption} value={statusOption}>
                                                                                            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}


                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                })()}

                                {/* USED */}
                                {selectedUnitStatus === "installed" && (() => {
                                    const usedSubs = builtSubAssemblies.filter(bsa => bsa.status === "installed");
                                    if (!usedSubs.length)
                                        return <p className="no-items-msg">No installed sub-assemblies found.</p>;

                                    // Define sort priority
                                    const statusOrder: Record<"in-stock" | "deployed" | "testing" | "maintenance", number> = {
                                        "in-stock": 1,
                                        deployed: 2,
                                        testing: 3,
                                        maintenance: 4
                                    };

                                    return builtDetectors
                                        .filter(det => usedSubs.some(bsa => bsa.used_by === det.mac_address))
                                        .sort((a, b) => {
                                            const aKey = a.status.toLowerCase() as keyof typeof statusOrder;
                                            const bKey = b.status.toLowerCase() as keyof typeof statusOrder;
                                            const aOrder = statusOrder[aKey] ?? 999;
                                            const bOrder = statusOrder[bKey] ?? 999;
                                            return aOrder - bOrder;
                                        })
                                        .map(det => {
                                            const subRows = usedSubs.filter(bsa => bsa.used_by === det.mac_address);

                                            const detectorInfo = detectors.find(d => d.id === det.detector);
                                            const detectorName = detectorInfo
                                                ? `${detectorInfo.name} v${detectorInfo.version}`
                                                : `Detector ID ${det.detector}`;

                                            return (
                                                <div key={det.mac_address}
                                                    ref={(el) => (slipRefs.current[det.mac_address] = el)} // store ref
                                                    className={`slip ${openSlips[det.mac_address] ? 'open' : ''}`}>
                                                    <div className="slip-header-detector" onClick={() => toggleSlip(det.mac_address)}>
                                                        <span>{det.code ? `${det.code} - ` : ""}{det.mac_address}</span> {/* to add sub amount do – {subRows.length} subassemblies */}
                                                        <span className={`detector-status ${det.status.toLowerCase()}`}>
                                                            {det.status}
                                                        </span>
                                                    </div>

                                                    {openSlips[det.mac_address] && (
                                                        <div className="slip-content">
                                                            {/* Detector build info header */}
                                                            <div className="detector-build-info">
                                                                <div className="detector-build-info-item">
                                                                    <strong>Assembled On:</strong> {new Date(det.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                                </div>
                                                                <div className="detector-build-info-item">
                                                                    <strong>Assembled By:</strong> {getFnnUserName(det.assembled_by)}
                                                                </div>
                                                                <div className="detector-build-info-item">
                                                                    <strong>Detector:</strong>
                                                                    <span
                                                                        className='warehouse-clickable-sub'
                                                                        onClick={() => {
                                                                            setTimeout(() => {
                                                                                setSelectedDetector2(detectorInfo ? detectorInfo : null);
                                                                                setShowDetBOMPopup(true);
                                                                            }, 50);
                                                                        }}>
                                                                        {detectorName}
                                                                    </span>
                                                                </div>

                                                                <div className="detector-build-info-item">
                                                                    <strong>Code:</strong>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={editCodes[det.mac_address] ?? det.code ?? ""}
                                                                        onChange={(e) =>
                                                                            setEditCodes((prev) => ({ ...prev, [det.mac_address]: e.target.value }))
                                                                        }
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter") {
                                                                                handleCodeUpdate(det);
                                                                            }
                                                                        }}
                                                                        placeholder="Enter code"
                                                                    />

                                                                </div>

                                                                <div className="detector-build-info-item">
                                                                    <strong>Status:</strong>
                                                                    <select
                                                                        className="form-select select-built"
                                                                        value={det.status}
                                                                        style={{ textTransform: "capitalize" }}
                                                                        onChange={(e) => handleDetectorStatusChange(det.mac_address, e.target.value)}
                                                                    >
                                                                        {["in-stock", "deployed", "testing", "maintenance"].map(statusOption => (
                                                                            <option key={statusOption} value={statusOption}>
                                                                                {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                            </div>

                                                            {!isMobile4 && (
                                                                <table className="inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>ID</th>
                                                                            <th>Sub-Assembly</th>
                                                                            <th>Built On</th>
                                                                            <th>Built By</th>
                                                                            <th>Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {subRows.map(bsa => (
                                                                            <tr key={bsa.id}>
                                                                                <td>{bsa.id}</td>
                                                                                <td
                                                                                    className='warehouse-clickable-sub'
                                                                                    onClick={() => {
                                                                                        setTimeout(() => {
                                                                                            const selected = subAssemblies.find(s => s.id === bsa.subassembly);
                                                                                            setSelectedSubAssembly(selected || null);
                                                                                            setShowBOMPopup(true);
                                                                                        }, 50);
                                                                                    }}>{subAssemblies.find(s => s.id === bsa.subassembly)?.name || bsa.subassembly}</td>
                                                                                <td>{new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</td>
                                                                                <td>{getFnnUserName(bsa.built_by)}</td>
                                                                                <td>
                                                                                    <select
                                                                                        className="form-select select-built"
                                                                                        value={bsa.status}
                                                                                        onChange={(e) => {
                                                                                            const newStatus = e.target.value;
                                                                                            if (newStatus === "installed") {
                                                                                                handleUnitStatusChange(bsa.id, newStatus);
                                                                                            } else {
                                                                                                setSubAssemblyToConfirmId(bsa.id);
                                                                                                setStatusToConfirm(newStatus);
                                                                                                setShowStatusConfirmPopup(true);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {["available", "installed", "faulty", "discarded"].map(statusOption => (
                                                                                            <option key={statusOption} value={statusOption}>
                                                                                                {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}

                                                            {isMobile4 && (
                                                                <div className="warehouse-mobile-list">
                                                                    {subRows.map((bsa) => (
                                                                        <div key={bsa.id} className="warehouse-card">
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">ID:</span>
                                                                                <span className="warehouse-value">{bsa.id}</span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Sub-Assembly:</span>
                                                                                <span className="warehouse-value warehouse-clickable-sub"
                                                                                    onClick={() => {
                                                                                        // ✅ Close the all slips
                                                                                        setOpenSlips({});

                                                                                        // ✅ Close current popup and open BOM shortly after
                                                                                        setShowSectionPopup(false);
                                                                                        setTimeout(() => {
                                                                                            const selected = subAssemblies.find(s => s.id === bsa.subassembly);
                                                                                            setSelectedSubAssembly(selected || null);
                                                                                            setFlowOrTableSub("flow");
                                                                                            setActiveSection('SubAssemblyBOM');
                                                                                            setShowSectionPopup(true);
                                                                                        }, 50);
                                                                                    }}

                                                                                >
                                                                                    {subAssemblies.find(s => s.id === bsa.subassembly)?.name || bsa.subassembly}</span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Built On:</span>
                                                                                <span className="warehouse-value">
                                                                                    {new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                                                </span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Built By:</span>
                                                                                <span className="warehouse-value">{getFnnUserName(bsa.built_by)}</span>
                                                                            </div>
                                                                            <div className="warehouse-row">
                                                                                <span className="warehouse-label">Status:</span>
                                                                                <select
                                                                                    className="form-select select-built warehouse-select"
                                                                                    value={bsa.status}
                                                                                    onChange={(e) => {
                                                                                        const newStatus = e.target.value;
                                                                                        if (newStatus === "installed") {
                                                                                            handleUnitStatusChange(bsa.id, newStatus);
                                                                                        } else {
                                                                                            setSubAssemblyToConfirmId(bsa.id);
                                                                                            setStatusToConfirm(newStatus);
                                                                                            setShowStatusConfirmPopup(true);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {["available", "installed", "faulty", "discarded"].map((statusOption) => (
                                                                                        <option key={statusOption} value={statusOption}>
                                                                                            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                })()}


                                {/* FAULTY / DISCARDED */}
                                {["faulty", "discarded"].includes(selectedUnitStatus || "") && (() => {
                                    const rows = builtSubAssemblies.filter(bsa => bsa.status === selectedUnitStatus);
                                    if (!rows.length)
                                        return <p className="no-items-msg">No {selectedUnitStatus} sub-assemblies found.</p>;

                                    return (
                                        <div>
                                            <div className="slip-content">
                                                {!isMobile4 && (
                                                    <table className="inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                        <thead>
                                                            <tr>
                                                                <th>ID</th>
                                                                <th>Sub-Assembly</th>
                                                                <th>Built On</th>
                                                                <th>Built By</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rows.map(bsa => (
                                                                <tr key={bsa.id}>
                                                                    <td>{bsa.id}</td>
                                                                    <td
                                                                        className='warehouse-clickable-sub'
                                                                        onClick={() => {
                                                                            // ✅ Close the all slips
                                                                            setOpenSlips({});

                                                                            // ✅ Close current popup and open BOM shortly after
                                                                            setShowSectionPopup(false);
                                                                            setTimeout(() => {
                                                                                const selected = subAssemblies.find(s => s.id === bsa.subassembly);
                                                                                setSelectedSubAssembly(selected || null);
                                                                                setFlowOrTableSub("flow");
                                                                                setActiveSection('SubAssemblyBOM');
                                                                                setShowSectionPopup(true);
                                                                            }, 50);
                                                                        }}>
                                                                        <span className='warehouse-clickable-sub'>{subAssemblies.find(s => s.id === bsa.subassembly)?.name || bsa.subassembly}</span></td>

                                                                    <td>{new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}</td>
                                                                    <td>{getFnnUserName(bsa.built_by)}</td>
                                                                    <td>
                                                                        <select
                                                                            className="form-select select-built"
                                                                            value={bsa.status}
                                                                            onChange={(e) => {
                                                                                const newStatus = e.target.value;
                                                                                if (newStatus === "installed") {
                                                                                    handleUnitStatusChange(bsa.id, newStatus);
                                                                                } else {
                                                                                    setSubAssemblyToConfirmId(bsa.id);
                                                                                    setStatusToConfirm(newStatus);
                                                                                    setShowStatusConfirmPopup(true);
                                                                                }
                                                                            }}
                                                                        >
                                                                            {["available", "installed", "faulty", "discarded"].map(statusOption => (
                                                                                <option key={statusOption} value={statusOption}>
                                                                                    {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>

                                            {isMobile4 && (
                                                <div className="warehouse-mobile-list">
                                                    {rows.map((bsa) => (
                                                        <div key={bsa.id} className="warehouse-card">
                                                            <div className="warehouse-row">
                                                                <span className="warehouse-label">ID:</span>
                                                                <span className="warehouse-value">{bsa.id}</span>
                                                            </div>
                                                            <div className="warehouse-row">
                                                                <span className="warehouse-label">Sub-Assembly:</span>
                                                                <span className="warehouse-value warehouse-clickable-sub"
                                                                    onClick={() => {
                                                                        // ✅ Close the all slips
                                                                        setOpenSlips({});

                                                                        // ✅ Close current popup and open BOM shortly after
                                                                        setShowSectionPopup(false);
                                                                        setTimeout(() => {
                                                                            const selected = subAssemblies.find(s => s.id === bsa.subassembly);
                                                                            setSelectedSubAssembly(selected || null);
                                                                            setFlowOrTableSub("flow");
                                                                            setActiveSection('SubAssemblyBOM');
                                                                            setShowSectionPopup(true);
                                                                        }, 50);
                                                                    }}

                                                                >
                                                                    {subAssemblies.find(s => s.id === bsa.subassembly)?.name || bsa.subassembly}</span>
                                                            </div>
                                                            <div className="warehouse-row">
                                                                <span className="warehouse-label">Built On:</span>
                                                                <span className="warehouse-value">
                                                                    {new Date(bsa.built_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                                                                </span>
                                                            </div>
                                                            <div className="warehouse-row">
                                                                <span className="warehouse-label">Built By:</span>
                                                                <span className="warehouse-value">{getFnnUserName(bsa.built_by)}</span>
                                                            </div>
                                                            <div className="warehouse-row">
                                                                <span className="warehouse-label">Status:</span>
                                                                <select
                                                                    className="form-select select-built warehouse-select"
                                                                    value={bsa.status}
                                                                    onChange={(e) => {
                                                                        const newStatus = e.target.value;
                                                                        if (newStatus === "installed") {
                                                                            handleUnitStatusChange(bsa.id, newStatus);
                                                                        } else {
                                                                            setSubAssemblyToConfirmId(bsa.id);
                                                                            setStatusToConfirm(newStatus);
                                                                            setShowStatusConfirmPopup(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    {["available", "installed", "faulty", "discarded"].map((statusOption) => (
                                                                        <option key={statusOption} value={statusOption}>
                                                                            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detector BOM Flowchart */}
            {showDetBOMPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => (setShowDetBOMPopup(false), setFlowOrTable('flow'))}
                        >
                            x
                        </button>

                        <h2>Detector Bill of Materials</h2>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <select
                                value={selectedDetector2?.id || ''} // Bind the selected value here
                                onChange={(e) => {
                                    const selected = detectors.find(d => d.id === Number(e.target.value));
                                    setSelectedDetector2(selected || null);
                                }}
                                style={{ marginBottom: "10px" }}
                                className='selecting-detec'
                            >
                                <option value="">Select Detector Version</option>
                                {detectors.map((detector) => (
                                    <option key={detector.id} value={detector.id}>
                                        {detector.name} (v{detector.version})
                                    </option>
                                ))}
                            </select>
                        </div>


                        <div className='bom-filters'>
                            <p
                                className={flowOrTable === "flow" ? "active" : ""}
                                onClick={() => setFlowOrTable("flow")}
                            >
                                Flow Chart
                            </p>
                            <p
                                className={flowOrTable === "table" ? "active" : ""}
                                onClick={() => setFlowOrTable("table")}
                            >
                                Table
                            </p>
                        </div>


                        {selectedDetector2 && flowOrTable === "flow" && (
                            <div className='flow-chart-wrapper'>
                                <ReactFlow
                                    nodes={generateFlowNodes(selectedDetector2, subAssemblies, invInfo)}
                                    edges={generateFlowEdges(selectedDetector2)}
                                    fitView
                                >
                                    {/* <MiniMap /> */}
                                    <Controls />
                                    <Background />
                                </ReactFlow>
                            </div>
                        )}

                        {selectedDetector2 && flowOrTable === "table" && (
                            <div className='bom-detector'>
                                <h1>{selectedDetector2.name} (v{selectedDetector2.version})</h1>
                                <p>Total: ${getDetectorCost(selectedDetector2, subAssemblies, invInfo).toFixed(2)}</p>

                                {selectedDetector2.subassemblies.map((ds, index) => {


                                    const sa =
                                        typeof ds.subassembly === 'object'
                                            ? ds.subassembly
                                            : subAssemblies.find(s => s.id === ds.subassembly);


                                    if (!sa) return null;

                                    return (
                                        <div key={sa.id || index} className='bom-subs'>

                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: "1rem" }}>
                                                <>
                                                    <img
                                                        src={sa.subPicture ? sa.subPicture : fnnBlueLogo}
                                                        alt={sa.name}
                                                        className="part-image"
                                                        style={{
                                                            width: 35,
                                                            height: 35,
                                                            objectFit: 'cover',
                                                            marginRight: 8,
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                    <h3> {sa.name} - ${getSubAssemblyCost(sa, invInfo).toFixed(2)}</h3>
                                                </>

                                            </div>

                                            <div className='bom-parts'>
                                                {sa.parts.map((p, idx) => {
                                                    const invPart = invInfo.find(i => i.partID === p.part);
                                                    if (!invPart) return null;

                                                    const totalCost = (invPart.unitCost * p.quantity_required).toFixed(2);

                                                    return (
                                                        <div key={idx} className='bom-single-part' style={{ display: 'flex', alignItems: 'center', marginBottom: "0.5rem" }}>
                                                            <img
                                                                src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                                                                alt={invPart.partName}
                                                                className="part-image"
                                                                style={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    objectFit: 'cover',
                                                                    marginRight: 8,
                                                                    borderRadius: 4,
                                                                }}
                                                            />
                                                            <span>{invPart.partName} x{p.quantity_required} - ${totalCost}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}



                    </div>
                </div>
            )}

            {/* Sub-Assembly BOM Flowchart */}
            {showBOMPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowBOMPopup(false);
                                setSelectedSubAssembly(null);
                                setFlowOrTableSub('flow');
                            }}
                        >
                            x
                        </button>

                        <h2>Sub-Assembly Bill of Materials</h2>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <select
                                value={selectedSubAssembly?.id || ''}
                                onChange={(e) => {
                                    const selected = subAssemblies.find(s => s.id === Number(e.target.value));
                                    setSelectedSubAssembly(selected || null);
                                }}
                                style={{ marginBottom: "10px" }}
                                className='selecting-detec'
                            >
                                <option value="">Select Sub-Assembly</option>
                                {subAssemblies.map(sa => (
                                    <option key={sa.id} value={sa.id}>
                                        {sa.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className='bom-filters'>
                            <p
                                className={flowOrTableSub === "flow" ? "active" : ""}
                                onClick={() => setFlowOrTableSub("flow")}
                            >
                                Flow Chart
                            </p>
                            <p
                                className={flowOrTableSub === "table" ? "active" : ""}
                                onClick={() => setFlowOrTableSub("table")}
                            >
                                Table
                            </p>
                        </div>

                        {selectedSubAssembly && flowOrTableSub === "flow" && (
                            <div className='flow-chart-wrapper'>
                                <ReactFlow
                                    nodes={generateSubAssemblyNodes(selectedSubAssembly, invInfo)}
                                    edges={generateSubAssemblyEdges(selectedSubAssembly)}
                                    fitView
                                >
                                    <Controls />
                                    <Background />
                                </ReactFlow>
                            </div>
                        )}

                        {selectedSubAssembly && flowOrTableSub === "table" && (
                            <div className='bom-detector'>

                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: "0.5rem" }}>
                                    <>
                                        <img
                                            src={selectedSubAssembly.subPicture ? selectedSubAssembly.subPicture : fnnBlueLogo}
                                            alt={selectedSubAssembly.name}
                                            className="part-image"
                                            style={{
                                                width: 35,
                                                height: 35,
                                                objectFit: 'cover',
                                                marginRight: 8,
                                                borderRadius: 4,
                                            }}
                                        />
                                        <h3> {selectedSubAssembly.name}</h3>
                                    </>
                                </div>



                                <p>Total: ${getSubAssemblyCost(selectedSubAssembly, invInfo).toFixed(2)}</p>
                                <div>
                                    {selectedSubAssembly.parts.map((p, idx) => {
                                        const invPart = invInfo.find(i => i.partID === p.part);
                                        if (!invPart) return null;

                                        const totalCost = (invPart.unitCost * p.quantity_required).toFixed(2);

                                        return (
                                            <div
                                                key={idx}
                                                className='bom-single-part'
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: "0.5rem"
                                                }}
                                            >
                                                <img
                                                    src={invPart.partPicture ? invPart.partPicture : fnnBlueLogo}
                                                    alt={invPart.partName}
                                                    className="part-image"
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        objectFit: 'cover',
                                                        marginRight: 8,
                                                        borderRadius: 4,
                                                    }}
                                                />
                                                <span>{invPart.partName} x{p.quantity_required} - ${totalCost}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Ordering Parts */}
            {activeSection === "OrderParts" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">

                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>Order Management</h2>

                        <div className="inventory-table-wrapper wrapper-for-orders">

                            {isMobile2 && (

                                <div>

                                    <div className="inv-stat-filters">
                                        {Object.keys(statusPriority).map((status) => (
                                            <button
                                                key={status}
                                                className={`inv-stats-opener ${selectedStatus === status ? 'active' : ''}`}
                                                onClick={() => setSelectedStatus(status as keyof typeof statusPriority)}
                                            >
                                                {status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="inv-search-wrapper order-search">
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            value={orderSearchTerm}
                                            onChange={(e) => setOrderSearchTerm(e.target.value)}
                                            className="inv-search-input"
                                        />
                                    </div>

                                    <div className="mobile-table-header">
                                        <span>Forecast Orders</span>
                                        <IoBagAddOutline className="add-part-icon" onClick={() => setShowAddOrderPopup(true)} />
                                    </div>

                                </div>
                            )}

                            {!isMobile2 ? (
                                // DESKTOP TABLE HERE_ORDER

                                <div className="inventory-table-scroll-container">

                                    <div className='inv-stat-filters-and-search'>
                                        <div className="inv-stat-filters filter-for-order">
                                            {Object.keys(statusPriority).map((status) => (
                                                <button
                                                    key={status}
                                                    className={`inv-stats-opener ${selectedStatus === status ? 'active' : ''}`}
                                                    onClick={() => setSelectedStatus(status as keyof typeof statusPriority)}
                                                >
                                                    {status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="inv-search-wrapper order-search">
                                            <input
                                                type="text"
                                                placeholder="Search"
                                                value={orderSearchTerm}
                                                onChange={(e) => setOrderSearchTerm(e.target.value)}
                                                className="inv-search-input"
                                            />
                                        </div>
                                    </div>

                                    <table className="inventory-table">
                                        <thead className="main-thead">
                                            <tr>
                                                <th>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        Part
                                                        <IoBagAddOutline
                                                            className="add-part-icon"
                                                            onClick={() => setShowAddOrderPopup(true)}
                                                        />
                                                    </div>
                                                </th>
                                                <th>Unit Amount</th>

                                                {/* ✅ Order Date column only for Requested (disabled), Ordered, or Arrived/Returned/Cancelled */}
                                                {selectedStatus !== "Requested" && (
                                                    <th>Order Date</th>
                                                )}

                                                {/* ✅ Arrival Date column only for Arrived/Returned/Cancelled */}
                                                {["Ordered"].includes(selectedStatus) && (
                                                    <th><div style={{ display: 'flex', gap: '0.3rem' }}>
                                                        <span style={{ whiteSpace: 'nowrap' }}>Expected By</span>
                                                        {showInfoTooltip("Day in which the item is supposed to arrive. Text will become red if expected arrival has passed and item still hasn't arrived.")}
                                                    </div></th>
                                                )}

                                                {/* ✅ Arrival Date column only for Arrived/Returned/Cancelled */}
                                                {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) && (
                                                    <th>Arrival Date</th>
                                                )}

                                                <th>Created by</th>
                                                <th>
                                                    <div className="column-row-div">
                                                        {["Arrived", "Returned", "Cancelled"].includes(selectedStatus)
                                                            ? "Status"
                                                            : selectedStatus === "Requested"
                                                                ? (
                                                                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                                        <span style={{ whiteSpace: 'nowrap' }}>Confirm Order</span>
                                                                        {showInfoTooltip("Click to confirm if an order was placed with a store.")}
                                                                    </div>
                                                                )
                                                                : selectedStatus === "Ordered"
                                                                    ? (
                                                                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                                            <span style={{ whiteSpace: 'nowrap' }}>Confirm Arrival</span>
                                                                            {showInfoTooltip("Click to confirm how many items from this order has arrived.")}
                                                                        </div>
                                                                    )
                                                                    : ""}
                                                    </div>
                                                </th>
                                                <th><div className="column-row-div">Order Again</div></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredForecasts.map((forecast) => {
                                                const matchingPart = invInfo.find(p => p.partID === forecast.part);

                                                return (
                                                    <tr
                                                        key={forecast.id}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const part = matchingPart || { partName: '' };
                                                            setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                            setEditingForecastId(forecast.id);
                                                            setForecastPartID(forecast.part);
                                                            setForecastPartName(part.partName);
                                                            setPartSearchTerm(part.partName);
                                                            setForecastAmount(forecast.expectedAmount);
                                                            setForecastCost(forecast.cost);
                                                            setForecastOrderedDate(
                                                                forecast.orderedDate
                                                                    ? new Date(forecast.orderedDate).toISOString().substring(0, 10)
                                                                    : ''
                                                            );
                                                            setForecastArrivalDate(
                                                                forecast.arrivalDate
                                                                    ? new Date(forecast.arrivalDate).toISOString().substring(0, 10)
                                                                    : ''
                                                            );
                                                            setForecastStatus(forecast.status);
                                                            setForecastStoreLinks(forecast.sourceLinks || '');
                                                            setForecastRequestor(`${first_name} ${last_name}`);
                                                            setForecastNotes(forecast.notes || '');
                                                            setForecastPriority(forecast.priority || 'Low');
                                                            setForecastLeadTime(forecast.lead_time || '');
                                                            setForecastIsPacket(!!forecast.is_packet);
                                                            setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                            setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                            setForecastPackCost(forecast.pack_cost || 0);
                                                            setShowAddOrderPopup(true);

                                                            setForecastExpectedArrivalDate(
                                                                forecast.expectedArrivalDate
                                                                    ? new Date(forecast.expectedArrivalDate).toISOString().substring(0, 10)
                                                                    : ''
                                                            );
                                                        }}
                                                    >
                                                        <td>
                                                            <div className="part-info-cell">
                                                                <img src={matchingPart?.partPicture ? matchingPart?.partPicture : fnnBlueLogo} alt={matchingPart?.partName} className="part-image" />
                                                                <span>{renderPartNameWithPriority(forecast)}</span>
                                                            </div>
                                                        </td>
                                                        <td>{renderUnitAmountWithPackInfo(forecast)}</td>


                                                        {/* ✅ Show Order Date only when not in Requested */}
                                                        {selectedStatus !== "Requested" && (
                                                            <td>{formatDateForDisplay(forecast.orderedDate)}</td>
                                                        )}

                                                        {/* ✅ Show Expected Arrival Date only in Ordered */}
                                                        {["Ordered"].includes(selectedStatus) && (() => {
                                                            if (!forecast.expectedArrivalDate) {
                                                                return <td>Unknown</td>;
                                                            }

                                                            const expectedDate = new Date(forecast.expectedArrivalDate);
                                                            const today = new Date();

                                                            // Normalize both to midnight UTC to fully ignore timezone and time portion
                                                            expectedDate.setUTCHours(0, 0, 0, 0);
                                                            today.setUTCHours(0, 0, 0, 0);

                                                            // Red only if expectedArrivalDate is strictly BEFORE today
                                                            const isLate = expectedDate.getTime() < today.getTime();

                                                            return (
                                                                <td style={{ color: isLate ? 'red' : 'inherit' }}>
                                                                    {formatDateForDisplay(forecast.expectedArrivalDate)}
                                                                </td>
                                                            );
                                                        })()}


                                                        {/* ✅ Show Arrival Date only in Arrived/Returned/Cancelled */}
                                                        {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) && (
                                                            <td>{formatDateForDisplay(forecast.arrivalDate)}</td>
                                                        )}

                                                        <td>{forecast.requestor}</td>

                                                        <td>
                                                            <div className="column-row-div">
                                                                {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) ? (
                                                                    <span>{forecast.status}</span>
                                                                ) : selectedStatus === "Requested" ? (
                                                                    <div
                                                                        className="delete-part-button mobile-confirm-part-button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setActiveForecast(forecast);
                                                                            setOrderedAmount(forecast.expectedAmount);

                                                                            // Calculate packets if packet-based
                                                                            if (forecast.is_packet && forecast.amount_per_packet) {
                                                                                setOrderedPackets(
                                                                                    forecast.expectedAmount / forecast.amount_per_packet
                                                                                );
                                                                            }

                                                                            setShowOrderItemPopup(true);
                                                                        }}
                                                                    >
                                                                        ✓
                                                                    </div>
                                                                ) : selectedStatus === "Ordered" ? (
                                                                    <div
                                                                        className="delete-part-button mobile-confirm-part-button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setActiveForecast(forecast);
                                                                            setArrivalAmount(forecast.expectedAmount);
                                                                            setShowArrivalItemPopup(true);
                                                                        }}
                                                                    >
                                                                        ✓
                                                                    </div>
                                                                ) : (
                                                                    ""
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div className="column-row-div">
                                                                <div className="delete-part-button reorder-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const part = matchingPart || { partName: '' };
                                                                        setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                                        setForecastPartID(forecast.part);
                                                                        setForecastPartName(part.partName);
                                                                        setPartSearchTerm(part.partName);
                                                                        setForecastAmount(forecast.expectedAmount);
                                                                        setForecastCost(forecast.cost);
                                                                        setForecastStatus("Requested");
                                                                        setForecastStoreLinks(forecast.sourceLinks || '');
                                                                        setForecastRequestor(`${first_name} ${last_name}`);
                                                                        setForecastNotes(forecast.notes || '');
                                                                        setForecastPriority(forecast.priority || 'Low');
                                                                        setForecastLeadTime(forecast.lead_time || '');
                                                                        setForecastIsPacket(!!forecast.is_packet);
                                                                        setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                                        setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                                        setForecastPackCost(forecast.pack_cost || 0);
                                                                        setShowAddOrderPopup(true);
                                                                    }}>
                                                                    <LuCopyPlus />
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* <td>
                                                            <div className="column-row-div">
                                                                <div
                                                                    className="delete-part-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setForecastToDeleteId(forecast.id);
                                                                        setShowDeleteForecastPopup(true);
                                                                    }}
                                                                >
                                                                    x
                                                                </div>
                                                            </div>
                                                        </td> */}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                </div>
                            ) : (
                                // MOBILE TABLE

                                <div className="mobile-forecast-list">

                                    {filteredForecasts.map((forecast) => {
                                        const matchingPart = invInfo.find(p => p.partID === forecast.part);
                                        const isExpanded = expandedForecastId === forecast.id;

                                        return (
                                            <React.Fragment key={forecast.id}>
                                                <div
                                                    className="mobile-part-row2"
                                                    onClick={() => {
                                                        setExpandedForecastId(expandedForecastId === forecast.id ? null : forecast.id);
                                                    }}
                                                >
                                                    <div className="part-info-cell">
                                                        <img src={matchingPart?.partPicture ? matchingPart?.partPicture : fnnBlueLogo} alt={matchingPart?.partName} className="part-image" />
                                                        <span>{renderPartNameWithPriority(forecast)}</span>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div
                                                        className="mobile-details-row2"
                                                        onClick={() => {
                                                            // your form prefill logic here
                                                            const part = matchingPart || { partName: '' };
                                                            setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                            setEditingForecastId(forecast.id);
                                                            setForecastPartID(forecast.part);
                                                            setForecastPartName(part.partName);
                                                            setPartSearchTerm(part.partName);
                                                            setForecastAmount(forecast.expectedAmount);
                                                            setForecastCost(forecast.cost);
                                                            setForecastOrderedDate(
                                                                forecast.orderedDate ? new Date(forecast.orderedDate).toISOString().substring(0, 10) : ''
                                                            );
                                                            setForecastArrivalDate(
                                                                forecast.arrivalDate ? new Date(forecast.arrivalDate).toISOString().substring(0, 10) : ''
                                                            );
                                                            setForecastStatus(forecast.status);
                                                            setForecastStoreLinks(forecast.sourceLinks || '');
                                                            setForecastRequestor(`${first_name} ${last_name}`);
                                                            setForecastNotes(forecast.notes || '');
                                                            setForecastPriority(forecast.priority || 'Low');
                                                            setForecastLeadTime(forecast.lead_time || '');
                                                            setForecastIsPacket(!!forecast.is_packet);
                                                            setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                            setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                            setForecastPackCost(forecast.pack_cost || 0);
                                                            setShowAddOrderPopup(true);

                                                            setForecastExpectedArrivalDate(
                                                                forecast.expectedArrivalDate
                                                                    ? new Date(forecast.expectedArrivalDate).toISOString().substring(0, 10)
                                                                    : ''
                                                            );
                                                        }}
                                                    >
                                                        <div className="mobile-detail-group">
                                                            <strong>Unit Amount:</strong> {renderUnitAmountWithPackInfo(forecast)}
                                                        </div>

                                                        {/* Ordered Date — hide when Requested */}
                                                        {selectedStatus !== "Requested" && (
                                                            <div className="mobile-detail-group">
                                                                <strong>Ordered Date:</strong>{" "}
                                                                {forecast.orderedDate ? formatDateForDisplay(forecast.orderedDate) : 'Undefined'}
                                                            </div>
                                                        )}

                                                        {/* Expected Arrival Date — hide when Requested */}
                                                        {selectedStatus === "Ordered" && (
                                                            <div className="mobile-detail-group">
                                                                <strong>Expected By:</strong>{" "}

                                                                {["Ordered"].includes(selectedStatus) && (() => {
                                                                    if (!forecast.expectedArrivalDate) {
                                                                        return <td>Unknown</td>;
                                                                    }

                                                                    const expectedDate = new Date(forecast.expectedArrivalDate);
                                                                    const today = new Date();

                                                                    // Normalize both to midnight UTC to fully ignore timezone and time portion
                                                                    expectedDate.setUTCHours(0, 0, 0, 0);
                                                                    today.setUTCHours(0, 0, 0, 0);

                                                                    // Red only if expectedArrivalDate is strictly BEFORE today
                                                                    const isLate = expectedDate.getTime() < today.getTime() && forecast.alert_generated === false;

                                                                    return (
                                                                        <td style={{ color: isLate ? 'red' : 'inherit' }}>
                                                                            {formatDateForDisplay(forecast.expectedArrivalDate)}
                                                                        </td>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}

                                                        {/* Arrival Date — hide when Requested or Ordered */}
                                                        {selectedStatus !== "Requested" && selectedStatus !== "Ordered" && (
                                                            <div className="mobile-detail-group">
                                                                <strong>Arrival Date:</strong>{" "}
                                                                {forecast.arrivalDate ? formatDateForDisplay(forecast.arrivalDate) : 'Unknown'}
                                                            </div>
                                                        )}

                                                        <div className="mobile-detail-group">
                                                            <strong>
                                                                {["Arrived", "Returned", "Cancelled"].includes(selectedStatus)
                                                                    ? "Status"
                                                                    : selectedStatus === "Requested"
                                                                        ? "Confirm Order:"
                                                                        : selectedStatus === "Ordered"
                                                                            ? "Confirm Arrival"
                                                                            : ""}
                                                            </strong>

                                                            {["Arrived", "Returned", "Cancelled"].includes(selectedStatus) ? (
                                                                <span>{forecast.status}</span>
                                                            ) : selectedStatus === "Requested" ? (
                                                                <div
                                                                    className="delete-part-button mobile-confirm-part-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActiveForecast(forecast);
                                                                        setOrderedAmount(forecast.expectedAmount);
                                                                        setExpandedForecastId(null);

                                                                        // Calculate packets if packet-based
                                                                        if (forecast.is_packet && forecast.amount_per_packet) {
                                                                            setOrderedPackets(
                                                                                forecast.expectedAmount / forecast.amount_per_packet
                                                                            );
                                                                        }

                                                                        setShowOrderItemPopup(true);
                                                                    }}
                                                                >
                                                                    ✓
                                                                </div>
                                                            ) : selectedStatus === "Ordered" ? (
                                                                <div
                                                                    className="delete-part-button mobile-confirm-part-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActiveForecast(forecast);
                                                                        setArrivalAmount(forecast.expectedAmount);
                                                                        setShowArrivalItemPopup(true);
                                                                    }}
                                                                >
                                                                    ✓
                                                                </div>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </div>

                                                        <div className="mobile-detail-group">
                                                            <strong>By:</strong> {forecast.requestor}
                                                        </div>


                                                        <div className="mobile-detail-delete">

                                                            <div className="delete-part-button reorder-button"
                                                                style={{ marginRight: '1rem' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const part = matchingPart || { partName: '' };
                                                                    setForecastPartImage((part as InventoryItems)?.partPicture || null);
                                                                    setForecastPartID(forecast.part);
                                                                    setForecastPartName(part.partName);
                                                                    setPartSearchTerm(part.partName);
                                                                    setForecastAmount(forecast.expectedAmount);
                                                                    setForecastCost(forecast.cost);
                                                                    setForecastStatus("Requested");
                                                                    setForecastStoreLinks(forecast.sourceLinks || '');
                                                                    setForecastRequestor(`${first_name} ${last_name}`);
                                                                    setForecastNotes(forecast.notes || '');
                                                                    setForecastPriority(forecast.priority || 'Low');
                                                                    setForecastLeadTime(forecast.lead_time || '');
                                                                    setForecastIsPacket(!!forecast.is_packet);
                                                                    setForecastNumberOfPackets(forecast.number_of_packets || 0);
                                                                    setForecastAmountPerPacket(forecast.amount_per_packet || 0);
                                                                    setForecastPackCost(forecast.pack_cost || 0);
                                                                    setShowAddOrderPopup(true);
                                                                }}>
                                                                <LuCopyPlus />
                                                            </div>

                                                            <div
                                                                className="delete-part-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setForecastToDeleteId(forecast.id);
                                                                    setShowDeleteForecastPopup(true);
                                                                }}
                                                            >
                                                                x
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </div>


                    </div>
                </div>
            )}

            {showAddOrderPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content for-forecast'>

                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowAddOrderPopup(false);
                                clearForecastForm();
                                setInvalidFields([]);
                            }}
                        >
                            x
                        </button>


                        <div className="add-new-part-form">
                            <h3>{editingForecastId ? 'Edit Order' : 'Create Order Request'}</h3>

                            <div className='add-new-part-table add-part-ext'>

                                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                    {forecastPartImage ? (
                                        <img
                                            className="large-part-picture-in-order"
                                            src={forecastPartImage}
                                            alt={forecastPartName || "Part Image"}
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).src = ""; // fallback
                                            }}
                                        />
                                    ) : (
                                        <AiFillPicture className="large-part-picture-in-order" />
                                    )}
                                </div>

                                <label style={{ position: 'relative' }}>
                                    <span>Part Name</span>
                                    <input
                                        type="text"
                                        placeholder="Search name or MPN"
                                        value={partSearchTerm}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setPartSearchTerm(value);

                                            // Clear cost and store link if input no longer matches any part
                                            const match = invInfo.find(p => p.partName.toLowerCase() === value.toLowerCase());
                                            if (!match) {
                                                setForecastCost(0);
                                                setForecastStoreLinks('');
                                                setForecastPartID(null);
                                                setForecastPartName(value);
                                                setShowDropdown(true);
                                            }
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)} // allow click
                                        className={invalidFields.includes("part") ? "input-error" : ""}
                                    />

                                    {showDropdown && (
                                        <ul className="autocomplete-dropdown order-dropdown-ext">
                                            {filteredPartOptions.map((item) => (
                                                <li
                                                    key={item.partID}
                                                    onClick={() => {
                                                        setForecastPartName(item.partName);
                                                        setForecastPartID(item.partID);
                                                        setPartSearchTerm(item.partName);

                                                        // Autofill cost and store link
                                                        setForecastCost(item.unitCost ?? 0);
                                                        setForecastStoreLinks(item.partLink ?? '');
                                                        setForecastPartImage(item.partPicture ?? null);

                                                        // ✅ Prefill packet-related fields
                                                        setForecastIsPacket(item.sold_in_packets ?? false);
                                                        setForecastAmountPerPacket(item.amount_per_packet ?? 0);

                                                        // Reset number of packets and total amount
                                                        setForecastNumberOfPackets(0);
                                                        setForecastAmount(0);
                                                        setForecastPackCost(0);

                                                        setShowDropdown(false);
                                                    }}
                                                >
                                                    <img className='part-image' src={item.partPicture ? item.partPicture : fnnBlueLogo} alt={item.partName}></img>
                                                    {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                                                </li>

                                            ))}
                                            {filteredPartOptions.length === 0 && (
                                                <li style={{ color: '#999' }}>No matches found</li>
                                            )}
                                            <button onClick={() => setShowAddItemPopup(true)}>Don’t see my part</button>
                                        </ul>
                                    )}
                                </label>



                                {/* Is Packet Checkbox */}
                                <label className="checkbox-label">
                                    <span>Order Comes in Packs?</span>
                                    <input
                                        type="checkbox"
                                        checked={forecastIsPacket}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setForecastIsPacket(checked);

                                            if (!checked) {
                                                // Reset packet-related fields
                                                setForecastNumberOfPackets(0);
                                                setForecastAmountPerPacket(0);
                                                setForecastPackCost(0);

                                                // Restore base unit cost from selected part
                                                const selectedPart = invInfo.find(p => p.partID === forecastPartID);
                                                setForecastCost(selectedPart?.unitCost ?? 0);
                                            } else {
                                                // If checked, and the part has a default amount per pack, prefill
                                                const selectedPart = invInfo.find(p => p.partID === forecastPartID);
                                                setForecastAmountPerPacket(selectedPart?.amount_per_packet ?? 0);
                                                setForecastNumberOfPackets(0);
                                                setForecastAmount(0);
                                                setForecastPackCost(0);
                                            }
                                        }}
                                    />
                                </label>


                                {/* If NOT packet, show normal Amount input */}
                                {!forecastIsPacket ? (
                                    <label>
                                        <span>Amount</span>
                                        <input
                                            type="number"
                                            value={forecastAmount}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForecastAmount(val === '' ? 0 : parseInt(val));
                                            }}
                                            className={invalidFields.includes("amount") ? "input-error" : ""}
                                        />
                                    </label>
                                ) : (
                                    <>
                                        {/* If IS packet, show packet fields */}
                                        <label>
                                            <span>Number of Packs</span>
                                            <input
                                                type="number"
                                                value={forecastNumberOfPackets}
                                                onChange={(e) => {
                                                    const num = parseInt(e.target.value || "0");
                                                    setForecastNumberOfPackets(num);
                                                    setForecastAmount(num * forecastAmountPerPacket);
                                                }}
                                            />
                                        </label>

                                        <label>
                                            <span>Amount per Pack</span>
                                            <input
                                                type="number"
                                                value={forecastAmountPerPacket}
                                                onChange={(e) => {
                                                    const num = parseInt(e.target.value || "0");
                                                    setForecastAmountPerPacket(num);
                                                    setForecastAmount(num * forecastNumberOfPackets);

                                                    // Update unit cost dynamically if pack cost is already set
                                                    if (forecastPackCost > 0 && num > 0) {
                                                        const unitCost = forecastPackCost / num;
                                                        setForecastCost(parseFloat(unitCost.toFixed(2)));
                                                    }
                                                }}
                                            />
                                        </label>


                                        <label>
                                            <span>Total Amount</span>
                                            <input
                                                type="number"
                                                value={forecastAmount}
                                                disabled
                                                readOnly
                                                style={{ backgroundColor: '#f0f0f0', cursor: "not-allowed" }}
                                            />
                                        </label>

                                        {/* NEW: Pack Cost */}
                                        <label>
                                            <span>Pack Cost</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={forecastPackCost}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const packCost = val === '' ? 0 : parseFloat(val);
                                                    setForecastPackCost(packCost);

                                                    // Auto-calculate unit cost if amount per pack > 0
                                                    if (forecastAmountPerPacket > 0) {
                                                        const unitCost = packCost / forecastAmountPerPacket;
                                                        setForecastCost(parseFloat(unitCost.toFixed(2)));
                                                    }
                                                }}
                                            />
                                        </label>


                                    </>
                                )}


                                <label>
                                    <span>Unit Cost</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={forecastCost}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setForecastCost(val === '' ? 0 : parseFloat(val));
                                        }}
                                        className={invalidFields.includes("cost") ? "input-error" : ""}
                                        disabled={forecastIsPacket}           // disable when pack mode is on
                                        readOnly={forecastIsPacket}           // prevents edits
                                        style={
                                            forecastIsPacket
                                                ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" } // greyed out look
                                                : {}
                                        }
                                    />
                                </label>


                                {editingForecastId ? <label>
                                    <span>Order Date</span>
                                    <input
                                        type="date"
                                        value={forecastOrderedDate}
                                        onChange={(e) => setForecastOrderedDate(e.target.value)}
                                    />
                                </label> : ''}

                                {editingForecastId ? <label>
                                    <span>Expected By</span>
                                    <input
                                        type="date"
                                        value={forecastExpectedArrivalDate}
                                        onChange={(e) => setForecastExpectedArrivalDate(e.target.value)}
                                    />
                                </label> : ''}


                                {editingForecastId ? <label>
                                    <span>Arrival Date</span>
                                    <input
                                        type="date"
                                        value={forecastArrivalDate}
                                        onChange={(e) => setForecastArrivalDate(e.target.value)}
                                    />
                                </label> : ''}

                                <label>
                                    <span>Status</span>
                                    <select
                                        value={forecastStatus}
                                        onChange={(e) => {
                                            const newStatus = e.target.value;
                                            setForecastStatus(newStatus);

                                            const today = new Date().toISOString().substring(0, 10); // format: YYYY-MM-DD

                                            if (newStatus === "Ordered") {
                                                setForecastOrderedDate(today);
                                            }

                                            if (newStatus === "Arrived") {
                                                setForecastArrivalDate(today);
                                            }
                                        }}
                                        className={invalidFields.includes("status") ? "input-error" : ""}
                                    >
                                        <option value="Requested">Requested</option>
                                        <option value="Ordered">Ordered</option>
                                        <option value="Cancelled">Cancelled</option>
                                        <option value="Returned">Returned</option>
                                        <option value="Arrived">Arrived</option>
                                    </select>
                                </label>

                                <label>
                                    <span>Order Priority</span>
                                    <select
                                        value={forecastPriority}
                                        onChange={(e) => setForecastPriority(e.target.value)}
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                    </select>
                                </label>

                                <label>
                                    <span>Lead Time</span>
                                    <input
                                        type="text"
                                        min={0}
                                        value={forecastLeadTime}
                                        onChange={(e) => setForecastLeadTime(e.target.value)}
                                        placeholder='How long it wil take the manufacturer to produce this item'
                                    />
                                </label>

                                <label>
                                    <span>Store Links</span>
                                    <textarea
                                        placeholder='E.g "www.storewebsite.com, ..."'
                                        value={forecastStoreLinks}
                                        onChange={(e) => setForecastStoreLinks(e.target.value)}
                                    />
                                </label>


                                <label>
                                    <span>Notes </span>
                                    <textarea
                                        placeholder='E.g "Item shipment was delayed"'
                                        value={forecastNotes}
                                        onChange={(e) => setForecastNotes(e.target.value)}
                                    />
                                </label>

                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                <button
                                    onClick={editOrAddForecast}
                                >
                                    {editingForecastId ? 'Update' : 'Confirm'}
                                </button>

                                {editingForecastId && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setForecastToDeleteId(editingForecastId);
                                            setShowDeleteForecastPopup(true);
                                        }}
                                        style={{
                                            backgroundColor: "#e74c3c",
                                            color: "#fff",
                                            border: "none",
                                            padding: "0.6rem 1.2rem",
                                            borderRadius: "0.2rem",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Delete
                                    </button>
                                )}


                            </div>
                        </div>

                    </div>
                </div>
            )}

            {showDeleteForecastPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>
                        <div className='inv-delete-inner-pop'>
                            <h3>Are You Sure You Want to Delete This Order?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteForecastPopup(false);
                                    setForecastToDeleteId(null);
                                    setShowAddOrderPopup(false);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (forecastToDeleteId !== null) {
                                            handleDeleteForecast(forecastToDeleteId);
                                            setShowAddOrderPopup(false);
                                            clearForecastForm();
                                            setInvalidFields([]);

                                            toast.success("Order successfully deleted", {
                                                autoClose: 4000,
                                                hideProgressBar: false,
                                                closeOnClick: true,
                                                pauseOnHover: true,
                                                draggable: true,
                                                style: {
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    fontSize: '16px',
                                                },
                                            });
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Parts for X more detectors popup */}
            {activeSection === "PartsForXMore" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">

                        <button
                            className="close-popup-button"
                            onClick={() => (setShowSectionPopup(false), setNextXDetectors(1))}
                        >
                            x
                        </button>

                        <h2 style={{ display: "flex", flexDirection: 'column', alignItems: "center", gap: "0.5rem" }}>
                            Parts for More Detectors
                            {selectedDetectorId && (() => {
                                const detector = detectors.find(d => d.id === selectedDetectorId);
                                if (!detector) return null;
                                return (
                                    <h2 style={{ paddingTop: '0.5rem', paddingBottom: 'unset' }}>
                                        {detector.name} v{detector.version}
                                    </h2>
                                );
                            })()}
                        </h2>

                        <div className='parts-req-and-input'>

                            <p>Parts for</p>
                            <input
                                type="number"
                                min={1}
                                value={nextXDetectors}
                                onChange={(e) => setNextXDetectors(Number(e.target.value))}
                                style={{ marginBottom: '1rem' }}
                                className='parts-req-and-input-input'
                            />

                            <p>Additional Detector(s)</p>
                        </div>

                        <div className="inventory-table-wrapper forecast-table">
                            <table className="inventory-table">
                                <thead>
                                    <tr>
                                        <th>Part</th>
                                        <th><div className='column-row-div'>Amount Necessary</div></th>
                                        <th><div className='column-row-div'>Forecasted</div></th>
                                        <th><div className='column-row-div'>Place Order Request</div></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {Object.entries(partsNeededForNext).map(([partID, quantity]) => {
                                        const part = invInfo.find(item => item.partID === Number(partID));
                                        const forecastedAmount = forecastAmountsByPart[Number(partID)] || 0;

                                        return (
                                            <tr key={partID}>
                                                <td>
                                                    {part ? (
                                                        <div className="part-info-cell">
                                                            <img
                                                                src={part.partPicture ? part.partPicture : fnnBlueLogo}
                                                                alt={part.partName}
                                                                className="part-image"
                                                                style={{ cursor: 'default' }}
                                                            />
                                                            <span>{truncateText(part.partName, 30)}</span>
                                                        </div>
                                                    ) : (
                                                        `Unknown Part #${partID}`
                                                    )}
                                                </td>
                                                <td><div className='column-row-div'>{quantity} </div></td>
                                                <td><div className='column-row-div'>{forecastedAmount} </div></td>
                                                <td>
                                                    <div className='column-row-div'>
                                                        <div
                                                            className='place-request-button'
                                                            onClick={() => {
                                                                setActiveRequestPartID(Number(partID));
                                                                setRequestQuantity(quantity); // prefill with default quantity
                                                                setShowRequestItemPopup(true);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            Request
                                                        </div>

                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>


                            </table>
                        </div>

                        <div className='request-all-parts-wrapper'>
                            <div
                                className='place-request-button'
                                onClick={() => {
                                    setActiveSection('PlanningSection');
                                    setShowSectionPopup(true);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                Plan a Batch
                            </div>

                            <div
                                className='place-request-button'
                                onClick={() => {
                                    Object.entries(partsNeededForNext).forEach(([id, qty]) => {
                                        handlePartForecastRequest(Number(id), qty as number);
                                    });
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                Request All
                            </div>

                        </div>

                    </div>
                </div>
            )}

            {/* Analytics Popup */}
            {activeSection === "Analytics" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">

                        <button
                            className="close-popup-button"
                            onClick={() => setShowSectionPopup(false)}
                        >
                            x
                        </button>

                        <h2>
                            Analytics ({formatDate(firstDayOfMonth)} - {formatDate(today)})
                        </h2>


                        <div className='inv-analytics-card-stack' ref={reportRef}>

                            <div className='inv-analytics-card'>

                                <div className='inv-analytics-icon-and-title'>
                                    <h1>Detectors</h1>
                                    <div className="analytics-icon-wrapper">
                                        <FaBoltLightning className='analytics-icon' />
                                    </div>
                                </div>

                                <div className='inv-analytics-inner'>
                                    <p> <strong> • Latest Detector Version:</strong> {getLatestDetectorVersionNumber(detectors)}</p>
                                    <p> <strong> • Number of Versions:</strong> {getNumberOfDetectorVersions(detectors)}</p>
                                </div>

                            </div>

                            <div className='inv-analytics-card iac1'>

                                <div className='inv-analytics-icon-and-title'>
                                    <h1>Budget</h1>
                                    <div className="analytics-icon-wrapper ic1">
                                        <GiTwoCoins className='analytics-icon ic1' />
                                    </div>
                                </div>

                                <div className='inv-analytics-inner'>
                                    <p><strong> • Value of Full Inventory:</strong> ${getFullInventoryCost(invInfo).toFixed(2)}</p>
                                    <p><strong> • Cost of Fulfilled Orders this Month:</strong> ${getFulfilledOrdersCostThisMonth(forecastInfo).toFixed(2)}</p>
                                    <p><strong> • Most Expensive Item in Inventory:</strong> {getMostExpensiveItem(invInfo)}</p>

                                    <div className='inv-analytics-table-and-title'>
                                        {subAssemblyProduction.length === 0 ? (
                                            <p><strong> • Value of Sub-Assemblies Produced this Month:</strong> N/A</p>
                                        ) : (
                                            <>
                                                <p><strong> • Value of Sub-Assemblies Produced this Month:</strong></p>
                                                <table className="inventory-table analytic-table-header">
                                                    <thead className='main-thead'>
                                                        <tr>
                                                            <th>SubAssembly</th>
                                                            <th>Amount</th>
                                                            <th>Unit Cost</th>
                                                            <th>Total Cost</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {subAssemblyProduction.map((item, index) => {
                                                            const subAssembly = subAssemblies.find(s => s.id === item.object_id);
                                                            if (!subAssembly) return null;

                                                            const unitCost = getSubAssemblyCost(subAssembly, invInfo);
                                                            const totalCost = unitCost * item.amount_produced;

                                                            return (
                                                                <tr key={index}>
                                                                    <td>{item.object_name}</td>
                                                                    <td>{item.amount_produced}</td>
                                                                    <td>${unitCost.toFixed(2)}</td>
                                                                    <td>${totalCost.toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                <p style={{ paddingLeft: '1rem' }}><strong>Total from Sub-Assemblies: </strong>${totalSubAssemblyProductionCost.toFixed(2)}</p>
                                                <p style={{ color: '#28aee1' }}><strong>Note: </strong>Only includes Sub-Assemblies consumed independently from consuming a Detector</p>
                                            </>
                                        )}
                                    </div>

                                    <div className='inv-analytics-table-and-title'>
                                        {detectorProduction.length === 0 ? (
                                            <p><strong> • Value of Detectors Produced this Month:</strong> N/A</p>
                                        ) : (
                                            <>
                                                <p><strong> • Value of Detectors Produced this Month:</strong></p>
                                                <table className="inventory-table analytic-table-header">
                                                    <thead className='main-thead'>
                                                        <tr>
                                                            <th>Detector</th>
                                                            <th>Amount</th>
                                                            <th>Unit Cost</th>
                                                            <th>Total Cost</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detectorProduction.map((item, index) => {
                                                            const detector = detectors.find(d => d.id === item.object_id);
                                                            if (!detector) return null;

                                                            const unitCost = getDetectorCost(detector, subAssemblies, invInfo);
                                                            const totalCost = unitCost * item.amount_produced;

                                                            return (
                                                                <tr key={index}>
                                                                    <td>{item.object_version ? `${item.object_name} (v${item.object_version})` : item.object_name}</td>
                                                                    <td>{item.amount_produced}</td>
                                                                    <td>${unitCost.toFixed(2)}</td>
                                                                    <td>${totalCost.toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                <p style={{ paddingLeft: '1rem' }}><strong>Total from Detectors: </strong>${totalDetectorProductionCost.toFixed(2)}</p>
                                            </>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', paddingTop: '1rem' }}>
                                        <TbReportMoney style={{ width: '1.5rem', height: '1.5rem' }} />
                                        <p style={{ width: '98%' }}>
                                            A Total of <strong style={{ color: '#28aee1' }}>${totalDetectorProductionCost.toFixed(2)}</strong> dollars
                                            were consumed from the inventory producing Detectors and a total
                                            of <strong style={{ color: '#28aee1' }}>${totalSubAssemblyProductionCost.toFixed(2)}</strong> dollars were
                                            consumed from the inventory producing independent Sub-Assemblies.
                                        </p>
                                    </div>
                                </div>


                            </div>

                            <div className='inv-analytics-card iac2'>

                                <div className='inv-analytics-icon-and-title'>
                                    <h1>Orders</h1>
                                    <div className="analytics-icon-wrapper ic2">
                                        <BsBoxSeamFill className='analytics-icon ic2' />
                                    </div>
                                </div>

                                <div className='inv-analytics-inner'>
                                    <p> <strong> • Orders Placed this Month:</strong> {getOrdersPlacedThisMonth(forecastInfo)}</p>
                                    <p> <strong> • Orders Fulfilled this Month:</strong> {getFulfilledOrdersCountThisMonth(forecastInfo)}</p>
                                    <p> <strong> • Items Ordered this Month:</strong> {getItemsOrderedThisMonth(forecastInfo)}</p>
                                    <p> <strong> • Most Ordered Item this Month (By Amount):</strong> {getMostOrderedItemByAmount(forecastInfo, invInfo)}</p>
                                    <p> <strong> • Most Ordered Item this Month (By Orders):</strong> {getMostOrderedItemByOrders(forecastInfo, invInfo)}</p>
                                </div>


                            </div>

                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={() => handlePrint(reportRef, `Monthly_Inventory_Report_${new Date().toISOString().split('T')[0]}`)}
                                className="place-request-button print-rep-button"
                            >
                                Print Monthly Report
                            </button>
                        </div>


                    </div>
                </div>
            )}

            {/* Planning Popup */}
            {activeSection === "PlanningSection" && showSectionPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content">
                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowSectionPopup(false);
                                setSubAssemblyAmounts({});
                                setConsiderInventory(true);
                                setConsiderForecast(false);
                                setRequestQuantity(0);
                                setActiveRequestPartID(null);
                                setIncludeMaxBuildable(false);
                                setDetectorsToAdd(0);
                                setNextXDetectors(1)
                            }}
                        >
                            x
                        </button>

                        <h2 style={{ display: "flex", flexDirection: 'column', alignItems: "center", gap: "0.5rem" }}>
                            Production Planning
                            {selectedDetectorId && (() => {
                                const detector = detectors.find(d => d.id === selectedDetectorId);
                                if (!detector) return null;
                                return (
                                    <h2 style={{ paddingTop: '0.5rem', paddingBottom: 'unset' }}>
                                        {detector.name} v{detector.version}
                                    </h2>
                                );
                            })()}
                        </h2>

                        <div ref={planningRef}>
                            <div className="parts-req-and-input prod-planning-tools">

                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }} className='dropdown-for-inv-planning'>
                                    <label style={{ marginRight: "4px" }}>Inventory Mode:</label>

                                    <div style={{ display: "flex", gap: "0.5rem" }}>

                                        <select
                                            className="inv-mode"
                                            value={
                                                considerInventory
                                                    ? considerForecast
                                                        ? "withForecast"
                                                        : "with"
                                                    : "without"
                                            }
                                            onChange={e => {
                                                if (e.target.value === "with") {
                                                    setConsiderInventory(true);
                                                    setConsiderForecast(false);
                                                } else if (e.target.value === "withForecast") {
                                                    setConsiderInventory(true);
                                                    setConsiderForecast(true);
                                                } else {
                                                    setConsiderInventory(false);
                                                    setConsiderForecast(false);
                                                }
                                            }}
                                        >
                                            <option value="with">Include Inventory</option>
                                            <option value="withForecast">Include Inventory + Forecasted</option>
                                            <option value="without">Ignore Inventory</option>
                                        </select>

                                        {considerForecast && showInfoTooltip(
                                            "Considers both current inventory and items that have been requested or ordered",
                                            "part-tooltip2"
                                        )}
                                    </div>
                                </div>


                                <div className="include-and-add-detec-section">
                                    <div className="">
                                        <button
                                            className='place-request-button'
                                            type="button"
                                            onClick={() => {
                                                if (!selectedDetectorId) return;
                                                const detector = detectors.find(d => d.id === selectedDetectorId);
                                                if (!detector) return;

                                                const defaults: Record<number, number> = {};
                                                detector.subassemblies.forEach(ds => {
                                                    const sub = subAssemblies.find(sa => sa.id === ds.subassembly);
                                                    if (sub) {
                                                        const amountPerDetector = ds.quantity_required || 1;
                                                        defaults[sub.id] = amountPerDetector * maxBuildableDetectors;
                                                    }
                                                });

                                                setSubAssemblyAmounts(defaults);
                                                setDetectorsToAdd(maxBuildableDetectors);
                                                setIncludeMaxBuildable(true); // optional, keeps track that max buildable is applied
                                            }}
                                            style={{
                                                padding: "0.5rem 1rem",
                                            }}
                                        >

                                            <div style={{ display: "flex", gap: "0.3rem" }}>
                                                <span>Set to Maximum Buildable Detectors</span>
                                                {showInfoTooltip(
                                                    "Sets sub-assembly counts to the maximum detectors that can be built with available inventory"
                                                )}
                                            </div>
                                        </button>


                                    </div>


                                    <div className="parts-req-and-input add-detect-req-input">
                                        <input
                                            type="number"
                                            min={0}
                                            value={detectorsToAdd}
                                            onChange={e => {
                                                const value = Number(e.target.value);
                                                setDetectorsToAdd(value);

                                                if (selectedDetectorId && value > 0) {
                                                    const detector = detectors.find(d => d.id === selectedDetectorId);
                                                    if (!detector) return;

                                                    setSubAssemblyAmounts(prev => {
                                                        const updated = { ...prev };
                                                        detector.subassemblies.forEach(ds => {
                                                            const sub = subAssemblies.find(sa => sa.id === ds.subassembly);
                                                            if (sub) {
                                                                const amountPerDetector = ds.quantity_required || 1;
                                                                updated[sub.id] = amountPerDetector * value;
                                                            }
                                                        });
                                                        return updated;
                                                    });
                                                } else {
                                                    setSubAssemblyAmounts({});
                                                }

                                                if (value >= maxBuildableDetectors) {
                                                    setIncludeMaxBuildable(true);
                                                } else {
                                                    setIncludeMaxBuildable(false);
                                                }
                                            }}
                                            style={{ marginBottom: "1rem" }}
                                            className="parts-req-and-input-input"
                                        />
                                        <p>Full Detector(s)</p>
                                    </div>
                                </div>
                            </div>

                            {selectedDetectorId && subAssemblies.length > 0 && (() => {
                                const detector = detectors.find(d => d.id === selectedDetectorId);
                                if (!detector) return null;

                                const combinedAvailable = (partId: number) => {
                                    const inventoryItem = invInfo.find(i => i.partID === partId);
                                    const baseAvailable = inventoryItem ? inventoryItem.currentAmount : 0;
                                    const forecastAmount = considerForecast ? (forecastAmountsByPart[partId] || 0) : 0;
                                    return baseAvailable + forecastAmount;
                                };

                                return (
                                    <div>
                                        <div className="sub-assembly-cards">
                                            {subAssemblies
                                                .filter(sub => detector.subassemblies.map(ds => ds.subassembly).includes(sub.id))
                                                .map(sub => (
                                                    <div key={sub.id} className="sub-assembly-card">
                                                        <div className="sub-assembly-card-left">
                                                            <img src={sub.subPicture ? sub.subPicture : fnnBlueLogo2} alt={sub.name} />
                                                            <h1>{sub.name}</h1>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                value={subAssemblyAmounts[sub.id] ?? 0}
                                                                onChange={e =>
                                                                    handleSubAssemblyAmountChange(sub.id, Number(e.target.value))
                                                                }
                                                            />
                                                        </div>

                                                        <div className="sub-assembly-card-right">
                                                            {sub.parts.map(p => {
                                                                const inventoryItem = invInfo.find(i => i.partID === p.part);
                                                                const totalNeeded = partsNeeded[p.part] || 0;
                                                                const available = considerInventory ? combinedAvailable(p.part) : 0;

                                                                let color = "black";
                                                                if (considerInventory) {
                                                                    color = available >= totalNeeded ? "green" : "red";
                                                                }

                                                                const subNeeded = (subAssemblyAmounts[sub.id] || 0) * p.quantity_required;

                                                                return (
                                                                    <div
                                                                        key={p.part}
                                                                        className="bom-single-part"
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                            color,
                                                                        }}
                                                                    >
                                                                        <div style={{ display: "flex" }}>
                                                                            <img
                                                                                src={inventoryItem?.partPicture ? inventoryItem?.partPicture : fnnBlueLogo}
                                                                                alt={inventoryItem?.partName || "Unknown"}
                                                                                className="part-image"
                                                                                style={{
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                    objectFit: "cover",
                                                                                    marginRight: 8,
                                                                                    borderRadius: 4,
                                                                                }}
                                                                            />
                                                                            <span>
                                                                                {inventoryItem?.partName || "Unknown"} x{subNeeded} - $
                                                                                {((inventoryItem?.unitCost || 0) * subNeeded).toFixed(2)}
                                                                            </span>
                                                                        </div>

                                                                        {considerInventory ? (
                                                                            <p>• {totalNeeded} needed, {available} available</p>
                                                                        ) : (
                                                                            <p>• {totalNeeded} needed</p>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>

                                        <div className="inventory-table-wrapper forecast-table">

                                            <div className='summary-inv-plan-with-req-all'>
                                                <h3>Summary of Required Parts </h3>

                                                {missingRequests.length > 0 && (
                                                    <div
                                                        className='place-request-button'
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            missingRequests.forEach((missing) => {
                                                                handlePartForecastRequest(missing.partId, missing.requestQty);
                                                            });
                                                        }}
                                                    >
                                                        Request All Missing
                                                    </div>
                                                )}
                                            </div>

                                            {(!isMobile3 || isPrinting) ? (
                                                // ✅ DESKTOP TABLE
                                                <table className="inventory-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Part</th>
                                                            <th>Needed</th>
                                                            {considerInventory && <th>Available</th>}
                                                            {considerForecast && <th>Forecasted</th>}
                                                            <th>
                                                                <div className="column-row-div">Request</div>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(partsNeeded).map(([partId, needed]) => {
                                                            const inventoryItem = invInfo.find(i => i.partID === Number(partId));
                                                            const available = considerInventory ? (inventoryItem?.currentAmount || 0) : 0;
                                                            const forecasted = considerForecast ? (forecastAmountsByPart[Number(partId)] || 0) : 0;

                                                            let isCovered = false;
                                                            if (considerForecast) {
                                                                isCovered = (available + forecasted) >= needed;
                                                            } else if (considerInventory) {
                                                                isCovered = available >= needed;
                                                            }

                                                            const neededColor = isCovered ? "green" : "red";

                                                            return (
                                                                <tr key={partId} style={{ fontWeight: "500" }}>
                                                                    <td>
                                                                        {inventoryItem?.partName ? (
                                                                            <div className="part-info-cell">
                                                                                <img
                                                                                    src={inventoryItem?.partPicture ? inventoryItem?.partPicture : fnnBlueLogo}
                                                                                    alt={inventoryItem.partName}
                                                                                    className="part-image"
                                                                                    style={{ cursor: "default" }}
                                                                                />
                                                                                <span>{truncateText(inventoryItem.partName, 30)}</span>
                                                                            </div>
                                                                        ) : (
                                                                            `Unknown Part #${inventoryItem?.partID}`
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        <div className="tooltip-wrapper">
                                                                            <span style={{ color: neededColor, cursor: 'default' }}>
                                                                                {needed}
                                                                            </span>
                                                                            <div className="custom-tooltip">
                                                                                {getNeededTooltip(needed, available, forecasted, considerInventory, considerForecast, isCovered)}
                                                                            </div>
                                                                        </div>
                                                                    </td>

                                                                    {considerInventory && <td>{available}</td>}
                                                                    {considerForecast && <td>{forecasted}</td>}

                                                                    <td>
                                                                        <div className="column-row-div">
                                                                            <div
                                                                                className="place-request-button"
                                                                                onClick={() => {
                                                                                    setActiveRequestPartID(Number(partId));
                                                                                    if (isCovered) {
                                                                                        setRequestQuantity(0);
                                                                                    } else {
                                                                                        if (considerForecast) {
                                                                                            setRequestQuantity(needed - (available + forecasted));
                                                                                        } else if (considerInventory) {
                                                                                            setRequestQuantity(needed - available);
                                                                                        } else {
                                                                                            setRequestQuantity(needed);
                                                                                        }
                                                                                    }
                                                                                    setShowRequestItemPopup(true);
                                                                                }}
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                    backgroundColor: isCovered ? "lightgray" : "",
                                                                                    color: isCovered ? "black" : "",
                                                                                }}
                                                                            >
                                                                                {isCovered ? "Request" : "Request Missing"}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                // ✅ MOBILE LIST
                                                <div className="mobile-required-list">
                                                    {Object.entries(partsNeeded).map(([partId, needed]) => {
                                                        const inventoryItem = invInfo.find(i => i.partID === Number(partId));
                                                        const available = considerInventory ? (inventoryItem?.currentAmount || 0) : 0;
                                                        const forecasted = considerForecast ? (forecastAmountsByPart[Number(partId)] || 0) : 0;

                                                        let isCovered = false;
                                                        if (considerForecast) {
                                                            isCovered = (available + forecasted) >= needed;
                                                        } else if (considerInventory) {
                                                            isCovered = available >= needed;
                                                        }

                                                        const neededColor = isCovered ? "green" : "red";

                                                        return (
                                                            <div key={partId} className="mobile-part-row2 for-order-batch">
                                                                <div className="part-info-cell">
                                                                    <img
                                                                        src={inventoryItem?.partPicture ? inventoryItem?.partPicture : { fnnBlueLogo }}
                                                                        alt={inventoryItem?.partName}
                                                                        className="part-image"
                                                                    />
                                                                    <span>{inventoryItem?.partName ?? `Unknown Part #${inventoryItem?.partID}`}</span>
                                                                </div>

                                                                <div className="mobile-detail-group" style={{ cursor: 'default' }}>
                                                                    <strong>Needed:</strong>{" "}
                                                                    <span style={{ color: neededColor }}>{needed}</span>
                                                                </div>

                                                                {considerInventory && (
                                                                    <div className="mobile-detail-group" style={{ cursor: 'default' }}>
                                                                        <strong>Available:</strong> {available}
                                                                    </div>
                                                                )}

                                                                {considerForecast && (
                                                                    <div className="mobile-detail-group" style={{ cursor: 'default' }}>
                                                                        <strong>Forecasted:</strong> {forecasted}
                                                                    </div>
                                                                )}

                                                                <div className="mobile-detail-group" style={{ cursor: 'default' }}>
                                                                    <strong>Request:</strong>
                                                                    <div
                                                                        className="place-request-button"
                                                                        onClick={() => {
                                                                            setActiveRequestPartID(Number(partId));
                                                                            if (isCovered) {
                                                                                setRequestQuantity(0);
                                                                            } else {
                                                                                if (considerForecast) {
                                                                                    setRequestQuantity(needed - (available + forecasted));
                                                                                } else if (considerInventory) {
                                                                                    setRequestQuantity(needed - available);
                                                                                } else {
                                                                                    setRequestQuantity(needed);
                                                                                }
                                                                            }
                                                                            setShowRequestItemPopup(true);
                                                                        }}
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            backgroundColor: isCovered ? "lightgray" : "",
                                                                            color: isCovered ? "black" : "",
                                                                        }}
                                                                    >
                                                                        {isCovered ? "Request" : "Request Missing"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                );
                            })()}

                        </div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                            <button
                                className="place-request-button print-rep-button"
                                onClick={() => handlePrint(planningRef, `${new Date().toLocaleString('default', { month: 'long' })}_${new Date().getFullYear()}_Production_Batch`)}
                            >
                                Print Production Plan
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Add and Delete Functionalities */}
            {showRequestItemPopup && activeRequestPartID !== null && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content'>

                        <button
                            className="close-popup-button"
                            onClick={() => setShowRequestItemPopup(false)}
                        >
                            x
                        </button>

                        <div className="add-new-part-form">
                            <h3>How many items to request?</h3>

                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Part Name</span>
                                    {invInfo.find(p => p.partID === activeRequestPartID)?.partName}
                                </label>
                            </div>

                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Amount</span>
                                    <input
                                        type="number"
                                        value={requestQuantity}
                                        min={1}
                                        // max={invInfo.find(p => p.partID === activeRequestPartID)?.currentAmount || 1000}
                                        onChange={(e) => setRequestQuantity(Number(e.target.value))}
                                    />
                                </label>
                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    onClick={() => {
                                        if (activeRequestPartID !== null) {
                                            handlePartForecastRequest(activeRequestPartID, requestQuantity);
                                            setShowRequestItemPopup(false);
                                        }
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {showArrivalItemPopup && activeForecast && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content'>

                        <button
                            className="close-popup-button"
                            onClick={() => setShowArrivalItemPopup(false)}
                        >
                            x
                        </button>

                        <div className="add-new-part-form">
                            <h3>
                                {isPacketOrder ? "How Many Packs Arrived?" : "How Many Arrived?"}
                            </h3>


                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Part Name</span>
                                    {invInfo.find(p => p.partID === activeForecast.part)?.partName}
                                </label>
                            </div>


                            <div className='add-new-part-table add-part-ext'>
                                {isPacketOrder ? (
                                    <label>
                                        <span>Packs</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={activeForecast.number_of_packets ?? undefined}
                                            value={arrivedPackets}
                                            onChange={(e) => {
                                                const packets = Number(e.target.value);
                                                setArrivedPackets(packets);

                                                // 🔁 auto-calculate unit amount
                                                setArrivalAmount(
                                                    packets * (activeForecast.amount_per_packet ?? 1)
                                                );
                                            }}
                                        />
                                        <small>
                                            {activeForecast.amount_per_packet} units per pack
                                        </small>
                                    </label>
                                ) : (
                                    <label>
                                        <span>Amount</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={activeForecast.expectedAmount}
                                            value={arrivalAmount}
                                            onChange={(e) => setArrivalAmount(Number(e.target.value))}
                                        />
                                    </label>
                                )}


                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    onClick={() => {
                                        if (!isArrivalAmountValid()) {
                                            toast.warn("Please enter a valid arrival amount.");
                                            return;
                                        }
                                        setShowConfirmProceedPopup(true);
                                    }}
                                >
                                    Confirm
                                </button>

                            </div>
                        </div>

                    </div>
                </div>
            )}


            {showOrderItemPopup && activeForecast && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content'>
                        <button
                            className="close-popup-button"
                            onClick={() => setShowOrderItemPopup(false)}
                        >
                            x
                        </button>

                        <div className="add-new-part-form">
                            <h3>
                                {isPacketOrder ? "How Many Packs to Order?" : "How Many to Order?"}
                            </h3>

                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Part Name</span>
                                    {invInfo.find(p => p.partID === activeForecast.part)?.partName}
                                </label>
                            </div>

                            <div className='add-new-part-table add-part-ext'>
                                {isPacketOrder ? (
                                    <label>
                                        <span>Packs</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={activeForecast.number_of_packets ?? undefined}
                                            value={orderedPackets}
                                            onChange={(e) => {
                                                const packets = Number(e.target.value);
                                                setOrderedPackets(packets);

                                                // 🔁 auto-calculate unit amount
                                                setOrderedAmount(
                                                    packets * (activeForecast.amount_per_packet ?? 1)
                                                );
                                            }}
                                        />
                                        <small>
                                            {activeForecast.amount_per_packet} units per pack
                                        </small>
                                    </label>
                                ) : (
                                    <label>
                                        <span>Amount</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={activeForecast.expectedAmount}
                                            value={orderedAmount}
                                            onChange={(e) => setOrderedAmount(Number(e.target.value))}
                                        />
                                    </label>
                                )}
                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    onClick={async () => {
                                        if (!isOrderAmountValid()) {
                                            toast.warn("Please enter a valid order amount.");
                                            return;
                                        }

                                        const success = await handleMarkOrdered();
                                        if (success) {
                                            setShowOrderItemPopup(false);
                                        }
                                    }}
                                >
                                    Confirm Order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SECOND CONFIRMATION POPUP */}
            {showConfirmProceedPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content second-confirm-forecast-popup-outer">

                        <div className='add-new-part-form'>


                            <h3>Confirm Arrival</h3>

                            <div className='second-confirm-forecast-popup'>
                                <p>
                                    You are about to mark{" "}
                                    <strong>
                                        {isPacketOrder
                                            ? `${arrivedPackets} pack${arrivedPackets !== 1 ? "s" : ""} (${arrivalAmount} units)`
                                            : `${arrivalAmount} item${arrivalAmount !== 1 ? "s" : ""}`}
                                    </strong>{" "}
                                    as arrived.
                                    This amount will be automatically appended to the inventory.
                                    Make sure the amount is correct.
                                </p>


                                <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                                    <button
                                        onClick={() => setShowConfirmProceedPopup(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={async () => {
                                            const success = await handleMarkArrived();

                                            if (!success) return; // ⛔ DO NOT PROCEED

                                            setShowConfirmProceedPopup(false);
                                            setShowArrivalItemPopup(false);

                                            const matchingPart = invInfo.find(p => p.partID === activeForecast?.part);
                                            setActiveLocation(matchingPart?.location || "Unknown Location");
                                            setActiveLocationPicture(matchingPart?.location_picture);
                                            setShowLocationPopup(true);
                                        }}
                                    >
                                        Proceed
                                    </button>



                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* POPUP TO SHOW WHERE TO STORE ITEM */}
            {showLocationPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content second-confirm-forecast-popup-outer">
                        <div className="add-new-part-form">
                            <h3>Storage Reminder</h3>

                            <div className="second-confirm-forecast-popup">
                                <p>
                                    Location: <strong>{activeLocation}</strong><br />
                                    Please take this time to store it at its proper location.
                                </p>

                                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                    {activeLocationPicture ? (
                                        <img
                                            className="location-picture-in-popup"
                                            src={activeLocationPicture}
                                            alt={activeLocationPicture}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                textAlign: "center",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <AiFillPicture
                                                className="location-picture-in-popup"
                                                style={{ fontSize: "6rem", color: "#555" }}
                                            />
                                            <p
                                                style={{
                                                    marginTop: "0.5rem",
                                                    fontStyle: "italic",
                                                    color: "#777",
                                                    fontSize: "0.9rem",
                                                }}
                                            >
                                                {activeLocationPicture || "No picture of storage location available for this part yet"}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: "1rem",
                                    }}
                                >
                                    <button
                                        onClick={() => setShowLocationPopup(false)}
                                        style={{ marginBottom: "1rem", marginTop: "1rem" }}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddSubAssemblyPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content add-sub-content-ext'>

                        <button
                            className="close-popup-button"
                            onClick={() => { closeAddSubPopup(); setPartSearchTerm(''); }}
                        >
                            x
                        </button>


                        <div className="add-new-part-form">
                            <h3>{editingSubAssemblyId ? 'Edit Sub-Assembly' : 'Add New Sub-Assembly'}</h3>

                            <div className='add-new-part-table'>
                                <label>
                                    <span>Name & Version</span>
                                    <input
                                        type="text"
                                        value={newSubAssemblyName}
                                        onChange={(e) => setNewSubAssemblyName(e.target.value)}
                                        placeholder='E.g "Dome v1.2"'
                                    />

                                </label>

                                {/* SubAssembly Picture Upload */}
                                <div className="file-upload-wrapper">
                                    <span>Sub-Assembly Picture (optional)</span>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Pictures must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                fontSize: '16px',
                                                            },
                                                        });
                                                        return; // Don't set the file
                                                    }
                                                    setNewSubAssemblyPic(file);
                                                }}
                                            />
                                            <p>Upload Image</p>
                                        </label>
                                        <span className="selected-file-name">
                                            {newSubAssemblyPic ? newSubAssemblyPic.name : existingPicName || "No image uploaded"}
                                        </span>
                                    </div>
                                </div>

                                {/* SubAssembly SOP Upload */}
                                <div className="file-upload-wrapper">
                                    <span>Sub-Assembly SOP (optional)</span>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.txt"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Documents must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                fontSize: '16px',
                                                            },
                                                        });
                                                        return; // Don't set the file
                                                    }
                                                    setNewSubAssemblySop(file);
                                                }}
                                            />
                                            <p>Upload Document</p>
                                        </label>
                                        <span className="selected-file-name">
                                            {newSubAssemblySop ? newSubAssemblySop.name : existingSopName || "No document uploaded"}
                                        </span>
                                    </div>
                                </div>




                                <div className="sub-part-and-amount-row">

                                    {/* Part Search */}
                                    <div className="field-group">
                                        <label style={{ position: 'relative' }}>
                                            <span>Part</span>
                                            <input
                                                type="text"
                                                placeholder="Search name or MPN"
                                                value={partSearchTerm}
                                                onChange={(e) => {
                                                    setPartSearchTerm(e.target.value);
                                                    setShowDropdown(true);
                                                }}
                                                onFocus={() => setShowDropdown(true)}
                                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)} // allow click
                                            />
                                            {showDropdown && (
                                                <div className="autocomplete-dropdown auto-dp-ext">
                                                    {filteredPartOptions.length > 0 ? (
                                                        filteredPartOptions.map((item) => (
                                                            <li
                                                                key={item.partID}
                                                                style={{ color: 'black' }}
                                                                onClick={() => {
                                                                    setSelectedPartId(item.partID);
                                                                    setPartSearchTerm(item.partName);
                                                                    setShowDropdown(false);
                                                                }}
                                                            >
                                                                <img className='part-image' src={item.partPicture ? item.partPicture : fnnBlueLogo} alt={item.partName}></img>
                                                                {truncateText(((item.mpn ? (item.mpn + " - ") : "") + item.partName), 40)}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li style={{ color: '#999' }}>No matches found</li>
                                                    )}
                                                    <button onClick={() => setShowAddItemPopup(true)}>Don’t see my part</button>
                                                </div>
                                            )}
                                        </label>
                                    </div>

                                    {/* Amount Input */}
                                    <div className="field-group">
                                        <label>
                                            <span>Amount</span>
                                            <input
                                                type="number"
                                                value={selectedPartAmount}
                                                onChange={(e) => setSelectedPartAmount(Number(e.target.value))}
                                                min={1}
                                                placeholder="Quantity Required"
                                            />
                                        </label>
                                    </div>

                                </div>



                                <button
                                    className='add-another-part'
                                    onClick={() => {
                                        if (!selectedPartId || selectedPartAmount <= 0) return;

                                        const alreadyExists = newSubAssemblyParts.some(
                                            (part) => part.part === selectedPartId
                                        );

                                        if (alreadyExists) {
                                            toast.warn("Cannot add the same part twice!", {
                                                autoClose: 4000,
                                                hideProgressBar: false,
                                                closeOnClick: true,
                                                pauseOnHover: true,
                                                draggable: true,
                                                style: {
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    fontSize: '16px',
                                                },
                                            });
                                            setPartSearchTerm('');
                                            return;
                                        }

                                        setNewSubAssemblyParts((prev) => [
                                            ...prev,
                                            {
                                                part: selectedPartId,
                                                quantity_required: selectedPartAmount,
                                            },
                                        ]);
                                        setSelectedPartId(null);
                                        setSelectedPartAmount(1);
                                        setPartSearchTerm('');
                                    }}
                                >
                                    + Add Part
                                </button>


                                <ul>
                                    {newSubAssemblyParts.map((p, idx) => {
                                        const part = invInfo.find(i => i.partID === p.part);
                                        return (
                                            <li key={idx}>
                                                {part?.partName || 'Unknown'} – Qty: {p.quantity_required}
                                                <button className='' onClick={() => {
                                                    setNewSubAssemblyParts(prev => prev.filter((_, i) => i !== idx));
                                                }}><FaTrashAlt /></button>
                                            </li>
                                        );
                                    })}
                                </ul>


                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                <button
                                    onClick={editOrAddSubAssembly}
                                    disabled={!newSubAssemblyName.trim() || newSubAssemblyParts.length === 0}
                                >
                                    {editingSubAssemblyId ? 'Update' : 'Confirm'}
                                </button>

                                {editingSubAssemblyId && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSubAssemblyToDeleteId(editingSubAssemblyId);
                                            setShowDeleteSubAssemblyPopup(true);
                                        }}
                                        style={{
                                            backgroundColor: "#e74c3c",
                                            color: "#fff",
                                            border: "none",
                                            padding: "0.6rem 1.2rem",
                                            borderRadius: "0.2rem",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Delete
                                    </button>
                                )}

                            </div>


                        </div>

                    </div>
                </div>
            )}


            {showAddItemPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content'>

                        <button
                            className="close-popup-button"
                            onClick={() => {
                                setShowAddItemPopup(false);
                                resetNewPartData(); // Reset fields
                            }}
                        >
                            x
                        </button>


                        <div className="add-new-part-form">
                            <h3>Add New Item</h3>

                            <div className='add-new-part-table add-part-ext'>
                                <label>
                                    <span>Part Name</span>
                                    <input
                                        type="text"
                                        placeholder='E.g "D-Link DWM-311 Router"'
                                        value={newPartData.partName}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, partName: e.target.value }))}
                                    />
                                </label>

                                <label>
                                    <span>Amount</span>
                                    <input
                                        type="number"
                                        placeholder="Current Amount"
                                        value={newPartData.currentAmount}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, currentAmount: Number(e.target.value) }))}
                                    />
                                </label>

                                <label>
                                    <span>Unit Cost</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Unit Cost"
                                        value={newPartData.unitCost}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, unitCost: Number(e.target.value) }))}
                                    />
                                </label>

                                <label>
                                    <span>Category</span>
                                    <select
                                        value={newPartData.category}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, category: e.target.value }))}
                                    >
                                        {CATEGORY_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>

                                <div className='subassembly-selector'>
                                    <p style={{ marginBottom: 'unset' }}><strong style={{ marginBottom: 'unset' }}>Optional Fields</strong></p>
                                </div>

                                <label>
                                    <span>Store Link</span>
                                    <input
                                        type="url"
                                        placeholder="https://store/item.com"
                                        value={newPartData.partLink ?? ''}
                                        onChange={(e) =>
                                            setNewPartData(prev => ({ ...prev, partLink: e.target.value }))
                                        }
                                    />
                                </label>

                                <label>
                                    <span>Stock Target</span>
                                    <input
                                        type="number"
                                        placeholder="Desired amount to be kept in stock"
                                        value={newPartData.stock_target ?? ''}   // ✅ empty string when null
                                        onChange={(e) =>
                                            setNewPartData(prev => ({
                                                ...prev,
                                                stock_target: e.target.value === '' ? null : Number(e.target.value), // ✅ handle empty input cleanly
                                            }))
                                        }
                                    />
                                </label>


                                <label>
                                    <span>Location</span>
                                    <input
                                        type="text"
                                        placeholder='E.g "Stored in room X, first drawer"'
                                        value={newPartData.location}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, location: e.target.value }))}
                                    />
                                </label>

                                <label>
                                    <span>Description</span>
                                    <input
                                        type="text"
                                        placeholder='E.g "Screw used to mount Dome"'
                                        value={newPartData.description}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </label>

                                <label>
                                    <span>Manufacturer Part Number (MPN)</span>
                                    <input
                                        type="text"
                                        placeholder='Unique # used by manufactures'
                                        value={newPartData.mpn}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, mpn: e.target.value }))}
                                    />
                                </label>

                                <label>
                                    <span>Manufacturer</span>
                                    <input
                                        type="text"
                                        placeholder='Company that manufactures the part'
                                        value={newPartData.manufacturer}
                                        onChange={(e) => setNewPartData(prev => ({ ...prev, manufacturer: e.target.value }))}
                                    />
                                </label>

                                {/* ---- Sold in Packs ---- */}
                                <label className="checkbox-label">
                                    <span>Sold in Packs</span>
                                    <input
                                        type="checkbox"
                                        checked={!!newPartData.sold_in_packets}
                                        onChange={(e) =>
                                            setNewPartData(prev => ({
                                                ...prev,
                                                sold_in_packets: e.target.checked,
                                                // clear packet amount when unchecked
                                                amount_per_packet: e.target.checked ? prev.amount_per_packet : null,
                                            }))
                                        }
                                    />
                                </label>

                                {/* ---- Amount per Packet (conditional) ---- */}
                                {newPartData.sold_in_packets && (
                                    <label>
                                        <span>Amount per Pack</span>
                                        <input
                                            type="number"
                                            min={1}
                                            placeholder="Units per packs"
                                            value={newPartData.amount_per_packet ?? ''}
                                            onChange={(e) =>
                                                setNewPartData(prev => ({
                                                    ...prev,
                                                    amount_per_packet:
                                                        e.target.value === '' ? null : Number(e.target.value),
                                                }))
                                            }
                                        />
                                    </label>
                                )}


                                <div className="file-upload-wrapper">
                                    <span>Part Picture</span>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Pictures must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                fontSize: '16px',
                                                            },
                                                        });
                                                        return; // Don't set the file
                                                    }
                                                    setNewPartData(prev => ({ ...prev, partPictureFile: file }));
                                                }}
                                            />
                                            <p>Upload Image</p>
                                        </label>

                                        {newPartData.partPictureFile && (
                                            <span className="selected-file-name">{newPartData.partPictureFile.name}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="file-upload-wrapper">
                                    <span>Part Document</span>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.txt"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Documents must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                fontSize: '16px',
                                                            },
                                                        });
                                                        return; // Don't set the file
                                                    }
                                                    setNewPartData(prev => ({ ...prev, partDocumentFile: file }));
                                                }}
                                            />
                                            <p>Upload Document</p>
                                        </label>
                                        {newPartData.partDocumentFile && (
                                            <span className="selected-file-name">{newPartData.partDocumentFile.name}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="file-upload-wrapper">
                                    <span>Picture of Location</span>
                                    <div className='file-upload-button-and-file'>
                                        <label className="custom-file-upload">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] ?? null;
                                                    if (file && file.size > 1024 * 1024) {
                                                        toast.warn("Pictures must be at most 1MB", {
                                                            autoClose: 4000,
                                                            hideProgressBar: false,
                                                            closeOnClick: true,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            style: {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                fontSize: '16px',
                                                            },
                                                        });
                                                        return; // Don't set the file
                                                    }
                                                    setNewPartData(prev => ({ ...prev, partLocationFile: file }));
                                                }}
                                            />
                                            <p>Upload Image</p>
                                        </label>

                                        {newPartData.partLocationFile && (
                                            <span className="selected-file-name">{newPartData.partLocationFile.name}</span>
                                        )}
                                    </div>
                                </div>

                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    onClick={async () => {
                                        await addNewPart();
                                        setShowAddItemPopup(false); // Close popup
                                    }}
                                    disabled={!newPartData.partName.trim()}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {showDeleteItemPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>

                        <div className='inv-delete-inner-pop'>
                            <h3>Are you sure you would like to delete this item?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteItemPopup(false);
                                    setItemToDeleteId(null);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (itemToDeleteId !== null) {
                                            deleteInventoryItem(itemToDeleteId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {showCountedPopup && itemToCount && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content del-pop">
                        <div className="inv-delete-inner-pop">
                            <h3 style={{ display: 'unset' }}>
                                You are about to set that <strong>{itemToCount.partName}</strong> has just been counted. Would you like to proceed?
                            </h3>

                            <div className="inv-delete-buttons-div">
                                <button
                                    onClick={() => {
                                        setShowCountedPopup(false);
                                        setItemToCount(null);
                                    }}
                                >
                                    No
                                </button>
                                <button
                                    onClick={() => {
                                        const today = new Date().toISOString().split("T")[0];
                                        const updatedAmount =
                                            editedValues.currentAmount ?? itemToCount.currentAmount;

                                        updateInventoryItem(itemToCount.partID, {
                                            lastCounted: today,
                                            currentAmount: updatedAmount,
                                        });

                                        setEditedValuesMap(prev => ({ ...prev, [itemToCount.partID]: {} }));
                                        setExpandedSlips(prev => ({ ...prev, [itemToCount.partID]: false }));

                                        setShowCountedPopup(false);
                                        setItemToCount(null);
                                    }}
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteSubAssemblyPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>
                        <div className='inv-delete-inner-pop'>
                            <h3>Are you sure you want to delete this sub-assembly?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteSubAssemblyPopup(false);
                                    setSubAssemblyToDeleteId(null);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (subAssemblyToDeleteId !== null) {
                                            handleDeleteSubAssembly(subAssemblyToDeleteId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddDetectorPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content add-sub-content-ext'>

                        <button
                            className="close-popup-button"
                            onClick={() => { closeAddDetectorPopup(); setSubAssemblySearchTerm(''); }}
                        >
                            x
                        </button>

                        <div className="add-new-part-form">
                            <h3>{editingDetectorId ? 'Edit Detector' : 'Add New Detector'}</h3>

                            <div className='add-new-part-table add-det-ext'>

                                <label>
                                    <span>Name</span>
                                    <input
                                        type="text"
                                        value={newDetectorName}
                                        onChange={(e) => setNewDetectorName(e.target.value)}
                                        placeholder='Detector Name'
                                    />
                                </label>

                                <label>
                                    <span>Version</span>
                                    <input
                                        type="text"
                                        value={newDetectorVersion}
                                        onChange={(e) => setNewDetectorVersion(e.target.value)}
                                        placeholder='Version'
                                    />
                                </label>

                                <div className="sub-part-and-amount-row">

                                    {/* Sub-Assembly Search */}
                                    <div className="field-group">
                                        <label style={{ position: 'relative' }}>
                                            <span>Sub-Assembly</span>
                                            <input
                                                type="text"
                                                placeholder="Search sub-assembly"
                                                value={subAssemblySearchTerm}
                                                onChange={(e) => {
                                                    setSubAssemblySearchTerm(e.target.value);
                                                    setShowSubAssemblyDropdown(true);
                                                }}
                                                onFocus={() => setShowSubAssemblyDropdown(true)}
                                                onBlur={() => setTimeout(() => setShowSubAssemblyDropdown(false), 200)} // allow click
                                            />
                                            {showSubAssemblyDropdown && (
                                                <ul className="autocomplete-dropdown">
                                                    {filteredSubAssemblies.length > 0 ? (
                                                        filteredSubAssemblies.map((sa) => (
                                                            <li
                                                                key={sa.id}
                                                                style={{ color: 'black' }}
                                                                onClick={() => {
                                                                    setSelectedSubAssemblyId(sa.id);
                                                                    setSubAssemblySearchTerm(sa.name);
                                                                    setShowSubAssemblyDropdown(false);
                                                                }}
                                                            >
                                                                {sa.name}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li style={{ color: '#999' }}>No matches found</li>
                                                    )}
                                                </ul>
                                            )}
                                        </label>
                                    </div>

                                    {/* Amount Input */}
                                    <div className="field-group">
                                        <label>
                                            <span>Amount</span>
                                            <input
                                                type="number"
                                                value={selectedSubAssemblyAmount}
                                                onChange={(e) => setSelectedSubAssemblyAmount(Number(e.target.value))}
                                                min={1}
                                                placeholder="Quantity Required"
                                            />
                                        </label>
                                    </div>

                                </div>



                                <button
                                    className='add-another-part'
                                    onClick={() => {
                                        if (!selectedSubAssemblyId || selectedSubAssemblyAmount <= 0) return;

                                        const alreadyExists = newDetectorSubAssemblies.some(
                                            (sa) => sa.subassembly === selectedSubAssemblyId
                                        );

                                        if (alreadyExists) {
                                            toast.warn("Cannot add the same sub-assembly twice!", {
                                                autoClose: 4000,
                                                hideProgressBar: false,
                                                closeOnClick: true,
                                                pauseOnHover: true,
                                                draggable: true,
                                                style: {
                                                    backgroundColor: 'white',
                                                    color: 'black',
                                                    fontSize: '16px',
                                                },
                                            });
                                            setSubAssemblySearchTerm('');
                                            return;
                                        }

                                        setNewDetectorSubAssemblies((prev) => [
                                            ...prev,
                                            {
                                                subassembly: selectedSubAssemblyId,
                                                quantity_required: selectedSubAssemblyAmount,
                                            },
                                        ]);
                                        setSelectedSubAssemblyId(null);
                                        setSelectedSubAssemblyAmount(1);
                                        setSubAssemblySearchTerm('');
                                    }}
                                >
                                    + Add SubAssembly
                                </button>



                                <ul>
                                    {newDetectorSubAssemblies.map((s, idx) => {
                                        const sub = subAssemblies.find(sa => sa.id === s.subassembly);
                                        return (
                                            <li key={idx}>
                                                {sub?.name || 'Unknown'} – Qty: {s.quantity_required}
                                                <button className='' onClick={() => {
                                                    setNewDetectorSubAssemblies(prev => prev.filter((_, i) => i !== idx));
                                                }}><FaTrashAlt /></button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                <button
                                    onClick={editOrAddDetector}
                                    disabled={!newDetectorName.trim() || newDetectorSubAssemblies.length === 0}
                                >
                                    {editingDetectorId ? 'Update' : 'Confirm'}
                                </button>

                                {editingDetectorId && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDetectorToDeleteId(editingDetectorId);
                                            setShowDeleteDetectorPopup(true);
                                        }}
                                        style={{
                                            backgroundColor: "#e74c3c",
                                            color: "#fff",
                                            border: "none",
                                            padding: "0.6rem 1.2rem",
                                            borderRadius: "0.2rem",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDetectorPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content del-pop'>
                        <div className='inv-delete-inner-pop'>
                            <h3>Are You Sure You Want to Delete This Detector Version?</h3>

                            <div className='inv-delete-buttons-div'>
                                <button onClick={() => {
                                    setShowDeleteDetectorPopup(false);
                                    setDetectorToDeleteId(null);
                                }}>
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (detectorToDeleteId !== null) {
                                            handleDeleteDetector(detectorToDeleteId);
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSelectDetectorPopup && currentSubAssemblyId && (
                <div className="add-item-popup-overlay">
                    <div className="built-det-for-sub-content">
                        <button
                            style={{ color: "black" }}
                            className="close-popup-button"
                            onClick={() => {
                                setShowSelectDetectorPopup(false);
                                setCurrentSubAssemblyId(null);
                            }}
                        >
                            x
                        </button>

                        <div className="subassembly-selector">
                            <strong>Select Detector for Sub-Assembly</strong>

                            <div className="subassembly-options-box">
                                {(() => {
                                    // ✅ 1. Find the built sub-assembly object (e.g. { id: "DOM35-2", subassembly: 35 })
                                    const builtSubAssemblyObj = builtSubAssemblies.find(
                                        bsa => bsa.id === currentSubAssemblyId
                                    );
                                    if (!builtSubAssemblyObj) {
                                        return <p className="no-options">Invalid built sub-assembly.</p>;
                                    }

                                    // ✅ 2. Extract its numeric subassembly ID (e.g. 35)
                                    const subassemblyId = builtSubAssemblyObj.subassembly;

                                    // ✅ 3. Find detector models whose BOM includes that subassembly ID
                                    const validDetectorModels = detectors.filter(det =>
                                        det.subassemblies.some(ds => ds.subassembly === subassemblyId)
                                    );

                                    // ✅ 4. Get their version numbers
                                    const validVersions = validDetectorModels.map(det => det.version);

                                    // ✅ 5. Filter built detectors based on those valid versions
                                    const matchingBuiltDetectors = builtDetectors.filter(det => {
                                        const detModel = detectors.find(d => d.id === det.detector);
                                        return detModel && validVersions.includes(detModel.version);
                                    });

                                    // ✅ 6. Display appropriate message if none found
                                    if (!matchingBuiltDetectors.length) {
                                        return (
                                            <p className="no-options">
                                                No detectors available for version(s):{" "}
                                                {validVersions.length ? validVersions.join(", ") : "None"}
                                            </p>
                                        );
                                    }

                                    // ✅ 7. Render filtered detectors
                                    return matchingBuiltDetectors.map(det => {
                                        const detModel = detectors.find(d => d.id === det.detector);
                                        return (
                                            <div
                                                key={det.mac_address}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    if (!currentSubAssemblyId) return;
                                                    handleUnitStatusChange(currentSubAssemblyId, "installed", det.mac_address);
                                                    setShowSelectDetectorPopup(false);
                                                    setCurrentSubAssemblyId(null);
                                                }}
                                                className="subassembly-option"
                                            >
                                                {det.code ? `${det.code} - ` : ""} {det.mac_address} — v{detModel?.version || "?"}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            <br />
                            <p>
                                <strong>Note 1:</strong> Only detectors whose version includes this sub-assembly
                                type will be shown.
                            </p>
                            <br />
                            <p><strong>Note 2:</strong> If the selected detector already has a sub-assembly of this type
                                (e.g., Dome v1), selecting that detector will remove the current sub-assembly it has and replace it
                                with the newly selected one.
                            </p><br />

                            <p><strong>Example:</strong> A detector currently has a Dome Sub-Assembly DOM1-1. If you assign
                                another Dome Sub-Assembly DOM1-2 to the same detector, DOM1-1 will be removed and marked as
                                available, while DOM1-2 will be installed in the detector.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {showStatusConfirmPopup && subAssemblyToConfirmId && statusToConfirm && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content second-confirm-forecast-popup-outer">

                        <div className="add-new-part-form">

                            <h3>Confirm Status Change</h3>

                            <div className="second-confirm-forecast-popup">

                                <p>
                                    You are about to mark <strong>{builtSubAssemblies.find(bsa => bsa.id === subAssemblyToConfirmId)?.id}</strong> as <strong>{statusToConfirm}</strong>. Would you like to proceed?
                                </p>

                                <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                                    <button
                                        onClick={() => {
                                            setShowStatusConfirmPopup(false);
                                            setSubAssemblyToConfirmId(null);
                                            setStatusToConfirm(null);
                                        }}
                                    >
                                        No
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (subAssemblyToConfirmId && statusToConfirm) {
                                                handleUnitStatusChange(subAssemblyToConfirmId, statusToConfirm);
                                            }
                                            setShowStatusConfirmPopup(false);
                                            setSubAssemblyToConfirmId(null);
                                            setStatusToConfirm(null);
                                        }}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {showBuildConfirmPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content confirm-a-build-wrapper">

                        <div className='add-new-part-form'>

                            <h3>Confirm Sub-Assembly Build</h3>

                            <div className='confirm-a-build-inner'>

                                {buildSummary.buildNew.length > 0 && (
                                    <>
                                        <div className='subassembly-selector'><strong><p>You are about to build NEW:</p></strong></div>
                                        <table className="confirm-table">
                                            <thead><tr><th>Sub</th><th>Quantity</th></tr></thead>
                                            <tbody>
                                                {buildSummary.buildNew.map((c, i) => (
                                                    <tr key={i}><td>{c.sub}</td><td>{c.qty}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p>Which will <strong>consume</strong> the listed sub-assemblies worth of parts from the inventory and create new built sub-assembly units.</p>
                                    </>
                                )}

                                {buildSummary.register.length > 0 && (
                                    <>
                                        <div className='subassembly-selector'><strong><p>You are about to register EXISTING:</p></strong></div>
                                        <table className="confirm-table">
                                            <thead><tr><th>Sub</th><th>Quantity</th></tr></thead>
                                            <tbody>
                                                {buildSummary.register.map((c, i) => (
                                                    <tr key={i}><td>{c.sub}</td><td>{c.qty}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p>Which will create new built sub-assembly units <strong>without consuming</strong> inventory.</p>
                                    </>
                                )}

                                {buildSummary.addComponents.length > 0 && (
                                    <>
                                        <div className='subassembly-selector'><strong><p>You are about to add components:</p></strong></div>
                                        <table className="confirm-table">
                                            <thead><tr><th>Sub</th><th>Quantity</th></tr></thead>
                                            <tbody>
                                                {buildSummary.addComponents.map((a, i) => (
                                                    <tr key={i}><td>{a.sub}</td><td>{a.qty}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p>Which will <strong>add</strong> the listed sub-assemblies worth of parts to the inventory.</p>
                                    </>
                                )}

                                <p style={{ marginTop: '1rem', textAlign: 'center' }}>Would you like to proceed?</p>

                                <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                                    <button
                                        style={{ marginTop: 'unset' }}
                                        onClick={() => setShowBuildConfirmPopup(false)}
                                    >
                                        No
                                    </button>
                                    <button
                                        style={{ marginTop: 'unset' }}
                                        onClick={async () => {
                                            setShowBuildConfirmPopup(false);
                                            await handleSubAssemblyBuildAction();

                                            // Only show the result popup if something was built
                                            if (buildSummary.buildNew.length > 0 || buildSummary.register.length > 0) {
                                                setShowBuildResultPopup(true);
                                            }
                                            else {
                                                setShowSectionPopup(false)
                                            }
                                        }}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {showBuildResultPopup && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content confirm-a-build-wrapper">

                        <div className='add-new-part-form'>
                            <h3>Build Complete</h3>

                            <div className='confirm-a-build-inner'>

                                <div className='subassembly-selector'><strong><p>You have just built:</p></strong></div>

                                <table className="confirm-table">
                                    <thead>
                                        <tr><th>Sub</th><th>Quantity</th><th>IDs</th></tr>
                                    </thead>
                                    <tbody>
                                        {newlyBuiltSubs.map((b, i) => (
                                            <tr key={i}>
                                                <td>{b.name}</td>
                                                <td>{b.qty}</td>
                                                <td>{b.ids.join(", ")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <p>Please take this moment to add labels to the built sub-assemblies with their respective IDs.</p>

                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <button onClick={() => (setShowBuildResultPopup(false), setShowSectionPopup(false))}>OK</button>
                                </div>

                            </div>
                        </div>


                    </div>
                </div>
            )}

        </div>
    )
}

export default InventoryManagement